import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, Mail, MailOpen, Phone, Calendar, MessageSquare, MessageCircle, Users, ChevronRight, Copy, Key, ExternalLink, Search, Download, Activity, FormInput, Ticket, Wallet, UserPlus, Trash2, ArrowUpRight, ChevronDown, Plus, Eye, EyeOff, Check, CheckCheck, Archive, ArchiveRestore, X, Minus, Send, Share2, Megaphone, Shield, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar as DayPickerCalendar } from "../components/ui/calendar";
import { api } from "../lib/api";
import { formatApiError } from "../lib/AuthContext";
import AdminPT from "../components/admin/AdminPT";
import { i18nDict } from "../lib/i18n";
import { DEFAULT_MEDIA, MEDIA_GROUPS } from "../lib/media";
import { PhoneField } from "../components/PhoneField";
import { sortRows, SortSelect, SortHeader, SORT_OPTIONS, MONEY_OPTIONS, firstOf, lastOf, waLink } from "../components/admin/listSort";
import { validatePhone } from "../lib/validate";
import { toast } from "sonner";

const TABS = [
  { key: "home", label: "Overzicht" },
  { key: "aanmeldingen", label: "Aanmeldingen" },
  { key: "members", label: "Leden" },
  { key: "trials", label: "Proeflessen" },
  { key: "classes", label: "Klassen" },
  { key: "pt", label: "Personal Training" },
  { key: "schedule", label: "Rooster" },
  { key: "archive", label: "Archief" },
  { key: "payments", label: "Betalingen" },
  { key: "announcement", label: "Mededelingen" },
  { key: "promos", label: "Acties" },
  { key: "teaser", label: "Teasers" },
  { key: "texts_home", label: "Homepagina" },
  { key: "texts_landing", label: "Landingpage" },
  { key: "media", label: "Media" },
  { key: "emails", label: "E-mails" },
  { key: "all", label: "Formulieren" },
  { key: "team", label: "Team" },
  { key: "access", label: "Toegang" },
];

const NAV_GROUPS = [
  { label: "Dashboard", keys: ["home"] },
  { label: "Klanten", keys: ["aanmeldingen", "members", "trials"] },
  { label: "Planning", keys: ["classes", "schedule", "archive"] },
  { label: "Personal Training", keys: ["pt"] },
  { label: "Financieel", keys: ["payments"] },
  { label: "Website", keys: ["texts_home", "texts_landing", "media", "announcement", "teaser"] },
  { label: "Communicatie", keys: ["emails", "promos"] },
  { label: "Beheer", keys: ["all", "team", "access"] },
];

const CONTENT_TABS = ["schedule", "teaser", "announcement", "texts_home", "texts_landing", "media", "emails", "trial_settings"];

// Omgevings-indicator: toont PREVIEW of LIVE met link naar de andere omgeving.
const ENV_INFO = (() => {
  const live = ["johboxing.com", "www.johboxing.com"].includes(window.location.hostname);
  return live
    ? { label: "LIVE", cls: "text-[#4ADE80] border-[#4ADE80]/40", other: "https://joh-boxing-preview.preview.emergentagent.com/admin", title: "Open de preview-omgeving" }
    : { label: "PREVIEW", cls: "accent-ink border-[#D4A93A]/50", other: "https://johboxing.com/admin", title: "Open de live-omgeving" };
})();

const WEEKDAY_EN = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

// Statussen aanmeldingen: backend-waarden → NL labels voor medewerkers
const SUB_STATUS_LABEL = { new: "Nieuw", planned: "In behandeling", approved: "Goedgekeurd", handled: "Afgerond", declined: "Geannuleerd" };
const SUB_STATUS_ORDER = ["new", "planned", "approved", "handled", "declined"];

// Bewerkbare website-teksten per sectie (admin → Website). Waarden vallen terug op i18n-standaarden.
// h1.*-velden zijn de grote display-koppen: een override vervangt de gestylde standaard door platte tekst.
const TEXT_SECTIONS_HOME = [
  { key: "hero", label: "Hero (bovenaan)", fields: [{ k: "hero.h1.1", label: "Grote titel — regel 1" }, { k: "hero.h1.2", label: "Grote titel — regel 2" }, { k: "hero.h1.3", label: "Grote titel — regel 3" }, { k: "hero.h1.4", label: "Grote titel — regel 4" }, { k: "hero.tag", label: "Titel (klein)" }, { k: "hero.sub", label: "Introductietekst" }] },
  { key: "intro", label: "Manifesto", fields: [{ k: "intro.h1.1", label: "Kop — regel 1" }, { k: "intro.h1.2", label: "Kop — regel 2" }, { k: "intro.h1.3", label: "Kop — regel 3" }, { k: "intro.ch", label: "Sectielabel" }, { k: "intro.p1", label: "Tekst 1" }, { k: "intro.p2", label: "Tekst 2" }] },
  { key: "values", label: "Values", fields: [{ k: "values.h1.1", label: "Kop — regel 1" }, { k: "values.h1.2", label: "Kop — regel 2" }, { k: "values.h1.3", label: "Kop — regel 3" }, { k: "values.sub", label: "Subtitel" }, { k: "values.1", label: "Respect" }, { k: "values.2", label: "Discipline" }, { k: "values.3", label: "Unity" }] },
  { key: "trainings", label: "Trainingen", fields: [{ k: "trainings.ch", label: "Sectielabel" }, { k: "training.boxing101.b", label: "Boxing 101" }, { k: "training.boxing.b", label: "Boxing Training" }, { k: "training.bag.b", label: "Bag Training" }, { k: "training.sparring.b", label: "Sparring" }, { k: "training.pt.b", label: "Personal Training" }] },
  { key: "teaser", label: "Teaser (This week at JOH.)", fields: [{ k: "teaser.ch", label: "Sectielabel" }, { k: "teaser.h1", label: "Kop" }, { k: "teaser.btn", label: "Knoptekst" }] },
  { key: "schedule", label: "Rooster", fields: [{ k: "schedule.h1.1", label: "Kop — regel 1" }, { k: "schedule.h1.2", label: "Kop — regel 2" }, { k: "schedule.note", label: "Notitie onder rooster" }] },
  { key: "membership", label: "Membership", fields: [{ k: "membership.h1.1", label: "Kop — regel 1" }, { k: "membership.h1.2", label: "Kop — regel 2" }, { k: "membership.h1.3", label: "Kop — regel 3" }, { k: "membership.blurb", label: "Tekst" }] },
  { key: "about", label: "Over JOH.", fields: [{ k: "about.h1.1", label: "Kop — regel 1" }, { k: "about.h1.2", label: "Kop — regel 2" }, { k: "about.h1.3", label: "Kop — regel 3" }, { k: "about.h1.4", label: "Kop — regel 4" }, { k: "about.p1", label: "Tekst 1" }, { k: "about.p2", label: "Tekst 2" }, { k: "about.p3", label: "Tekst 3" }] },
  { key: "vision", label: "Filosofie", fields: [{ k: "vision.h1.1", label: "Kop — regel 1" }, { k: "vision.h1.2", label: "Kop — regel 2" }, { k: "vision.blurb", label: "Tekst" }] },
  { key: "community", label: "Community", fields: [{ k: "community.h1.1", label: "Kop — regel 1" }, { k: "community.h1.2", label: "Kop — regel 2" }, { k: "community.h1.3", label: "Kop — regel 3" }, { k: "community.blurb", label: "Tekst" }, { k: "community.invite", label: "Uitnodiging" }] },
  { key: "pt", label: "Personal Training", fields: [{ k: "pt.h1.1", label: "Kop — regel 1" }, { k: "pt.h1.2", label: "Kop — regel 2" }, { k: "pt.h1.3", label: "Kop — regel 3" }, { k: "pt.blurb", label: "Tekst" }] },
  { key: "trial", label: "Proefles", fields: [{ k: "trial.h1.1", label: "Kop — regel 1" }, { k: "trial.h1.2", label: "Kop — regel 2" }, { k: "trial.h1.3", label: "Kop — regel 3" }, { k: "trial.h1.4", label: "Kop — regel 4" }, { k: "trial.blurb", label: "Tekst" }, { k: "trial.hint", label: "Hint" }] },
  { key: "contact", label: "Contact", fields: [{ k: "contact.h1.1", label: "Kop — regel 1" }, { k: "contact.h1.2", label: "Kop — regel 2" }, { k: "contact.h1.3", label: "Kop — regel 3" }, { k: "contact.h1.4", label: "Kop — regel 4" }, { k: "contact.addr", label: "Adres" }] },
  { key: "nav", label: "Navigatie (menu)", fields: [{ k: "nav.home", label: "Home" }, { k: "nav.about", label: "Over JOH." }, { k: "nav.trainings", label: "Trainingen" }, { k: "nav.schedule", label: "Rooster" }, { k: "nav.membership", label: "Membership" }, { k: "nav.pt", label: "Personal Training" }, { k: "nav.community", label: "Community" }, { k: "nav.contact", label: "Contact" }, { k: "nav.book", label: "Boek-knop" }] },
  { key: "buttons", label: "Knoppen", fields: [{ k: "hero.btn.trial", label: "Hero — proefles" }, { k: "hero.btn.schedule", label: "Hero — rooster" }, { k: "schedule.btn", label: "Rooster — reserveer" }, { k: "membership.btn", label: "Membership — start" }, { k: "about.btn", label: "Over — train met JOH." }, { k: "community.btn", label: "Community — sluit je aan" }, { k: "trial.form.submit", label: "Proefles — verstuur" }, { k: "pt.form.submit", label: "PT — aanvragen" }, { k: "contact.submit", label: "Contact — verstuur" }] },
  { key: "footer", label: "Footer (onderaan)", fields: [{ k: "footer.line", label: "Grote regel" }, { k: "footer.tag", label: "Slotwoord" }, { k: "footer.nav", label: "Kolom Navigatie" }, { k: "footer.legal", label: "Kolom Legal" }, { k: "footer.terms", label: "Voorwaarden-link" }, { k: "footer.privacy", label: "Privacy-link" }, { k: "footer.follow", label: "Kolom Volgen" }, { k: "footer.copy", label: "Copyright" }, { k: "footer.made", label: "Onderste regel" }] },
];

const TEXT_SECTIONS_LANDING = [
  { key: "whero", label: "Hero (bovenaan)", fields: [{ k: "welkom.tag", label: "Label boven titel" }, { k: "welkom.h1.1", label: "Grote titel — regel 1" }, { k: "welkom.h1.2", label: "Grote titel — regel 2" }, { k: "welkom.h1.3", label: "Grote titel — regel 3" }, { k: "welkom.intro", label: "Introductietekst" }, { k: "welkom.cta", label: "Grote knop" }] },
  { key: "wvalues", label: "Voordelen (3 kaarten)", fields: [{ k: "welkom.v1.t", label: "Kaart 1 — titel" }, { k: "welkom.v1.b", label: "Kaart 1 — tekst" }, { k: "welkom.v2.t", label: "Kaart 2 — titel" }, { k: "welkom.v2.b", label: "Kaart 2 — tekst" }, { k: "welkom.v3.t", label: "Kaart 3 — titel" }, { k: "welkom.v3.b", label: "Kaart 3 — tekst" }] },
  { key: "wgallery", label: "Fotoblok", fields: [{ k: "welkom.gallery.tag", label: "Label" }, { k: "welkom.gallery.h1.1", label: "Kop — regel 1" }, { k: "welkom.gallery.h1.2", label: "Kop — regel 2" }, { k: "welkom.gallery.p", label: "Tekst" }] },
  { key: "wsteps", label: "Vijf stappen", fields: [{ k: "welkom.steps.tag", label: "Label" }, { k: "welkom.steps.h1.1", label: "Kop — regel 1" }, { k: "welkom.steps.h1.2", label: "Kop — regel 2" }, { k: "welkom.s1.t", label: "Stap 1 — titel" }, { k: "welkom.s1.b", label: "Stap 1 — tekst" }, { k: "welkom.s2.t", label: "Stap 2 — titel" }, { k: "welkom.s2.b", label: "Stap 2 — tekst" }, { k: "welkom.s3.t", label: "Stap 3 — titel" }, { k: "welkom.s3.b", label: "Stap 3 — tekst" }, { k: "welkom.s4.t", label: "Stap 4 — titel" }, { k: "welkom.s4.b", label: "Stap 4 — tekst" }, { k: "welkom.s5.t", label: "Stap 5 — titel" }, { k: "welkom.s5.b", label: "Stap 5 — tekst" }] },
  { key: "wform", label: "Boekingsformulier", fields: [{ k: "welkom.form.tag", label: "Label" }, { k: "welkom.form.h1.1", label: "Kop — regel 1" }, { k: "welkom.form.h1.2", label: "Kop — regel 2" }, { k: "welkom.form.reply", label: "Reactietijd-tekst" }, { k: "welkom.form.badge", label: "Badge in formulier" }, { k: "welkom.form.submit", label: "Verstuurknop" }] },
  { key: "walgemeen", label: "Algemeen", fields: [{ k: "welkom.back", label: "Terug-link (naar site)" }] },
];

// Bewerkbare automatische e-mails staan nu in Admin → E-mails (EmailManager hieronder).
// De oude statische EMAIL_SECTIONS-lijst is vervangen door het backend-register EMAIL_TEMPLATES.

// Module-level panelen (stabiele component-identiteit → geen focusverlies in invoervelden)
function TextsPanel({ tag, title, hint, sections, form, setForm, onSave, busy, testid }) {
  return (
    <div className="max-w-4xl" data-testid={testid}>
      <div className="section-tag section-tag-dark mb-4">— {tag}</div>
      <h2 className="font-display uppercase text-3xl md:text-5xl">{title}</h2>
      <p className="mt-4 text-[#DDD6CB] max-w-xl">{hint}</p>
      <div className="mt-10 space-y-10" data-testid={`${testid}-sections`}>
        {sections.map((sec) => (
          <div key={sec.key} className="border border-[#F5F3F0]/15 p-5" data-testid={`${testid}-${sec.key}`}>
            <div className="chip text-[#A3A3A3] mb-4">— {sec.label}</div>
            <div className="space-y-5">
              {sec.fields.map((f) => (
                <div key={f.k}>
                  <div className="chip text-[#DDD6CB] mb-2">{f.label}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <textarea rows={2} value={form[f.k]?.nl ?? i18nDict.nl[f.k] ?? ""} onChange={(e) => setForm(prev => ({ ...prev, [f.k]: { nl: e.target.value, en: prev[f.k]?.en ?? i18nDict.en[f.k] ?? "" } }))} className="editorial-input dark w-full" placeholder="NL" data-testid={`admin-text-${f.k}-nl`} />
                    <textarea rows={2} value={form[f.k]?.en ?? i18nDict.en[f.k] ?? ""} onChange={(e) => setForm(prev => ({ ...prev, [f.k]: { nl: prev[f.k]?.nl ?? i18nDict.nl[f.k] ?? "", en: e.target.value } }))} className="editorial-input dark w-full" placeholder="EN" data-testid={`admin-text-${f.k}-en`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={onSave} disabled={busy} className="btn-light mt-8" data-testid={`${testid}-save`}>{busy ? "Opslaan…" : "Alle teksten opslaan"}</button>
    </div>
  );
}

/* Admin → E-mails (spec 12-17): alle automatische mails bekijken/bewerken met
   gestructureerde velden binnen de vaste JOH-layout, [variabelen], preview en testmail. */
function EmailsPanel() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null); // {key, label, vars, subject, intro, cta_label, subject_en, intro_en, cta_label_en, greeting}
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLang, setPreviewLang] = useState("nl");
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState("templates"); // templates | log | broadcast

  const load = useCallback(() => {
    api.get("/admin/emails").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => { setEdit({ ...item }); setPreviewLang("nl"); };
  const currentDraft = () => ({
    subject: edit.subject, intro: edit.intro, cta_label: edit.cta_label,
    subject_en: edit.subject_en, intro_en: edit.intro_en, cta_label_en: edit.cta_label_en,
    greeting: edit.greeting,
  });
  const preview = async (lang = previewLang) => {
    setPreviewLang(lang);
    const { data } = await api.post("/admin/emails/preview", { key: edit.key, lang, draft: currentDraft() });
    setPreviewHtml(data.html);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/emails/${edit.key}`, currentDraft());
      toast.success("E-mailtemplate opgeslagen");
      setEdit(null); setPreviewHtml(null); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt"); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Terug naar de standaardtekst voor dit e-mailtype?")) return;
    setBusy(true);
    try {
      await api.put(`/admin/emails/${edit.key}`, { subject: "", intro: "", cta_label: "", subject_en: "", intro_en: "", cta_label_en: "", greeting: false });
      toast.success("Standaardtekst hersteld");
      setEdit(null); setPreviewHtml(null); load();
    } catch { toast.error("Herstellen mislukt"); }
    setBusy(false);
  };
  const testmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/test", {
        key: edit.key,
        lang: previewLang,
        draft: currentDraft(),
      });
      toast.success(`Testmail verstuurd naar ${data.to}`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Testmail mislukt"); }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl" data-testid="admin-emails-panel">
      <div className="section-tag section-tag-dark mb-4">— E-mails</div>
      <h2 className="font-display uppercase text-3xl md:text-5xl">E-mailbeheer.</h2>
      <p className="mt-4 text-[#DDD6CB] max-w-xl">
        Alle automatische JOH Boxing-mails in één vaste huisstijl. Pas onderwerp, aanhef, tekst en knop aan —
        het logo, de slogan en de footer blijven automatisch onderdeel van elke mail.
      </p>
      <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-emails-subnav">
        {[["templates", "Templates"], ["log", "Verzonden"], ["broadcast", "Broadcast"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setSub(k)}
            className={`chip px-4 py-2 border transition-colors ${sub === k ? "border-[#D4A93A] accent-ink bg-[#D4A93A]/10" : "border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0]"}`}
            data-testid={`admin-emails-sub-${k}`}>
            {lbl}
          </button>
        ))}
      </div>
      {sub === "log" && <MailLogPanel />}
      {sub === "broadcast" && <BroadcastPanel />}
      {sub === "templates" && (items === null ? <div className="chip text-[#A3A3A3] mt-10">Laden…</div> : (
        <div className="mt-10 line-t line-dark">
          {items.map((it) => (
            <div key={it.key} className="line-b line-dark py-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-email-row-${it.key}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase text-lg">{it.label}</div>
                <div className="text-[#A3A3A3] text-sm truncate mt-1">{it.subject}</div>
                <div className="chip text-[#4A4A4A] mt-1">
                  {it.updated_at ? `Laatst aangepast ${new Date(it.updated_at).toLocaleDateString("nl-NL")}` : "Nooit aangepast"}
                </div>
              </div>
              <span className={`chip px-2 py-0.5 border ${it.overridden ? "accent-ink border-[#D4A93A]/50" : "text-[#4A4A4A] border-[#F5F3F0]/15"}`} data-testid={`admin-email-status-${it.key}`}>
                {it.overridden ? "Aangepast" : "Standaard"}
              </span>
              <button onClick={() => openEdit(it)} className="chip px-4 py-2 border border-[#F5F3F0]/25 text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid={`admin-email-edit-${it.key}`}>
                Bewerken
              </button>
            </div>
          ))}
        </div>
      ))}

      {edit && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setEdit(null); setPreviewHtml(null); }}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-3xl w-full p-6 md:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-email-editor">
            <div className="chip text-[#A3A3A3]">— E-mail bewerken</div>
            <div className="font-display uppercase text-2xl md:text-3xl mt-1">{edit.label}</div>
            <div className="chip text-[#4A4A4A] mt-2" data-testid="admin-email-vars">
              Variabelen: {edit.vars.map(v => `[${v}]`).join(" ")}
            </div>
            <div className="space-y-5 mt-6">
              <div className="chip text-[#4A4A4A]">— Nederlands (NL)</div>
              <div>
                <div className="chip text-[#DDD6CB] mb-2">Onderwerp NL</div>
                <input value={edit.subject} onChange={(e) => setEdit(p => ({ ...p, subject: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-email-edit-subject"/>
              </div>
              <label className="flex items-center gap-3 chip text-[#A3A3A3] cursor-pointer" data-testid="admin-email-greeting-wrap">
                <input type="checkbox" checked={!!edit.greeting} onChange={(e) => setEdit(p => ({ ...p, greeting: e.target.checked }))} className="w-4 h-4 accent-[#D4A93A]" data-testid="admin-email-edit-greeting"/>
                Persoonlijke aanhef gebruiken ("Hoi [voornaam],")
              </label>
              <div>
                <div className="chip text-[#DDD6CB] mb-2">Tekst NL</div>
                <textarea rows={7} value={edit.intro} onChange={(e) => setEdit(p => ({ ...p, intro: e.target.value }))} className="editorial-input dark w-full" placeholder="Leeg = standaardtekst. Gebruik [voornaam], [les], [bedrag] enz." data-testid="admin-email-edit-intro"/>
              </div>
              <div>
                <div className="chip text-[#DDD6CB] mb-2">CTA-knop NL</div>
                <input value={edit.cta_label} onChange={(e) => setEdit(p => ({ ...p, cta_label: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-email-edit-cta"/>
              </div>
              {edit.bilingual && (
                <div className="border border-[#F5F3F0]/15 p-4 space-y-5" data-testid="admin-email-en-fields">
                  <div className="chip" style={{ color: "var(--joh-accent)" }}>— English (EN)</div>
                  <div>
                    <div className="chip text-[#DDD6CB] mb-2">Onderwerp EN</div>
                    <input value={edit.subject_en || ""} onChange={(e) => setEdit(p => ({ ...p, subject_en: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-email-edit-subject-en"/>
                  </div>
                  <div>
                    <div className="chip text-[#DDD6CB] mb-2">Tekst EN</div>
                    <textarea rows={6} value={edit.intro_en || ""} onChange={(e) => setEdit(p => ({ ...p, intro_en: e.target.value }))} className="editorial-input dark w-full" placeholder="Empty = English default. Use [voornaam] too." data-testid="admin-email-edit-intro-en"/>
                  </div>
                  <div>
                    <div className="chip text-[#DDD6CB] mb-2">CTA-knop EN</div>
                    <input value={edit.cta_label_en || ""} onChange={(e) => setEdit(p => ({ ...p, cta_label_en: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-email-edit-cta-en"/>
                  </div>
                  <p className="chip text-[#4A4A4A]">Leeg EN-veld = Engelse standaardtekst. NL-velden worden nooit aan EN-klanten getoond.</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={save} disabled={busy} className="btn-light" data-testid="admin-email-save">{busy ? "…" : "Opslaan"}</button>
              <button onClick={() => preview("nl")} className={`chip px-4 py-2.5 border ${previewLang === "nl" ? "border-[#D4A93A] accent-ink" : "border-[#F5F3F0]/25 text-[#F5F3F0]"}`} data-testid="admin-email-preview-nl">Preview NL</button>
              {edit.bilingual && <button onClick={() => preview("en")} className={`chip px-4 py-2.5 border ${previewLang === "en" ? "border-[#D4A93A] accent-ink" : "border-[#F5F3F0]/25 text-[#F5F3F0]"}`} data-testid="admin-email-preview-en">Preview EN</button>}
              <button onClick={testmail} disabled={busy} className="chip px-4 py-2.5 border inline-flex items-center gap-2" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="admin-email-test-btn">
                <Mail size={13}/> Testmail {edit.bilingual ? previewLang.toUpperCase() : ""} versturen
              </button>
              <button onClick={reset} disabled={busy} className="chip px-4 py-2.5 border border-[#F87171]/40 text-[#F87171]" data-testid="admin-email-reset">Herstel standaard</button>
              <button onClick={() => { setEdit(null); setPreviewHtml(null); }} className="chip px-4 py-2.5 text-[#A3A3A3]" data-testid="admin-email-close">Sluiten</button>
            </div>
            {previewHtml && (
              <div className="mt-8" data-testid="admin-email-preview">
                <div className="chip text-[#A3A3A3] mb-3">— Preview (incl. logo, slogan en footer)</div>
                <iframe title="email-preview" srcDoc={previewHtml} className="w-full h-[480px] border border-[#F5F3F0]/20 bg-white" data-testid="admin-email-preview-frame"/>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}




/* Admin → E-mails → Verzonden: doorzoekbare database van alle uitgaande mails. */
function MailLogPanel() {
  const [data, setData] = useState(null);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/emails/log", { params: { q, type, status, page, limit: 25 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: 0, types: [] }));
  }, [q, type, status, page]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/emails/log/${id}`); setDetail(data); }
    catch { toast.error("Kon e-mail niet laden"); }
  };

  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / 25)) : 1;
  const statusColor = { sent: "#4ADE80", failed: "#F87171", skipped: "#A3A3A3" };

  return (
    <div className="mt-8" data-testid="admin-maillog-panel">
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(qInput); }} className="relative flex-1">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
          <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Zoek op naam, e-mail of onderwerp…"
            className="editorial-input dark pl-6 w-full" data-testid="admin-maillog-search"/>
        </form>
        <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} className="editorial-input dark md:w-56" data-testid="admin-maillog-type-filter">
          <option value="" className="bg-[#0A0A0A]">Alle types</option>
          {(data?.types || []).map(t => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
        </select>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="editorial-input dark md:w-44" data-testid="admin-maillog-status-filter">
          <option value="" className="bg-[#0A0A0A]">Alle statussen</option>
          <option value="sent" className="bg-[#0A0A0A]">Verzonden</option>
          <option value="failed" className="bg-[#0A0A0A]">Mislukt</option>
          <option value="skipped" className="bg-[#0A0A0A]">Overgeslagen</option>
        </select>
      </div>

      {data === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : data.items.length === 0 ? (
        <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="admin-maillog-empty">
          {q || type || status ? "Geen e-mails gevonden voor deze filters." : "Nog geen e-mails verzonden."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto" data-testid="admin-maillog-list">
            <div className="min-w-[860px]">
              <div className="grid grid-cols-12 gap-3 chip text-[#4A4A4A] pb-2 line-b line-dark">
                <span className="col-span-2">Datum</span>
                <span className="col-span-2">Naam</span>
                <span className="col-span-3">E-mail</span>
                <span className="col-span-3">Onderwerp</span>
                <span className="col-span-1">Type</span>
                <span className="col-span-1 text-right">Status</span>
              </div>
              {data.items.map((m) => (
                <div key={m.id} role="button" tabIndex={0} onClick={() => openDetail(m.id)} onKeyDown={(e) => { if (e.key === "Enter") openDetail(m.id); }}
                  className="w-full text-left line-b line-dark py-3 grid grid-cols-12 gap-3 items-center hover:bg-white/[0.03] transition-colors cursor-pointer"
                  data-testid={`admin-maillog-row-${m.id}`}>
                  <span className="col-span-2 chip text-[#A3A3A3]">{m.created_at ? new Date(m.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                  <span className="col-span-2 text-sm truncate">{m.name || "—"}</span>
                  <span className="col-span-3 text-[#DDD6CB] text-sm truncate">{m.to}</span>
                  <span className="col-span-3 text-sm truncate">{m.subject}</span>
                  <span className="col-span-1 chip text-[#4A4A4A] truncate">{m.type}</span>
                  <span className="col-span-1 chip text-right" style={{ color: statusColor[m.status] || "#A3A3A3" }} data-testid={`admin-maillog-status-${m.id}`}>
                    {m.status === "sent" ? "Verzonden" : m.status === "failed" ? "Mislukt" : m.status === "skipped" ? "Overgeslagen" : m.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between mt-6">
            <span className="chip text-[#4A4A4A]" data-testid="admin-maillog-total">{data.total} e-mails · pagina {page}/{pages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="chip px-4 py-2 border border-[#F5F3F0]/25 text-[#F5F3F0] disabled:opacity-30 hover:bg-[#F5F3F0]/10" data-testid="admin-maillog-prev">Vorige</button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="chip px-4 py-2 border border-[#F5F3F0]/25 text-[#F5F3F0] disabled:opacity-30 hover:bg-[#F5F3F0]/10" data-testid="admin-maillog-next">Volgende</button>
            </div>
          </div>
        </>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-3xl w-full p-6 md:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-maillog-detail">
            <div className="chip text-[#A3A3A3]">— Verzonden e-mail</div>
            <div className="font-display uppercase text-2xl md:text-3xl mt-1 break-words">{detail.subject}</div>
            <div className="mt-4 space-y-1.5">
              <div className="chip text-[#A3A3A3]">Aan: {detail.name ? `${detail.name} · ` : ""}{detail.to}</div>
              <div className="chip text-[#4A4A4A]">{detail.created_at ? new Date(detail.created_at).toLocaleString("nl-NL") : ""} · {detail.type} · {detail.status === "sent" ? "Verzonden" : detail.status === "failed" ? "Mislukt" : detail.status}</div>
            </div>
            <iframe title="mail-detail" srcDoc={detail.html || ""} className="w-full h-[440px] border border-[#F5F3F0]/20 bg-white mt-6" data-testid="admin-maillog-detail-frame"/>
            <div className="mt-6">
              <button onClick={() => setDetail(null)} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-[#F5F3F0]" data-testid="admin-maillog-detail-close">Sluiten</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Admin → E-mails → Broadcast: vrije mail naar alle actieve leden, met verplichte bevestiging. */
function BroadcastPanel() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false); // harde guard: nooit twee broadcasts tegelijk

  const loadPreview = useCallback(() => {
    api.get("/admin/emails/broadcast/preview")
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview({ count: 0, recipients: [] }));
  }, []);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/broadcast", { subject, message, confirm: true });
      toast.success(`Broadcast verstuurd naar ${data.recipients} actieve ${data.recipients === 1 ? "lid" : "leden"}`);
      setSubject(""); setMessage(""); setConfirming(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Versturen mislukt");
    }
    setSending(false);
    inFlight.current = false;
  };

  const ready = subject.trim() && message.trim() && (preview?.count || 0) > 0;

  return (
    <div className="mt-8 max-w-2xl" data-testid="admin-broadcast-panel">
      <p className="text-[#DDD6CB]">
        Stuur een vrije e-mail naar <span className="accent-ink">alle actieve leden</span> (actief membership of resterende credits).
        Verzonden vanaf team@johboxing.com, in de vaste JOH-huisstijl. Variabelen: [voornaam], [naam].
      </p>
      <div className="space-y-5 mt-6">
        <div>
          <div className="chip text-[#DDD6CB] mb-2">Onderwerp</div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="editorial-input dark w-full" placeholder="Bv. Trainerstip van deze week" data-testid="admin-broadcast-subject"/>
        </div>
        <div>
          <div className="chip text-[#DDD6CB] mb-2">Bericht</div>
          <textarea rows={8} value={message} onChange={(e) => setMessage(e.target.value)} className="editorial-input dark w-full" placeholder="Hoi [voornaam], …" data-testid="admin-broadcast-message"/>
        </div>
      </div>
      <div className="mt-6 border border-[#F5F3F0]/15 p-5" data-testid="admin-broadcast-recipients">
        <div className="flex items-center justify-between gap-4">
          <div className="chip text-[#A3A3A3]">
            {preview === null ? "Ontvangers laden…" : <>Ontvangers: <span className="accent-ink">{preview.count} actieve {preview.count === 1 ? "lid" : "leden"}</span></>}
          </div>
          <button onClick={loadPreview} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" data-testid="admin-broadcast-refresh">Ververs</button>
        </div>
        {(preview?.recipients || []).length > 0 && (
          <div className="chip text-[#4A4A4A] mt-3 break-words" data-testid="admin-broadcast-recipient-list">
            {preview.recipients.slice(0, 12).map(r => r.name ? `${r.name} (${r.email})` : r.email).join(" · ")}
            {preview.count > 12 ? ` · +${preview.count - 12} meer` : ""}
          </div>
        )}
      </div>
      <div className="mt-6">
        <button onClick={() => setConfirming(true)} disabled={!ready} className="btn-light disabled:opacity-40" data-testid="admin-broadcast-send-open">
          <Megaphone size={14}/> Verstuur broadcast
        </button>
        {preview !== null && preview.count === 0 && <div className="chip text-[#A3A3A3] mt-3">Geen actieve leden gevonden — er valt niemand te mailen.</div>}
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !sending && setConfirming(false)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-broadcast-confirm">
            <div className="chip text-[#A3A3A3]">— Bevestiging</div>
            <div className="font-display uppercase text-2xl mt-1">Zeker weten?</div>
            <p className="text-[#DDD6CB] mt-4">
              Je verstuurt <span className="accent-ink">"{subject}"</span> naar <span className="accent-ink">{preview?.count || 0} actieve leden</span>.
              Dit gaat direct de deur uit en kan niet ongedaan worden.
            </p>
            <div className="flex gap-3 mt-8">
              <button onClick={send} disabled={sending} className="btn-light flex-1 justify-center" data-testid="admin-broadcast-confirm-send">
                {sending ? "Versturen…" : `Ja, verstuur naar ${preview?.count || 0} leden`}
              </button>
              <button onClick={() => setConfirming(false)} disabled={sending} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#A3A3A3]" data-testid="admin-broadcast-confirm-cancel">Annuleer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Individuele vrije e-mail vanuit een ledenprofiel (gelogd in Verzonden). */
function EmailComposerModal({ email, name, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // harde guard tegen dubbele verzending (snelle kliks/remount)

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await api.post("/admin/emails/send-one", { email, name, subject, message });
      toast.success(`E-mail verstuurd naar ${email}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Versturen mislukt");
    }
    setBusy(false);
    inFlight.current = false;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="email-composer-modal">
        <div className="chip text-[#A3A3A3]">— E-mail sturen</div>
        <div className="font-display uppercase text-2xl mt-1 break-words">{name || email}</div>
        <div className="chip text-[#4A4A4A] mt-1 break-words">{email} · via team@johboxing.com · variabelen: [voornaam], [naam]</div>
        <div className="space-y-5 mt-6">
          <div>
            <div className="chip text-[#DDD6CB] mb-2">Onderwerp</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="editorial-input dark w-full" data-testid="email-composer-subject"/>
          </div>
          <div>
            <div className="chip text-[#DDD6CB] mb-2">Bericht</div>
            <textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="editorial-input dark w-full" placeholder="Hoi [voornaam], …" data-testid="email-composer-message"/>
          </div>
        </div>
        <div className="flex gap-3 mt-8">
          <button onClick={send} disabled={busy || !subject.trim() || !message.trim()} className="btn-light flex-1 justify-center disabled:opacity-40" data-testid="email-composer-send">
            <Send size={14}/> {busy ? "Versturen…" : "Verstuur e-mail"}
          </button>
          <button onClick={onClose} disabled={busy} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#A3A3A3]" data-testid="email-composer-cancel">Annuleer</button>
        </div>
      </div>
    </div>
  );
}



/* Admin → Toegang → Audit log: wie deed wat, wanneer (admins én owners). */
const AUDIT_LABELS = {
  "team.admin_add": "Admin toegevoegd", "team.owner_add": "Owner toegevoegd",
  "team.admin_promote": "Gepromoveerd tot admin", "team.owner_promote": "Gepromoveerd tot owner",
  "team.admin_remove": "Admin verwijderd", "team.role_change": "Rol gewijzigd",
  "password.own_change": "Eigen wachtwoord gewijzigd", "password.set_other": "Wachtwoord gezet voor",
  "email.send_one": "Individuele e-mail", "email.broadcast": "Broadcast verstuurd",
  "email.template_save": "E-mailtemplate aangepast", "member.archive": "Klant gearchiveerd",
};

function AuditLogPanel() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    api.get("/admin/audit-log", { params: { page, limit: 50 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: 0 }));
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / 50)) : 1;

  return (
    <div className="mt-12 border border-[#F5F3F0]/15 p-5 md:p-6" data-testid="admin-audit-panel">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="chip text-[#A3A3A3]">— Audit log · wie deed wat</div>
        <button onClick={load} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" data-testid="admin-audit-refresh">Ververs</button>
      </div>
      {data === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : data.items.length === 0 ? (
        <div className="chip text-[#4A4A4A] py-4" data-testid="admin-audit-empty">Nog geen acties gelogd.</div>
      ) : (
        <>
          <div className="line-t line-dark" data-testid="admin-audit-list">
            {data.items.map((a) => (
              <div key={a.id} className="line-b line-dark py-3 grid grid-cols-12 gap-3 items-center" data-testid={`admin-audit-row-${a.id}`}>
                <span className="col-span-3 md:col-span-2 chip text-[#4A4A4A]">{a.created_at ? new Date(a.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                <span className="col-span-4 md:col-span-3 chip text-[#DDD6CB] truncate">{a.actor}</span>
                <span className="col-span-5 md:col-span-3 text-sm truncate">{AUDIT_LABELS[a.action] || a.action}</span>
                <span className="col-span-12 md:col-span-4 chip text-[#A3A3A3] truncate">{[a.target, a.detail].filter(Boolean).join(" · ")}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="chip text-[#4A4A4A]" data-testid="admin-audit-total">{data.total} acties · pagina {page}/{pages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="chip px-4 py-2 border border-[#F5F3F0]/25 text-[#F5F3F0] disabled:opacity-30 hover:bg-[#F5F3F0]/10" data-testid="admin-audit-prev">Vorige</button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="chip px-4 py-2 border border-[#F5F3F0]/25 text-[#F5F3F0] disabled:opacity-30 hover:bg-[#F5F3F0]/10" data-testid="admin-audit-next">Volgende</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}



function MediaPanel({ form, files, busy, onUpload, onSetUrl, onReset, onDeleteFile }) {
  return (
    <div className="max-w-5xl" data-testid="admin-media-panel">
      <div className="section-tag section-tag-dark mb-4">— Afbeeldingen &amp; video</div>
      <h2 className="font-display uppercase text-3xl md:text-5xl">Media aanpassen.</h2>
      <p className="mt-4 text-[#DDD6CB] max-w-xl">Vervang elke foto of video op de site: upload een bestand óf plak een link. "Herstel" zet de standaard terug. Wijzigingen zijn direct zichtbaar.</p>
      <div className="mt-10 space-y-12">
        {MEDIA_GROUPS.map((g) => (
          <div key={g.label}>
            <div className="chip text-[#A3A3A3] mb-4">— {g.label}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {g.slots.map((slot) => {
                const override = (form[slot.key] || "").trim();
                const current = override || DEFAULT_MEDIA[slot.key] || "";
                const isVideo = slot.kind === "video";
                return (
                  <div key={slot.key} className="border border-[#F5F3F0]/15 p-4" data-testid={`admin-media-slot-${slot.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="chip text-[#DDD6CB]">{slot.label}</div>
                      <div className="chip" style={{ color: override ? "var(--joh-accent)" : "#4A4A4A" }}>{override ? "Aangepast" : "Standaard"}</div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#F5F3F0]/10 h-40 flex items-center justify-center overflow-hidden">
                      {isVideo
                        ? <video src={current} muted loop autoPlay playsInline className="h-full w-full object-cover" data-testid={`admin-media-preview-${slot.key}`} />
                        : <img src={current} alt="" className="h-full w-full object-cover" data-testid={`admin-media-preview-${slot.key}`} />}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <label className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0]/40 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] cursor-pointer inline-flex items-center gap-2" data-testid={`admin-media-upload-label-${slot.key}`}>
                        {busy === slot.key ? "Uploaden…" : "Upload bestand"}
                        <input type="file" accept={isVideo ? "video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png,image/webp,image/gif"} className="hidden" disabled={busy === slot.key} onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(slot.key, f); e.target.value = ""; }} data-testid={`admin-media-upload-${slot.key}`} />
                      </label>
                      {override && (
                        <button onClick={() => onReset(slot.key)} className="chip text-red-400 hover:text-red-300" data-testid={`admin-media-reset-${slot.key}`}>Herstel standaard</button>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input placeholder="of plak een https:// link…" className="editorial-input dark flex-1" id={`media-url-${slot.key}`} data-testid={`admin-media-url-${slot.key}`} />
                      <button onClick={() => { const el = document.getElementById(`media-url-${slot.key}`); onSetUrl(slot.key, el.value); el.value = ""; }} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0]/40 hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid={`admin-media-url-save-${slot.key}`}>Gebruik link</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14" data-testid="admin-media-files">
        <div className="chip text-[#A3A3A3] mb-4">— Geüploade bestanden ({files.length})</div>
        {files.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-6 text-center chip text-[#A3A3A3]" data-testid="admin-media-files-empty">Nog geen uploads.</div>
        ) : (
          <div className="line-t line-dark">
            {files.map((f) => (
              <div key={f.id} className="line-b line-dark py-3 flex items-center justify-between gap-4" data-testid={`admin-media-file-${f.id}`}>
                <span className="text-[#DDD6CB] text-sm truncate">{f.original_filename} <span className="chip text-[#4A4A4A] ml-2">{Math.round((f.size || 0) / 1024)} KB</span></span>
                <button onClick={() => onDeleteFile(f.id)} className="chip text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`admin-media-file-delete-${f.id}`}><Trash2 size={14}/> Verwijder</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_META = {
  new: { label: "Nieuw", color: "#F5F3F0", bg: "rgba(245, 243, 240, 0.08)" },
  planned: { label: "In behandeling", color: "var(--joh-accent)", bg: "rgba(245, 168, 43, 0.12)" },
  approved: { label: "Goedgekeurd", color: "#4ADE80", bg: "rgba(74, 222, 128, 0.12)" },
  handled: { label: "Afgerond", color: "#4ADE80", bg: "rgba(74, 222, 128, 0.12)" },
  declined: { label: "Geannuleerd", color: "#F87171", bg: "rgba(248, 113, 113, 0.10)" },
};

const WEEKDAY_NL = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];

/* JOH World top-scores — live leaderboard in Admin */
function jwFmt(ms) {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

function JohWorldScoresPanel() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/joh-world/leaderboard?limit=10");
      setRows(data.items);
    } catch {
      toast.error("JOH World scores laden mislukt");
    }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000); // live meeverversen
    return () => clearInterval(iv);
  }, [load]);

  return (
    <div data-testid="admin-johworld-scores">
      <div className="section-tag section-tag-dark mb-4">— JOH World</div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h3 className="font-display uppercase text-2xl md:text-3xl">Fastest Fighters.</h3>
        <button onClick={load} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/20 hover:text-[#F5F3F0] text-[10px]" data-testid="admin-jw-scores-refresh">
          Ververs
        </button>
      </div>
      <p className="mt-2 text-[#DDD6CB] max-w-xl text-sm">Live top 10 van johboxing.com/johworld — gerangschikt op snelste totale tijd. Ververs automatisch elke 15 seconden.</p>
      <div className="mt-5 border-t border-[#F5F3F0]/10">
        {rows === null && <div className="chip text-[#A3A3A3] py-6">Laden…</div>}
        {rows !== null && rows.length === 0 && (
          <div className="chip text-[#4A4A4A] py-6" data-testid="admin-jw-scores-empty">Nog geen scores — de eerste fighter moet nog finishen.</div>
        )}
        {(rows || []).map((r) => (
          <div key={r.rank} className="flex items-center gap-4 py-3 border-b border-[#F5F3F0]/10" data-testid={`admin-jw-score-row-${r.rank}`}>
            <span className="font-display text-xl w-10 text-[#FFC61A]">{["🥇", "🥈", "🥉"][r.rank - 1] || `#${r.rank}`}</span>
            <span className="flex-1 font-medium">{r.name}</span>
            <span className="chip text-[#FFC61A]" data-testid={`admin-jw-score-time-${r.rank}`}>{jwFmt(r.time_ms)}</span>
            <span className="chip text-[#A3A3A3] hidden sm:inline">{r.score} PTS</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* JOH World muziekbeheer — mp3 uploaden en koppelen aan een level/positie in het spel */
const JW_MUSIC_SLOTS = [
  { key: "intro", label: "Intro", hint: "vóór Level 1 (titelscherm)" },
  { key: "level1", label: "Level 1", hint: "Road to JOH Gym" },
  { key: "level2", label: "Level 2", hint: "Push-Up Challenge" },
  { key: "level3", label: "Level 3", hint: "Bag Memory" },
  { key: "final", label: "Finale", hint: "einde spel / leaderboard" },
];

function JohWorldMusicPanel() {
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/joh-world/music");
      setSlots(data.slots);
    } catch {
      toast.error("JOH World muziek laden mislukt");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/joh-world/admin/music/${slot}`, fd);
      toast.success(`Track gekoppeld aan ${slot}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload mislukt");
    }
    setBusy("");
  };
  const reset = async (slot) => {
    setBusy(slot);
    try {
      await api.delete(`/joh-world/admin/music/${slot}`);
      toast.success("Standaardtrack hersteld");
      await load();
    } catch {
      toast.error("Herstellen mislukt");
    }
    setBusy("");
  };

  return (
    <div data-testid="admin-johworld-music">
      <div className="section-tag section-tag-dark mb-4">— JOH World</div>
      <h3 className="font-display uppercase text-2xl md:text-3xl">Game-muziek.</h3>
      <p className="mt-3 text-[#DDD6CB] max-w-xl text-sm">Upload een eigen mp3 per level of positie in het spel. De track start automatisch bij het level en stopt bij de overgang. "Herstel" zet de standaardtrack terug.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {JW_MUSIC_SLOTS.map((s) => {
          const cur = slots?.[s.key];
          return (
            <div key={s.key} className="border border-[#F5F3F0]/15 p-4" data-testid={`admin-music-slot-${s.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="chip text-[#F5F3F0]">{s.label}</div>
                  <div className="chip text-[#4A4A4A] mt-0.5">{s.hint}</div>
                </div>
                <span className={`chip ${cur?.custom ? "text-[#C6FF3C]" : "text-[#A3A3A3]"}`} data-testid={`admin-music-status-${s.key}`}>
                  {cur ? (cur.custom ? "Eigen upload" : "Standaard") : "…"}
                </span>
              </div>
              {cur && (
                <audio controls preload="none" src={cur.url} className="w-full mt-3 h-8" data-testid={`admin-music-preview-${s.key}`} />
              )}
              <div className="chip text-[#4A4A4A] mt-1 truncate" data-testid={`admin-music-name-${s.key}`}>{cur?.name || "…"}</div>
              <div className="flex gap-2 mt-3">
                <label className={`btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0]/40 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] cursor-pointer inline-flex items-center gap-2 ${busy === s.key ? "opacity-50 pointer-events-none" : ""}`} data-testid={`admin-music-upload-${s.key}`}>
                  {busy === s.key ? "Uploaden…" : "Upload mp3"}
                  <input type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={(e) => { upload(s.key, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                {cur?.custom && (
                  <button onClick={() => reset(s.key)} disabled={busy === s.key} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/20 hover:text-[#F5F3F0]" data-testid={`admin-music-reset-${s.key}`}>
                    Herstel
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = (dt.getUTCDay() + 6) % 7;
  return { weekday: WEEKDAY_NL[wd], day: dt.getUTCDate(), month: dt.toLocaleString("nl-NL", { month: "short", timeZone: "UTC" }) };
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("home");
  const [navOpen, setNavOpen] = useState(false);
  const [dash, setDash] = useState(null); // /admin/dashboard payload
  const [aanmeldingenFilter, setAanmeldingenFilter] = useState("alle");
  const [listSearch, setListSearch] = useState("");
  const [listSort, setListSort] = useState("date_new");
  const [memberSort, setMemberSort] = useState("date_new");
  const [paymentSort, setPaymentSort] = useState("date_new");
  // Leden / klantenbestand
  const [memberStatusFilter, setMemberStatusFilter] = useState("");
  const [memberGroup, setMemberGroup] = useState("all"); // all | active | inactive
  const [emailTo, setEmailTo] = useState(null); // {email, name} → EmailComposerModal
  const [birthdays, setBirthdays] = useState(null); // aankomende verjaardagen (home)
  // Ingelogde admin/owner (stuurt rol-badges + owner-acties in Team-tab)
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);
  const isOwner = me?.role === "owner";
  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({ first_name: "", last_name: "", email: "", phone: "", membership: "", start_date: "", status: "actief", notes: "", class_id: "" });
  // lessen laden voor de optionele les-koppeling in het klant-formulier
  useEffect(() => {
    if (!memberFormOpen || classes.length > 0) return;
    api.get("/admin/classes").then(({ data }) => setClasses(data.items || [])).catch(() => {});
  }, [memberFormOpen]); // eslint-disable-line
  const [memberFormBusy, setMemberFormBusy] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberHistory, setMemberHistory] = useState(null);
  // Volledige klantgeschiedenis laden zodra een klantprofiel opent
  useEffect(() => {
    setMemberHistory(null);
    if (!selectedMember?.email) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/members/${encodeURIComponent(selectedMember.email)}/history`);
        setMemberHistory(data);
      } catch { /* historie is aanvullend; modal blijft werken */ }
    })();
  }, [selectedMember]); // eslint-disable-line
  // Betalingen: Stripe testbetaling
  const [testPayBusy, setTestPayBusy] = useState(false);
  const [testPayResult, setTestPayResult] = useState(null);
  // Archief (lesaanmeldingen > 1 maand)
  const [archiveData, setArchiveData] = useState(null);
  const [archiveCat, setArchiveCat] = useState("betalingen");
  const [archiveQuery, setArchiveQuery] = useState("");
  const [archiveSort, setArchiveSort] = useState("recent");
  const [archivePaySub, setArchivePaySub] = useState("alle");
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState("all"); // all / paid / open / overdue
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [activity, setActivity] = useState([]);
  const [expandedPerson, setExpandedPerson] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  // Maandoverzicht aanwezigheid & no-shows (Overzicht-tab)
  const [attMonth, setAttMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [attMonthly, setAttMonthly] = useState(null);
  const [attMonthlyBusy, setAttMonthlyBusy] = useState(false);
  useEffect(() => {
    if (tab !== "home") return;
    setAttMonthlyBusy(true);
    api.get("/admin/attendance/monthly", { params: { month: attMonth } })
      .then(({ data }) => setAttMonthly(data))
      .catch(() => setAttMonthly(null))
      .finally(() => setAttMonthlyBusy(false));
  }, [tab, attMonth]); // eslint-disable-line
  const shiftAttMonth = (delta) => {
    const [y, m] = attMonth.split("-").map(Number);
    setAttMonth(new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 7));
  };
  const attMonthLabel = new Date(Date.UTC(+attMonth.slice(0, 4), +attMonth.slice(5, 7) - 1, 1))
    .toLocaleString("nl-NL", { month: "long", year: "numeric", timeZone: "UTC" });
  const [stats, setStats] = useState({ trial: 0, pt: 0, contact: 0, total: 0 });
  const [selected, setSelected] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [magicLink, setMagicLink] = useState("");
  const [magicDays, setMagicDays] = useState(30);
  const [magicLoading, setMagicLoading] = useState(false);
  const [team, setTeam] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [formsSearch, setFormsSearch] = useState("");
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", kind: "percent", percent: 10, amount_eur: "5", packages: ["all"], starts_at: "", ends_at: "", max_uses: "", active: true });
  const [promoBusy, setPromoBusy] = useState(false);
  // Site-content (CMS) state
  const [scheduleRows, setScheduleRows] = useState([]);
  const [scheduleProgramStart, setScheduleProgramStart] = useState("");
  const [teaserForm, setTeaserForm] = useState({ sub_nl: "", sub_en: "", cards: [] });
  const [annForm, setAnnForm] = useState({ active: false, date: "", text_nl: "", text_en: "" });
  const [bannerForm, setBannerForm] = useState({ active: false, text_nl: "", text_en: "", code: "" });
  const [textsForm, setTextsForm] = useState({});
  const [mediaForm, setMediaForm] = useState({});
  const [emailsForm, setEmailsForm] = useState({});
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaBusy, setMediaBusy] = useState("");
  const [contentBusy, setContentBusy] = useState(false);
  // Wachtwoord-beheer
  const [pwForm, setPwForm] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwResetFor, setPwResetFor] = useState("");
  const [pwResetValue, setPwResetValue] = useState("");
  // Proefles beheer
  const [trialDay, setTrialDay] = useState("");
  // Weekplanning extra items
  const [weekItems, setWeekItems] = useState([]);
  const [weekForm, setWeekForm] = useState({ title: "", date: "", time: "", kind: "event", description: "" });
  const [weekEditId, setWeekEditId] = useState("");
  const [weekBusy, setWeekBusy] = useState(false);
  // Proefles instellingen
  const [trialSettingsForm, setTrialSettingsForm] = useState({ enabled: true, date: "" });

  // Auto-login from ?token=... in the URL (bookmarkable magic link)
  useEffect(() => {
    const t = searchParams.get("token");
    if (t) {
      localStorage.setItem("joh_admin_token", t);
      searchParams.delete("token");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      if (tab === "home") {
        const d = await api.get("/admin/dashboard");
        setDash(d.data);
        setActivity(d.data.recent_activity || []);
        api.get("/admin/birthdays", { params: { days: 30 } }).then(({ data }) => setBirthdays(data.items || [])).catch(() => {});
      } else if (tab === "aanmeldingen" || tab === "trials") {
        const [sub, p, s] = await Promise.all([api.get("/admin/submissions"), api.get("/admin/payments"), api.get("/admin/stats")]);
        setItems(sub.data.items || []); setPayments(p.data.items || []); setStats(s.data);
        if (tab === "trials") {
          const c = await api.get("/site/content");
          setTrialSettingsForm(c.data.trial_settings || { enabled: true, date: "" });
        }
      } else if (tab === "classes") {
        const [c, s] = await Promise.all([api.get("/admin/classes"), api.get("/admin/stats")]);
        setClasses(c.data.items || []); setStats(s.data);
      } else if (tab === "archive") {
        const a = await api.get("/admin/archive");
        setArchiveData(a.data || { categories: {} });
      } else if (tab === "payments") {
        const [p, s] = await Promise.all([api.get("/admin/payments"), api.get("/admin/stats")]);
        setPayments(p.data.items || []); setStats(s.data);
      } else if (tab === "members") {
        const [m, s] = await Promise.all([api.get("/admin/members"), api.get("/admin/stats")]);
        setMembers(m.data.items || []); setStats(s.data);
      } else if (tab === "team") {
        const [tm, s] = await Promise.all([api.get("/admin/team"), api.get("/admin/stats")]);
        setTeam(tm.data.items || []); setStats(s.data);
      } else if (tab === "promos") {
        const [p, s] = await Promise.all([api.get("/admin/promos"), api.get("/admin/stats")]);
        setPromos(p.data.items || []); setStats(s.data);
      } else if (CONTENT_TABS.includes(tab)) {
        const [c, s] = await Promise.all([api.get("/site/content"), api.get("/admin/stats")]);
        setStats(s.data);
        const cd = c.data;
        setScheduleRows(cd.schedule?.rows || []);
        setScheduleProgramStart(cd.schedule?.program_start || "2026-10-06");
        setTeaserForm(cd.teaser || { sub_nl: "", sub_en: "", cards: [] });
        setAnnForm(cd.announcement || { active: false, date: "", text_nl: "", text_en: "", url: "" });
        setBannerForm(cd.promo_banner || { active: false, text_nl: "", text_en: "", code: "" });
        setTextsForm(cd.text_overrides || {});
        setMediaForm(cd.image_overrides || {});
        setEmailsForm(cd.email_overrides || {});
        setTrialSettingsForm(cd.trial_settings || { enabled: true, date: "" });
        if (tab === "media") {
          const mf = await api.get("/admin/media");
          setMediaFiles(mf.data.items || []);
        }
        if (tab === "announcement") {
          const w = await api.get("/admin/week-items");
          setWeekItems(w.data.items || []);
        }
      } else if (tab === "access") {
        const s = await api.get("/admin/stats");
        setStats(s.data);
      } else {
        const params = tab === "all" ? {} : { type: tab };
        const [{ data }, s] = await Promise.all([api.get("/admin/submissions", { params }), api.get("/admin/stats")]);
        setItems(data.items || []); setStats(s.data);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Eén mislukte request mag de sessie niet wissen — verifieer het token eerst.
        // Alleen bij een écht ongeldige/verlopen sessie wordt uitgelogd.
        const token = localStorage.getItem("joh_admin_token");
        let valid = false;
        if (token) {
          try {
            const { data: me } = await api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
            valid = ["admin", "owner"].includes(me?.role);
          } catch { valid = false; }
        }
        if (valid) {
          toast.error("Kon gegevens niet laden");
          setLoadError(true);
          setLoading(false);
          return;
        }
        localStorage.removeItem("joh_admin_token");
        toast.error("Sessie verlopen — log opnieuw in");
        nav("/admin/login");
        return;
      }
      toast.error("Kon gegevens niet laden");
      setLoadError(true);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [tab]); // eslint-disable-line

  useEffect(() => { setTrialDay(selected?.data?.preferred_day || ""); }, [selected]);

  // Proefles-koppeling: laad de lessen van de proeflesdatum zodra een trial-modal opent
  const [trialLinkOptions, setTrialLinkOptions] = useState([]);
  const [trialLinkChoice, setTrialLinkChoice] = useState("");
  useEffect(() => {
    setTrialLinkOptions([]);
    setTrialLinkChoice("");
    if (selected?.type !== "trial" || selected?.status === "approved") return;
    const d = selected?.data || {};
    const isoDay = /^\d{4}-\d{2}-\d{2}$/.test(d.preferred_day || "") ? d.preferred_day : (d.trial_date || "");
    (async () => {
      try {
        let day = isoDay;
        if (!day) {
          // Zelfde fallback als backend: vaste proeflesdatum uit de instellingen
          const sc = await api.get("/site/content");
          day = sc.data?.trial_settings?.date || "";
        }
        if (!day) return;
        const { data } = await api.get("/admin/classes");
        const opts = (data.items || []).filter(c => c.date === day);
        setTrialLinkOptions(opts);
        if (opts.length) setTrialLinkChoice(opts[0].id);
      } catch { /* opties blijven leeg; backend kiest dan zelf */ }
    })();
  }, [selected]); // eslint-disable-line

  // Rooster-editor helpers
  const updRow = (i, patch) => setScheduleRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setScheduleRows(prev => [...prev, { weekday: 1, day: "Dinsdag", dayEn: "Tuesday", start: "18:30", end: "20:00", type: "", level: "All levels", coach: "Glenn", capacity: 14 }]);
  const removeRow = (i) => setScheduleRows(prev => prev.filter((_, idx) => idx !== i));

  // Teaser-editor helpers
  const updCard = (i, patch) => setTeaserForm(f => ({ ...f, cards: f.cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const addCard = () => setTeaserForm(f => ({ ...f, cards: [...(f.cards || []), { day: "", dayEn: "", time: "", type: "", level: "" }] }));
  const removeCard = (i) => setTeaserForm(f => ({ ...f, cards: f.cards.filter((_, idx) => idx !== i) }));
  const cardsFromSchedule = () => setTeaserForm(f => ({
    ...f,
    cards: scheduleRows.map(r => ({ day: r.day, dayEn: r.dayEn, time: `${r.start} — ${r.end}`, type: r.type, level: r.level })),
  }));

  const logout = () => {
    const t = localStorage.getItem("joh_admin_token");
    localStorage.removeItem("joh_admin_token");
    // Was de membersessie hetzelfde token? Dan ook daar uitloggen.
    if (t && localStorage.getItem("joh_auth_token") === t) localStorage.removeItem("joh_auth_token");
    nav("/admin/login");
  };

  // ----- Site content (CMS) -----
  const saveContent = async (key, value) => {
    setContentBusy(true);
    try {
      await api.put(`/admin/site/content/${key}`, value);
      toast.success("Opgeslagen — direct zichtbaar op de website");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
    setContentBusy(false);
  };

  // ----- Media (afbeeldingen & video) -----
  const saveImageOverrides = async (next) => {
    setMediaForm(next);
    try {
      await api.put("/admin/site/content/image_overrides", next);
      toast.success("Media opgeslagen — direct zichtbaar op de website");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
  };

  const mediaUpload = async (slotKey, file) => {
    setMediaBusy(slotKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd);
      await saveImageOverrides({ ...mediaForm, [slotKey]: data.url });
      const mf = await api.get("/admin/media");
      setMediaFiles(mf.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload mislukt");
    }
    setMediaBusy("");
  };

  const mediaSetUrl = (slotKey, url) => {
    const u = (url || "").trim();
    if (!u) return;
    if (!/^(https:\/\/|\/)/.test(u)) { toast.error("Link moet met https:// beginnen"); return; }
    saveImageOverrides({ ...mediaForm, [slotKey]: u });
  };

  const mediaReset = (slotKey) => {
    const next = { ...mediaForm };
    delete next[slotKey];
    saveImageOverrides(next);
  };

  const mediaDeleteFile = async (fileId) => {
    try {
      await api.delete(`/admin/media/${fileId}`);
      setMediaFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success("Bestand verwijderd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // ----- Wachtwoord-beheer -----
  const changeOwnPassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/admin/password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Wachtwoord gewijzigd");
      setPwForm({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Wijzigen mislukt");
    }
    setPwBusy(false);
  };

  const setAdminPassword = async (email) => {
    setPwBusy(true);
    try {
      await api.post("/admin/team/password", { email, new_password: pwResetValue });
      toast.success(`Wachtwoord gezet voor ${email}`);
      setPwResetFor(""); setPwResetValue("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Wijzigen mislukt");
    }
    setPwBusy(false);
  };

  // ----- Proefles beheer -----
  const saveTrialDay = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/submissions/${selected.id}`, { status: selected.status || "new", preferred_day: trialDay });
      setSelected(data.submission);
      setItems(prev => prev.map(it => it.id === selected.id ? data.submission : it));
      toast.success("Proefles-dag opgeslagen");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
  };

  const deleteTrial = async () => {
    if (!selected) return;
    if (!window.confirm(`Proefles van ${selected.data?.name || selected.data?.email} verwijderen?\n\nDe aanmelding wordt naar het archief verplaatst en daar nog 31 dagen bewaard. Daarna wordt hij automatisch definitief verwijderd. Herstellen kan vanuit het archief.`)) return;
    try {
      await api.delete(`/admin/submissions/${selected.id}`);
      setItems(prev => prev.filter(it => it.id !== selected.id));
      setSelected(null);
      toast.success("Naar archief verplaatst — 31 dagen herstelbaar");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // ----- Weekplanning: extra items -----
  const saveWeekItem = async (e) => {
    e.preventDefault();
    setWeekBusy(true);
    try {
      if (weekEditId) {
        await api.put(`/admin/week-items/${weekEditId}`, weekForm);
        toast.success("Item bijgewerkt");
      } else {
        await api.post("/admin/week-items", weekForm);
        toast.success("Item toegevoegd aan de weekplanning");
      }
      setWeekForm({ title: "", date: "", time: "", kind: "event", description: "" });
      setWeekEditId("");
      const w = await api.get("/admin/week-items");
      setWeekItems(w.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
    setWeekBusy(false);
  };

  const editWeekItem = (it) => {
    setWeekEditId(it.id);
    setWeekForm({ title: it.title, date: it.date, time: it.time || "", kind: it.kind || "event", description: it.description || "" });
  };

  const deleteWeekItem = async (id) => {
    try {
      await api.delete(`/admin/week-items/${id}`);
      setWeekItems(prev => prev.filter(x => x.id !== id));
      toast.success("Item verwijderd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  const generateMagicLink = async () => {
    setMagicLoading(true);
    try {
      const { data } = await api.post(`/auth/magic-link?days=${magicDays}`);
      const url = `${window.location.origin}/admin?token=${data.token}`;
      setMagicLink(url);
      toast.success(`Bookmarkable URL gegenereerd (${data.expires_in_days} dagen geldig)`);
    } catch (e) {
      toast.error("Magic-link genereren mislukt");
    }
    setMagicLoading(false);
  };

  const copyMagicLink = async () => {
    if (!magicLink) return;
    try {
      await navigator.clipboard.writeText(magicLink);
      toast.success("Gekopieerd naar klembord");
    } catch { toast.error("Kopiëren mislukt — selecteer handmatig"); }
  };

  const typeLabel = { trial: "Proefles", pt: "Personal Training", contact: "Contact" };
  const totalAttendees = classes.reduce((sum, c) => sum + (c.going || 0), 0);

  // ----- Promo codes (Acties tab) -----
  const PROMO_PACKAGES = [
    ["all", "Alle pakketten"],
    ["joh_membership_1x", "Membership 1× p/w"],
    ["joh_membership_2x", "Membership 2× p/w"],
    ["joh_membership_3x", "Membership 3× p/w"],
    ["joh_pack_5", "Strippenkaart 5"],
    ["joh_pack_10", "Strippenkaart 10"],
    ["joh_dropin", "Drop-in les"],
  ];
  const pkgLabel = (keys) => (keys || ["all"]).map(k => PROMO_PACKAGES.find(p => p[0] === k)?.[1] || k).join(", ");
  const promoKindLabel = (p) =>
    p.kind === "percent" ? `${p.percent}% korting`
    : p.kind === "fixed" ? `€${((p.amount_cents || 0) / 100).toFixed(0)} korting`
    : "Gratis 1 sessie";
  const promoWindowLabel = (p) =>
    p.starts_at || p.ends_at
      ? `${p.starts_at ? `vanaf ${p.starts_at}` : ""}${p.starts_at && p.ends_at ? " · " : ""}${p.ends_at ? `t/m ${p.ends_at}` : ""}`
      : "altijd geldig";

  const createPromo = async (e) => {
    e.preventDefault();
    setPromoBusy(true);
    try {
      await api.post("/admin/promos", {
        code: promoForm.code,
        kind: promoForm.kind,
        percent: promoForm.kind === "percent" ? Number(promoForm.percent) : null,
        amount_cents: promoForm.kind === "fixed" ? Math.round(parseFloat(String(promoForm.amount_eur).replace(",", ".")) * 100) : null,
        packages: promoForm.packages.length ? promoForm.packages : ["all"],
        starts_at: promoForm.starts_at || null,
        ends_at: promoForm.ends_at || null,
        max_uses: promoForm.max_uses === "" ? null : Number(promoForm.max_uses),
        active: promoForm.active,
      });
      toast.success(`Promocode ${promoForm.code.toUpperCase()} aangemaakt`);
      setPromoForm({ code: "", kind: "percent", percent: 10, amount_eur: "5", packages: ["all"], starts_at: "", ends_at: "", max_uses: "", active: true });
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Aanmaken mislukt");
    }
    setPromoBusy(false);
  };

  const togglePromo = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, {
        code: p.code, kind: p.kind, percent: p.percent, amount_cents: p.amount_cents ?? null,
        packages: p.packages || ["all"], starts_at: p.starts_at ?? null, ends_at: p.ends_at ?? null,
        max_uses: p.max_uses ?? null, active: !p.active,
      });
      fetchData();
    } catch { toast.error("Bijwerken mislukt"); }
  };

  const deletePromo = async (p) => {
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success(`${p.code} verwijderd`);
      fetchData();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const togglePromoPkg = (k) => setPromoForm(f => {
    if (k === "all") return { ...f, packages: ["all"] };
    const cur = f.packages.filter(x => x !== "all");
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    return { ...f, packages: next.length ? next : ["all"] };
  });

  const paidRevenue = payments.filter(p => p.payment_status === "paid" && !p.is_test).reduce((s, p) => s + (p.amount || 0), 0);

  // Payment aging: how many days since payment was created without being paid
  const paymentAge = (p) => {
    if (p.payment_status === "paid") return { days: 0, tone: "paid" };
    const days = Math.floor((Date.now() - new Date(p.created_at).getTime()) / (1000 * 60 * 60 * 24));
    if (days >= 7) return { days, tone: "overdue" };
    if (days >= 5) return { days, tone: "warning" };
    return { days, tone: "open" };
  };

  const filteredPayments = useMemo(() => {
    let list = payments;
    if (paymentFilter !== "all") {
      list = list.filter(p => {
        const t = paymentAge(p).tone;
        if (paymentFilter === "paid") return p.payment_status === "paid";
        if (paymentFilter === "open") return p.payment_status !== "paid" && t !== "overdue";
        if (paymentFilter === "overdue") return t === "overdue";
        if (paymentFilter === "failed") return ["failed", "overdue"].includes(p.payment_status || p.status);
        if (paymentFilter === "test") return !!p.is_test;
        return true;
      });
    }
    return sortRows(list, paymentSort, {
      first: (p) => p.name || p.email || "",
      date: (p) => p.created_at || "",
      amount: (p) => p.amount || 0,
    });
  }, [payments, paymentFilter, paymentSort]);

  // Forms search + monthly grouping
  const filteredItems = useMemo(() => {
    if (!formsSearch.trim()) return items;
    const q = formsSearch.trim().toLowerCase();
    return items.filter(x =>
      (x.data?.name || "").toLowerCase().includes(q) ||
      (x.data?.email || "").toLowerCase().includes(q) ||
      (x.data?.phone || "").toLowerCase().includes(q) ||
      (x.data?.subject || "").toLowerCase().includes(q) ||
      (x.data?.message || "").toLowerCase().includes(q)
    );
  }, [items, formsSearch]);

  const groupedByMonth = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach(item => {
      const d = new Date(item.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("nl-NL", { month: "long", year: "numeric" });
      if (!groups.has(key)) groups.set(key, { key, label, rows: [] });
      groups.get(key).rows.push(item);
    });
    return Array.from(groups.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [filteredItems]);

  const toggleMonth = (key) => {
    setOpenMonths(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // Auto-open the most recent month
  useEffect(() => {
    if (groupedByMonth.length > 0 && openMonths.size === 0) {
      setOpenMonths(new Set([groupedByMonth[0].key]));
    }
  }, [groupedByMonth]); // eslint-disable-line

  const inviteAdmin = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const { data } = await api.post("/admin/team", inviteForm);
      const url = data.magic_token
        ? `${window.location.origin}/admin?token=${data.magic_token}`
        : null;
      setInviteResult({ ...data, magic_url: url });
      setInviteForm({ email: "", name: "", password: "", role: "admin" });
      const t = await api.get("/admin/team");
      setTeam(t.data.items || []);
      toast.success(data.status === "promoted" ? "Gepromoveerd" : "Toegevoegd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Uitnodigen mislukt");
    }
    setInviteLoading(false);
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`Verwijder admin ${email}?`)) return;
    try {
      await api.delete(`/admin/team?email=${encodeURIComponent(email)}`);
      const t = await api.get("/admin/team");
      setTeam(t.data.items || []);
      toast.success("Admin verwijderd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  const changeTeamRole = async (email, role) => {
    if (!window.confirm(`${email} ${role === "owner" ? "owner" : "gewone admin"} maken?`)) return;
    try {
      await api.patch("/admin/team/role", { email, role });
      const t = await api.get("/admin/team");
      setTeam(t.data.items || []);
      toast.success(role === "owner" ? "Owner gemaakt" : "Teruggezet naar admin");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Rol wijzigen mislukt");
    }
  };

  const promoteWaitlist = async (instance_id, email) => {
    try {
      const params = email ? `?instance_id=${encodeURIComponent(instance_id)}&email=${encodeURIComponent(email)}` : `?instance_id=${encodeURIComponent(instance_id)}`;
      await api.post(`/admin/classes/waitlist/promote${params}`);
      const c = await api.get("/admin/classes");
      setClasses(c.data.items || []);
      const updated = (c.data.items || []).find(x => x.id === instance_id);
      if (updated) setSelectedClass(updated);
      toast.success("Van reservelijst gepromoveerd — bevestigingsmail verstuurd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Promoveren mislukt");
    }
  };

  const removeWaitlist = async (instance_id, email) => {
    if (!window.confirm(`Verwijder ${email} van de reservelijst?`)) return;
    try {
      await api.delete(`/admin/classes/waitlist?instance_id=${encodeURIComponent(instance_id)}&email=${encodeURIComponent(email)}`);
      const c = await api.get("/admin/classes");
      setClasses(c.data.items || []);
      const updated = (c.data.items || []).find(x => x.id === instance_id);
      if (updated) setSelectedClass(updated);
      toast.success("Verwijderd van reservelijst");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  const updateSubmissionStatus = async (id, status, instanceId) => {
    try {
      const payload = { status };
      if (instanceId) payload.instance_id = instanceId;
      const { data } = await api.patch(`/admin/submissions/${id}`, payload);
      setItems(prev => prev.map(x => x.id === id ? (data.submission || { ...x, status }) : x));
      if (selected?.id === id) setSelected(data.submission || { ...selected, status });
      const suffix = data?.email_sent ? " · mail verzonden" : "";
      if (data?.linked) {
        toast.success(`Goedgekeurd · gekoppeld aan ${data.linked.class_type} — ${data.linked.date} ${data.linked.start}`);
      } else {
        toast.success(`Status: ${STATUS_META[status]?.label || status}${suffix}`);
      }
      // dashboard-tellers (openstaande goedkeuringen) direct bijwerken
      api.get("/admin/dashboard").then(({ data: dd }) => setDash(dd)).catch(() => {});
      return data;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Status wijzigen mislukt");
    }
  };

  // Navigeer vanuit een aanmelding naar de gekoppelde les (Klassen-tab + modal)
  const openLinkedClass = async (instanceId) => {
    try {
      const { data } = await api.get("/admin/classes");
      const items = data.items || [];
      setClasses(items);
      const inst = items.find(c => c.id === instanceId);
      setSelected(null);
      setTab("classes");
      if (inst) setSelectedClass(inst);
      else toast.error("Les niet gevonden in het komende rooster");
    } catch {
      toast.error("Kon het lesprogramma niet laden");
    }
  };

  // Open een aanmelding vanuit het lesprogramma of de activiteitenfeed
  const openSubmissionById = async (subId) => {
    try {
      const { data } = await api.get("/admin/submissions");
      const sub = (data.items || []).find(x => x.id === subId);
      if (sub) {
        setSelectedClass(null);
        setSelected(sub);
      } else {
        toast.error("Aanmelding niet gevonden");
      }
    } catch {
      toast.error("Kon aanmelding niet laden");
    }
  };

  // Klik op een activiteit → ga naar het juiste onderdeel (fallback: klantprofiel)
  const openActivity = (e) => {
    if ((e.kind === "submission" || e.kind === "approval") && e.sub_id) openSubmissionById(e.sub_id);
    else if (e.kind === "rsvp" && e.instance_id) openLinkedClass(e.instance_id);
    else if (e.kind === "payment") setTab("payments");
    else if (e.kind === "cancellation" && e.instance_id) openLinkedClass(e.instance_id);
    else if (e.email) openMemberByEmail(e.email);
  };

  // Open het klantprofiel vanuit het lesprogramma (Leden-tab met zoekfilter)
  // Interne placeholder-adressen (blanco/handmatige deelnemers) niet als e-mail tonen
  const realEmail = (e) => (e && !String(e).endsWith("@joh.local") ? e : "");
  const openMemberByEmail = (email) => {
    setSelectedClass(null);
    setMemberQuery(email || "");
    setTab("members");
  };

  // Handmatig (blanco) een deelnemer aan de open les toevoegen
  const [attForm, setAttForm] = useState({ name: "", email: "", phone: "" });
  const [attBusy, setAttBusy] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  useEffect(() => { setAttForm({ name: "", email: "", phone: "" }); setAttOpen(false); }, [selectedClass?.id]);

  const addManualAttendee = async (e) => {
    e.preventDefault();
    if (!selectedClass) return;
    setAttBusy(true);
    try {
      const { data } = await api.post(`/admin/classes/${selectedClass.id}/attendees`, {
        name: attForm.name.trim(),
        email: attForm.email.trim() || undefined,
        phone: attForm.phone.trim() || undefined,
      });
      setSelectedClass(c => ({
        ...c,
        attendees: [...(c.attendees || []), data.attendee],
        going: (c.going || 0) + 1,
        member_count: (c.member_count || 0) + 1,
      }));
      setAttForm({ name: "", email: "", phone: "" });
      setAttOpen(false);
      toast.success(`${data.attendee.name} toegevoegd aan de les`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Toevoegen mislukt");
    }
    setAttBusy(false);
  };

  // Handmatige deelnemer weer van de les verwijderen
  const removeManualAttendee = async (a) => {
    if (!selectedClass) return;
    try {
      await api.delete(`/admin/attendance/${a.id}`);
      setSelectedClass(c => ({
        ...c,
        attendees: (c.attendees || []).filter(x => x.id !== a.id),
        going: Math.max(0, (c.going || 0) - 1),
        member_count: Math.max(0, (c.member_count || 0) - 1),
      }));
      toast.success(`${a.name} verwijderd van de les`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // Aanmeldingsteller op de website per les aan/uit (admin ziet altijd alles)
  const toggleCountVisibility = async (c) => {
    const nv = !c.show_count;
    try {
      await api.put(`/admin/classes/${c.id}/visibility`, { show_count: nv });
      setClasses((prev) => prev.map((x) => (x.id === c.id ? { ...x, show_count: nv } : x)));
      toast.success(nv ? `Teller zichtbaar op de website (${c.type} ${fmtDate(c.date).day} ${fmtDate(c.date).month})` : "Teller verborgen op de website");
    } catch {
      toast.error("Wijzigen mislukt");
    }
  };

  // Aanwezigheid: deelnemer markeren als aanwezig / no-show / ongemarkeerd
  const markPresence = async (attendanceId, attended) => {
    try {
      await api.patch(`/admin/attendance/${attendanceId}/presence`, { attended });
      setSelectedClass(c => {
        if (!c) return c;
        const attendees = (c.attendees || []).map(a => a.id === attendanceId ? { ...a, attended } : a);
        return {
          ...c,
          attendees,
          present_count: attendees.filter(a => a.attended === true).length,
          absent_count: attendees.filter(a => a.attended === false).length,
        };
      });
    } catch {
      toast.error("Aanwezigheid markeren mislukt");
    }
  };

  // Coach-deellink: maak/kopieer een publieke alleen-lezen link naar de deelnemerslijst
  const [shareLink, setShareLink] = useState("");
  const shareClass = async (instanceId) => {
    try {
      const { data } = await api.post(`/admin/classes/${instanceId}/share`);
      const url = `${window.location.origin}${data.url}`;
      setShareLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Deellink gekopieerd — stuur 'm naar de coach");
      } catch {
        toast.success("Deellink aangemaakt");
      }
    } catch {
      toast.error("Deellink maken mislukt");
    }
  };
  useEffect(() => { setShareLink(""); }, [selectedClass?.id]);

  // Klant volledig verwijderen (account + aanmeldingen + lesdeelnames; betalingen blijven)
  const deleteMember = async (m) => {
    const naam = m.name || m.email;
    if (!window.confirm(`${naam} verwijderen?\n\nDe klant verdwijnt direct uit alle overzichten (account, aanmeldingen, lesdeelnames, strippen) maar wordt NIET direct definitief verwijderd: alles blijft 31 dagen herstelbaar in het archief. Daarna wordt het automatisch definitief verwijderd. Betalingshistorie blijft altijd bewaard.`)) return;
    try {
      await api.delete(`/admin/members/by-email/${encodeURIComponent(m.email)}`);
      toast.success(`${naam} naar archief verplaatst — 31 dagen herstelbaar`);
      setSelectedMember(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // snelle actie vanuit tabellen (zonder modal te openen) — verwijderen = naar archief (31 dagen)
  const deleteTrialDirect = async (item) => {
    if (!window.confirm(`${item.data?.name || item.data?.email} verwijderen?\n\nHet item wordt naar het archief verplaatst en daar nog 31 dagen bewaard. Daarna wordt het automatisch definitief verwijderd. Herstellen kan altijd vanuit het archief.`)) return;
    try {
      await api.delete(`/admin/submissions/${item.id}`);
      setItems(prev => prev.filter(x => x.id !== item.id));
      toast.success("Naar archief verplaatst — 31 dagen herstelbaar");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  const [expandedApproval, setExpandedApproval] = useState(null);
  const approvalGroups = useMemo(() => {
    const grouped = { new: [], planned: [], handled: [], declined: [] };
    items.forEach(x => {
      const st = x.status || "new";
      if (grouped[st]) grouped[st].push(x);
      else grouped.new.push(x);
    });
    return grouped;
  }, [items]);

  const isActiveMember = (m) => !!m.has_membership || (m.pack_credits || 0) > 0;
  const memberCounts = useMemo(() => ({
    active: members.filter(isActiveMember).length,
    inactive: members.filter((m) => !isActiveMember(m)).length,
  }), [members]); // eslint-disable-line

  const filteredMembers = useMemo(() => {
    let list = members;
    if (memberGroup === "active") list = list.filter(isActiveMember);
    if (memberGroup === "inactive") list = list.filter((m) => !isActiveMember(m));
    if (memberStatusFilter) list = list.filter(m => (m.status_label || "") === memberStatusFilter);
    if (memberQuery.trim()) {
      const q = memberQuery.trim().toLowerCase();
      list = list.filter(m =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.phone || "").toLowerCase().includes(q)
      );
    }
    return sortRows(list, memberSort, {
      first: (m) => firstOf(m),
      last: (m) => lastOf(m),
      date: (m) => m.start_date || m.created_at || "",
    });
  }, [members, memberQuery, memberStatusFilter, memberGroup, memberSort]); // eslint-disable-line

  // Archief: herstellen / definitief verwijderen
  const archiveRestore = async (item) => {
    try {
      await api.post("/admin/archive/restore", { kind: item.kind, id: item.id });
      toast.success(`${item.title} hersteld naar de oorspronkelijke plek`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Herstellen mislukt");
    }
  };

  const archivePurge = async (item) => {
    if (!window.confirm(`${item.title} DEFINITIEF verwijderen?\n\nWAARSCHUWING: dit kan niet ongedaan worden. Het item wordt direct en permanent uit het systeem verwijderd.`)) return;
    try {
      await api.delete(`/admin/archive/${item.kind}/${encodeURIComponent(item.id)}`);
      toast.success("Definitief verwijderd");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // Betalingen: openstaande betaling naar archief (31 dagen bewaard)
  const archivePayment = async (p) => {
    const label = p.label || p.lookup_key || "deze betaling";
    if (!window.confirm(`"${label}" van ${p.email || "onbekend"} (€${((p.amount || 0) / 100).toLocaleString("nl-NL")}) verwijderen?\n\nDe betaling wordt NIET direct definitief verwijderd, maar eerst naar het archief verplaatst. Daar wordt hij nog 31 dagen bewaard — daarna wordt hij automatisch definitief verwijderd. Herstellen kan vanuit Archief → Betalingen.`)) return;
    try {
      const pid = p.session_id || p.id;
      await api.delete(`/admin/payments/${pid}`);
      setPayments(prev => prev.filter(x => (x.session_id || x.id) !== pid));
      toast.success("Naar archief verplaatst — wordt na 31 dagen definitief verwijderd");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  // Membership goedkeuren / afwijzen (spec 7-9: betaling + approval = actief; afwijzen = auto-terugbetaling)
  const membershipApprove = async (p) => {
    try {
      await api.post(`/admin/payments/${p.session_id}/membership-approve`);
      toast.success(`Membership goedgekeurd — ${p.email} is nu actief`);
      const { data } = await api.get("/admin/payments");
      setPayments(data.payments || []);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Goedkeuren mislukt"); }
  };

  const membershipReject = async (p) => {
    const paid = p.payment_status === "paid";
    const who = (p.name && p.name !== "null" ? p.name : null) || p.email || "deze aanmelding";
    if (!window.confirm(`Membership van ${who} afwijzen?${paid ? "\n\nDe betaling wordt automatisch terugbetaald via Stripe en de klant krijgt een mail." : ""}\n\nDe aanmelding blijft bewaard met status Afgewezen.`)) return;
    try {
      const { data } = await api.post(`/admin/payments/${p.session_id}/membership-reject`);
      toast.success(data.refunded ? "Afgewezen — betaling terugbetaald en klant gemaild" : "Afgewezen — klant gemaild");
      const res = await api.get("/admin/payments");
      setPayments(res.data.payments || []);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Afwijzen mislukt"); }
  };

  // Handmatig klant toevoegen
  const submitMemberForm = async (e) => {
    e.preventDefault();
    setMemberFormBusy(true);
    try {
      await api.post("/admin/members", memberForm);
      // optioneel: direct koppelen aan een les
      if (memberForm.class_id) {
        try {
          await api.post(`/admin/classes/${memberForm.class_id}/attendees`, {
            name: `${memberForm.first_name} ${memberForm.last_name}`.trim(),
            email: memberForm.email || undefined,
            phone: memberForm.phone || undefined,
          });
          const gekoppeld = classes.find(c => c.id === memberForm.class_id);
          toast.success(`Klant toegevoegd én gekoppeld aan ${gekoppeld?.type || "de les"} (${gekoppeld?.date || ""} ${gekoppeld?.start || ""})`);
        } catch (linkErr) {
          toast.success(`Klant ${memberForm.first_name} toegevoegd`);
          toast.error(formatApiError(linkErr?.response?.data?.detail) || "Koppelen aan les mislukt — klant is wel aangemaakt");
        }
      } else {
        toast.success(`Klant ${memberForm.first_name} ${memberForm.last_name} toegevoegd`);
      }
      setMemberFormOpen(false);
      setMemberForm({ first_name: "", last_name: "", email: "", phone: "", membership: "", start_date: "", status: "actief", notes: "", class_id: "" });
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
    }
    setMemberFormBusy(false);
  };

  // Stripe testbetaling
  const runTestPayment = async () => {
    setTestPayBusy(true);
    setTestPayResult(null);
    try {
      const { data } = await api.post("/admin/stripe/test-payment");
      setTestPayResult(data);
      if (data.success) toast.success("Testbetaling geslaagd");
      else toast.error("Testbetaling mislukt");
      fetchData();
    } catch (err) {
      setTestPayResult({ success: false, error: formatApiError(err?.response?.data?.detail) || "Verzoek mislukt", created: new Date().toISOString() });
      toast.error("Testbetaling mislukt");
    }
    setTestPayBusy(false);
  };

  const exportCSV = (filename, rows) => {
    if (!rows.length) return toast.error("Niets om te exporteren");
    const headers = Object.keys(rows[0]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} rijen geëxporteerd`);
  };

  const exportMembers = () => exportCSV(
    `joh-leden-${new Date().toISOString().slice(0,10)}.csv`,
    filteredMembers.map(m => ({
      naam: m.name || "",
      email: m.email,
      telefoon: m.phone || "",
      status: m.has_membership ? "Member" : m.pack_credits > 0 ? `Pack ${m.pack_credits}` : "Gast",
      pack_credits: m.pack_credits,
      rsvp_count: m.rsvp_count,
      bronnen: (m.sources || []).join(" | "),
      eerst_gezien: m.first_seen,
      laatst_gezien: m.last_seen,
    })),
  );

  const exportClasses = () => {
    const rows = [];
    classes.forEach(c => {
      (c.attendees || []).forEach(a => {
        rows.push({
          les_datum: c.date, les_start: c.start, les_eind: c.end,
          les_type: c.type, niveau: c.level,
          naam: a.name, email: a.email, telefoon: a.phone || "",
          tegoed: a.credit_type,
          aangemeld_op: a.created_at,
        });
      });
    });
    exportCSV(`joh-klassen-${new Date().toISOString().slice(0,10)}.csv`, rows);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark" data-testid="admin-dashboard">
      <header className="line-b line-dark px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-40">
        <div className="flex items-center gap-4">
          <button onClick={() => setNavOpen(true)} className="md:hidden chip text-[#F5F3F0] border border-[#F5F3F0]/25 px-3 py-2" data-testid="admin-menu-open">Menu</button>
          <Link to="/" className="font-logo text-2xl accent-hover">JOH.</Link>
          <span className="chip text-[#A3A3A3] hidden sm:inline">Admin · {TABS.find(x => x.key === tab)?.label}</span>
          <a href={ENV_INFO.other} target="_blank" rel="noreferrer" title={ENV_INFO.title}
            className={`chip px-2 py-1 border inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity ${ENV_INFO.cls}`}
            data-testid="admin-env-indicator">
            <span className="w-1.5 h-1.5 rounded-full bg-current inline-block"></span>{ENV_INFO.label}
          </a>
        </div>
        <button onClick={logout} title="Uitloggen" aria-label="Uitloggen" className="w-11 h-11 border border-[#F5F3F0]/45 flex items-center justify-center text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="admin-logout-btn">
          <LogOut size={16}/>
        </button>
      </header>

      <div className="flex">
        {/* Vaste sidebar (desktop) */}
        <aside className="hidden md:block w-52 shrink-0 border-r border-[#F5F3F0]/10 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto py-6 px-4" data-testid="admin-sidebar">
          {NAV_GROUPS.map((g) => (
            <div key={g.label} className="mb-7">
              <div className="chip text-[#4A4A4A] px-3 mb-2">{g.label}</div>
              {g.keys.map((k) => {
                const tt = TABS.find(x => x.key === k);
                return (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${tab === k ? "text-[#F5F3F0] bg-[#F5F3F0]/10 border-l-2 border-[#D4A93A]" : "text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent"}`}
                    data-testid={`admin-tab-${k}`}
                  >
                    {tt.label}
                  </button>
                );
              })}
            </div>
          ))}
          <div className="mb-7">
            <div className="chip text-[#4A4A4A] px-3 mb-2">System</div>
            <a
              href="https://johboxing.com/jace"
              target="_blank"
              rel="noreferrer"
              className="w-full text-left px-3 py-2 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent transition-colors inline-flex items-center gap-2"
              data-testid="admin-link-jace"
            >
              JACE <ExternalLink size={12}/>
            </a>
            <a
              href="/project11/admin"
              className="w-full text-left px-3 py-2 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent transition-colors inline-flex items-center gap-2"
              data-testid="admin-link-project11"
            >
              PROJECT 11*
            </a>
            <a
              href="/admin/bedrijven"
              className="w-full text-left px-3 py-2 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent transition-colors inline-flex items-center gap-2"
              data-testid="admin-link-bedrijven"
            >
              BEDRIJVEN
            </a>
          </div>
        </aside>

        {/* Mobiele drawer */}
        {navOpen && (
          <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-nav-drawer">
            <div className="absolute inset-0 bg-black/70" onClick={() => setNavOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-[#F5F3F0]/15 py-6 px-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-6 px-3">
                <span className="font-logo text-xl">JOH. Admin</span>
                <button onClick={() => setNavOpen(false)} className="chip text-[#A3A3A3]" data-testid="admin-menu-close">Sluit</button>
              </div>
              {NAV_GROUPS.map((g) => (
                <div key={g.label} className="mb-7">
                  <div className="chip text-[#4A4A4A] px-3 mb-2">{g.label}</div>
                  {g.keys.map((k) => {
                    const tt = TABS.find(x => x.key === k);
                    return (
                      <button
                        key={k}
                        onClick={() => { setTab(k); setNavOpen(false); }}
                        className={`w-full text-left px-3 py-2.5 text-sm ${tab === k ? "text-[#F5F3F0] bg-[#F5F3F0]/10 border-l-2 border-[#D4A93A]" : "text-[#A3A3A3] border-l-2 border-transparent"}`}
                        data-testid={`admin-drawer-tab-${k}`}
                      >
                        {tt.label}
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className="mb-7">
                <div className="chip text-[#4A4A4A] px-3 mb-2">System</div>
                <a
                  href="https://johboxing.com/jace"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setNavOpen(false)}
                  className="w-full text-left px-3 py-2.5 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent inline-flex items-center gap-2"
                  data-testid="admin-drawer-link-jace"
                >
                  JACE <ExternalLink size={12}/>
                </a>
                <a
                  href="/project11/admin"
                  onClick={() => setNavOpen(false)}
                  className="w-full text-left px-3 py-2.5 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent inline-flex items-center gap-2"
                  data-testid="admin-drawer-link-project11"
                >
                  PROJECT 11*
                </a>
                <a
                  href="/admin/bedrijven"
                  onClick={() => setNavOpen(false)}
                  className="w-full text-left px-3 py-2.5 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent inline-flex items-center gap-2"
                  data-testid="admin-drawer-link-bedrijven"
                >
                  BEDRIJVEN
                </a>
              </div>
              <button
                onClick={logout}
                className="w-full text-left px-3 py-2.5 text-sm text-[#A3A3A3] hover:text-[#F5F3F0] border-l-2 border-transparent inline-flex items-center gap-2 mt-4"
                data-testid="admin-drawer-logout"
              >
                <LogOut size={14}/> Uitloggen
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 px-6 md:px-10 py-8">
        {loading ? (
          <div className="chip text-[#A3A3A3]">Laden…</div>
        ) : loadError ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center" data-testid="admin-load-error">
            <div className="chip text-[#A3A3A3] mb-4">Gegevens konden niet geladen worden.</div>
            <button onClick={fetchData} className="btn-light" data-testid="admin-retry-btn">Opnieuw proberen</button>
          </div>
        ) : tab === "home" ? (
          <div data-testid="admin-home-dashboard">
            <div className="section-tag section-tag-dark mb-4">— Dashboard</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl mb-8">Overzicht.</h2>

            {/* Statistiekkaarten — klikbaar naar het bijbehorende overzicht */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="admin-dash-stats">
              {[
                { label: "Actieve leden", value: dash?.stats?.active_members ?? "—", go: "members", hint: "Actief", testid: "dash-active-members" },
                { label: "Nieuwe aanmeldingen", value: dash?.stats?.new_submissions ?? "—", go: "aanmeldingen", hint: "Nieuw", testid: "dash-new-submissions", alert: (dash?.stats?.new_submissions || 0) > 0 },
                { label: "Proefles-aanmeldingen", value: dash?.stats?.trials_new ?? "—", go: "trials", hint: "Actie nodig", testid: "dash-new-trials", alert: (dash?.stats?.trials_new || 0) > 0 },
                { label: "Openstaande betalingen", value: dash?.stats?.payments_open ?? "—", go: "payments", hint: "Openstaand", testid: "dash-open-payments", alert: (dash?.stats?.payments_open || 0) > 0 },
                { label: "Mislukte betalingen", value: dash?.stats?.payments_failed ?? "—", go: "payments", hint: "Actie nodig", testid: "dash-failed-payments", alert: (dash?.stats?.payments_failed || 0) > 0 },
                { label: "Nieuwe contactaanvragen", value: dash?.stats?.contact_new ?? "—", go: "aanmeldingen", hint: "Nieuw", testid: "dash-new-contact", alert: (dash?.stats?.contact_new || 0) > 0 },
              ].map((c) => (
                <button key={c.testid} onClick={() => setTab(c.go)} className="text-left border border-[#F5F3F0]/15 p-4 hover:bg-white/[0.04] transition-colors" data-testid={c.testid}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="chip text-[#A3A3A3]">{c.label}</span>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.alert ? "" : "opacity-20"}`} style={{ background: c.alert ? "var(--joh-accent)" : "#A3A3A3" }} />
                  </div>
                  <div className="font-display text-4xl md:text-5xl mt-2">{c.value}</div>
                  <div className="chip mt-2" style={{ color: c.alert ? "var(--joh-accent)" : "#4A4A4A" }}>{c.hint}</div>
                </button>
              ))}
            </div>

            {/* ACTIE NODIG */}
            <div className="mt-10" data-testid="admin-dash-actions">
              <div className="chip text-[#A3A3A3] mb-3">— Actie nodig</div>
              {!dash || dash.action_items.length === 0 ? (
                <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]" data-testid="admin-dash-actions-empty">Alles bij — geen openstaande acties.</div>
              ) : (
                <div className="line-t line-dark">
                  {dash.action_items.map((a, i) => (
                    <button key={i} onClick={() => { setTab(a.tab); if (a.subtab) setAanmeldingenFilter(a.subtab); }} className="w-full line-b line-dark py-3.5 flex items-center gap-4 text-left hover:bg-white/[0.03] transition-colors group" data-testid={`admin-dash-action-${i}`}>
                      <span className="w-2 h-2 shrink-0" style={{ background: "var(--joh-accent)" }} />
                      <span className="font-display text-lg md:text-xl uppercase flex-1">{a.text}</span>
                      <ChevronRight size={16} className="text-[#4A4A4A] group-hover:text-[#F5F3F0]" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* VERJAARDAGEN — komende 30 dagen */}
            {birthdays && birthdays.length > 0 && (
              <div className="mt-10" data-testid="admin-dash-birthdays">
                <div className="chip text-[#A3A3A3] mb-3">— Verjaardagen (30 dagen)</div>
                <div className="line-t line-dark">
                  {birthdays.map((b) => (
                    <div key={b.email} className="line-b line-dark py-3 flex flex-wrap items-center gap-3" data-testid={`admin-birthday-${b.email}`}>
                      <span className="font-display uppercase text-lg flex-1 min-w-40 truncate">{b.name || b.email}</span>
                      <span className="chip text-[#DDD6CB]">{new Date(b.date + "T00:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} · wordt {b.turning}</span>
                      <span className={`chip ${b.in_days === 0 ? "accent-ink" : "text-[#4A4A4A]"}`}>{b.in_days === 0 ? "VANDAAG" : `over ${b.in_days}d`}</span>
                      <button onClick={() => setEmailTo({ email: b.email, name: b.name })}
                        className="chip px-3 py-1.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-[#F5F3F0] hover:border-[#F5F3F0] transition-colors inline-flex items-center gap-1.5"
                        data-testid={`admin-birthday-mail-${b.email}`}>
                        <Send size={11} /> Feliciteer
                      </button>
                      <a href={`https://wa.me/${(b.phone || "").replace(/[^0-9]/g, "").replace(/^0/, "31")}?text=${encodeURIComponent(`Gefeliciteerd met je verjaardag${b.name ? `, ${b.name.split(" ")[0]}` : ""}! Namens heel JOH Boxing — tot snel in de ring.`)}`}
                        target="_blank" rel="noreferrer"
                        className="chip px-3 py-1.5 border border-[#4ADE80]/40 text-[#4ADE80] hover:bg-[#4ADE80]/10 transition-colors inline-flex items-center gap-1.5"
                        title={b.phone ? `WhatsApp naar ${b.phone}` : "WhatsApp (geen nummer — kies contact in WhatsApp)"}
                        data-testid={`admin-birthday-wa-${b.email}`}>
                        <MessageCircle size={11} /> WhatsApp
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RECENTE ACTIVITEIT — gegroepeerd per persoon (nieuwste actie zichtbaar, rest uitklapbaar) */}
            <div className="mt-10" data-testid="admin-dash-activity">
              <div className="flex items-center justify-between mb-3">
                <div className="chip text-[#A3A3A3]">— Recente activiteit</div>
                {activity.length > 0 && (
                  <button onClick={() => setShowAllActivity(v => !v)} className="chip text-[#A3A3A3] accent-hover" data-testid="admin-activity-toggle-all">
                    {showAllActivity ? "Groepeer per persoon" : "Toon complete geschiedenis"}
                  </button>
                )}
              </div>
              {activity.length === 0 ? (
                <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]" data-testid="admin-activity-empty">Nog geen activiteit.</div>
              ) : (() => {
                const renderRow = (e, i, extraTestId) => {
                  const Icon = e.kind === "payment" ? Wallet : (e.kind === "rsvp" || e.kind === "cancellation") ? Ticket : FormInput;
                  const navigable = (e.kind === "submission" && e.sub_id) || (e.kind === "approval" && e.sub_id) || (e.kind === "rsvp" && e.instance_id) || e.kind === "payment" || e.kind === "cancellation" || !!e.email;
                  return (
                    <div
                      key={extraTestId || i}
                      className={`line-b line-dark py-3 flex items-center gap-4 ${navigable ? "cursor-pointer hover:bg-white/[0.03] transition-colors group" : ""}`}
                      onClick={() => navigable && openActivity(e)}
                      role={navigable ? "button" : undefined}
                      title={navigable ? "Klik om naar het betreffende onderdeel te gaan" : undefined}
                      data-testid={extraTestId || `admin-activity-${i}`}
                    >
                      <span className={`chip w-24 shrink-0 inline-flex items-center gap-2 ${e.kind === "payment" ? "accent-ink" : "text-[#A3A3A3]"}`} style={e.kind === "payment" ? { color: "var(--joh-accent)" } : {}}>
                        <Icon size={12}/> {e.kind === "payment" ? "Betaling" : e.kind === "rsvp" ? "RSVP" : e.kind === "cancellation" ? "Annulering" : e.kind === "approval" ? "Goedkeuring" : "Formulier"}
                      </span>
                      <span className={`font-display text-base md:text-lg uppercase flex-1 truncate ${navigable ? "group-hover:text-[#D4A93A] transition-colors" : ""}`}>{e.title}</span>
                      <span className="text-[#DDD6CB] text-sm truncate hidden md:inline max-w-xs">{e.detail}</span>
                      <span className="chip text-[#4A4A4A] shrink-0">{new Date(e.at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      {navigable && <ChevronRight size={14} className="text-[#4A4A4A] group-hover:text-[#D4A93A] transition-colors shrink-0"/>}
                    </div>
                  );
                };
                if (showAllActivity) {
                  return <div className="line-t line-dark" data-testid="admin-activity-list">{activity.map((e, i) => renderRow(e, i))}</div>;
                }
                // groepeer per persoon (e-mail, anders titel): toon alleen de nieuwste actie
                const groups = new Map();
                activity.forEach(e => {
                  const key = e.email || e.title;
                  if (!groups.has(key)) groups.set(key, []);
                  groups.get(key).push(e);
                });
                const personName = (e) => (e.title.split("·")[1] || e.title).trim();
                return (
                  <div className="line-t line-dark" data-testid="admin-activity-list">
                    {[...groups.entries()].map(([key, events], gi) => {
                      const latest = events[0];
                      const rest = events.slice(1);
                      const isOpen = expandedPerson === key;
                      return (
                        <div key={key}>
                          <div className="relative">
                            {renderRow(latest, gi)}
                            {rest.length > 0 && (
                              <button
                                onClick={() => setExpandedPerson(isOpen ? null : key)}
                                className="absolute right-0 top-1/2 -translate-y-1/2 chip px-2 py-1 border border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1 bg-[#0A0A0A]"
                                title={`Alle acties van ${personName(latest)}`}
                                data-testid={`admin-activity-expand-${gi}`}
                              >
                                <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                                +{rest.length} van {personName(latest)}
                              </button>
                            )}
                          </div>
                          {isOpen && (
                            <div className="pl-8 border-l-2 border-[#D4A93A]/40 ml-3 my-1" data-testid={`admin-activity-history-${gi}`}>
                              {rest.map((e, ri) => renderRow(e, ri, `admin-activity-history-${gi}-${ri}`))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* AANWEZIGHEID & NO-SHOWS PER MAAND */}
            <div className="mt-10" data-testid="admin-dash-attendance">
              <div className="flex items-center justify-between mb-3">
                <div className="chip text-[#A3A3A3]">— Aanwezigheid & no-shows</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => shiftAttMonth(-1)} className="chip border border-[#F5F3F0]/25 px-3 py-1.5 text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="admin-att-prev">‹</button>
                  <span className="chip text-[#F5F3F0] w-40 text-center capitalize" data-testid="admin-att-month">{attMonthLabel}</span>
                  <button onClick={() => shiftAttMonth(1)} className="chip border border-[#F5F3F0]/25 px-3 py-1.5 text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="admin-att-next">›</button>
                </div>
              </div>
              {attMonthlyBusy && !attMonthly ? (
                <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]">Laden…</div>
              ) : !attMonthly || attMonthly.classes.length === 0 ? (
                <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]" data-testid="admin-att-empty">Geen lessen met aanmeldingen in {attMonthLabel}.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="admin-att-totals">
                    {[
                      { label: "Geboekt", value: attMonthly.totals.booked, testid: "admin-att-total-booked" },
                      { label: "Aanwezig", value: attMonthly.totals.present, testid: "admin-att-total-present", color: "#4ADE80" },
                      { label: "No-show", value: attMonthly.totals.absent, testid: "admin-att-total-absent", color: "#F87171" },
                      { label: "Opkomst", value: attMonthly.totals.rate === null ? "—" : `${attMonthly.totals.rate}%`, testid: "admin-att-total-rate" },
                    ].map((s) => (
                      <div key={s.testid} className="border border-[#F5F3F0]/15 p-4" data-testid={s.testid}>
                        <div className="chip text-[#A3A3A3]">{s.label}</div>
                        <div className="font-display text-3xl md:text-4xl mt-1" style={s.color ? { color: s.color } : {}}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="line-t line-dark" data-testid="admin-att-list">
                    {attMonthly.classes.map((c, i) => {
                      const d = c.date ? fmtDate(c.date) : null;
                      return (
                        <div key={c.instance_id} className="line-b line-dark py-3.5 flex flex-wrap items-center gap-3 md:gap-6" data-testid={`admin-att-row-${i}`}>
                          <span className="chip text-[#A3A3A3] w-44 shrink-0 whitespace-nowrap">{d ? `${d.weekday} ${d.day} ${d.month}` : "—"} · {c.start || ""}</span>
                          <span className="font-display text-lg uppercase flex-1 min-w-32 truncate">{c.type || "Les"}</span>
                          <span className="chip text-[#A3A3A3] w-20 text-right">{c.booked} geboekt</span>
                          <span className="chip w-20 text-right" style={{ color: "#4ADE80" }}>{c.present} aanw.</span>
                          <span className="chip w-20 text-right" style={{ color: c.absent > 0 ? "#F87171" : "#4A4A4A" }} data-testid={`admin-att-absent-${i}`}>{c.absent} no-show</span>
                          <span className="chip w-14 text-right" style={{ color: "var(--joh-accent)" }} data-testid={`admin-att-rate-${i}`}>{c.rate === null ? "—" : `${c.rate}%`}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : tab === "aanmeldingen" ? (
          <AanmeldingenInbox
            items={items} payments={payments} filter={aanmeldingenFilter} setFilter={setAanmeldingenFilter}
            onOpen={(it) => setSelected(it)} onStatus={updateSubmissionStatus} onDelete={deleteTrialDirect}
            search={listSearch} setSearch={setListSearch} sort={listSort} setSort={setListSort}
            onOpenLinked={openLinkedClass}
          />
        ) : tab === "trials" ? (
          <div className="max-w-5xl" data-testid="admin-trials-panel">
            <div className="section-tag section-tag-dark mb-4">— Klanten / Proeflessen</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Proeflessen.</h2>
            {/* Instellingen */}
            <div className="mt-8 border border-[#F5F3F0]/15 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-end" data-testid="admin-trial-settings-panel">
              <label className="flex items-center gap-3 chip text-[#A3A3A3] cursor-pointer">
                <input type="checkbox" checked={!!trialSettingsForm.enabled} onChange={(e) => setTrialSettingsForm(f => ({ ...f, enabled: e.target.checked }))} className="w-4 h-4 accent-[#D4A93A]" data-testid="admin-trial-enabled" />
                Proefles-inschrijving actief
              </label>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="chip text-[#A3A3A3]">Vaste proefles-datum</label>
                  <input type="date" value={trialSettingsForm.date || ""} onChange={(e) => setTrialSettingsForm(f => ({ ...f, date: e.target.value }))} className="editorial-input dark" data-testid="admin-trial-date"/>
                </div>
                <button onClick={() => saveContent("trial_settings", trialSettingsForm)} disabled={contentBusy} className="btn-light" data-testid="admin-trial-settings-save">{contentBusy ? "…" : "Opslaan"}</button>
              </div>
            </div>
            {/* Gratis-proefles promotie (spec 10/11): aan/uit + vrije teksten; uitschakelen verwijdert alleen de melding */}
            <div className="mt-4 border border-[#D4A93A]/30 p-5 md:p-6 space-y-5" data-testid="admin-trial-promo-panel">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="chip" style={{ color: "var(--joh-accent)" }}>— Gratis proefles promotie</div>
                  <p className="text-[#A3A3A3] text-sm mt-1 max-w-lg">Toont een opvallende melding op de proefles-landingspagina. Uitschakelen verwijdert alleen deze promotie — inschrijvingen blijven werken.</p>
                </div>
                <label className="flex items-center gap-3 chip text-[#A3A3A3] cursor-pointer">
                  <input type="checkbox" checked={!!trialSettingsForm.promo_active} onChange={(e) => setTrialSettingsForm(f => ({ ...f, promo_active: e.target.checked }))} className="w-4 h-4 accent-[#D4A93A]" data-testid="admin-trial-promo-active" />
                  Promotie tonen
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="chip text-[#DDD6CB]">Titel</label>
                  <input value={trialSettingsForm.promo_title || ""} onChange={(e) => setTrialSettingsForm(f => ({ ...f, promo_title: e.target.value }))} className="editorial-input dark" placeholder="Gratis proefles" data-testid="admin-trial-promo-title"/>
                </div>
                <div>
                  <label className="chip text-[#DDD6CB]">Datum-tekst</label>
                  <input value={trialSettingsForm.promo_date || ""} onChange={(e) => setTrialSettingsForm(f => ({ ...f, promo_date: e.target.value }))} className="editorial-input dark" placeholder="6 oktober" data-testid="admin-trial-promo-date"/>
                </div>
                <div>
                  <label className="chip text-[#DDD6CB]">CTA-tekst</label>
                  <input value={trialSettingsForm.promo_cta || ""} onChange={(e) => setTrialSettingsForm(f => ({ ...f, promo_cta: e.target.value }))} className="editorial-input dark" placeholder="Meld je aan voor de proefles" data-testid="admin-trial-promo-cta"/>
                </div>
                <div>
                  <label className="chip text-[#DDD6CB]">Beschrijving</label>
                  <input value={trialSettingsForm.promo_description || ""} onChange={(e) => setTrialSettingsForm(f => ({ ...f, promo_description: e.target.value }))} className="editorial-input dark" placeholder="Je eerste les is gratis." data-testid="admin-trial-promo-description"/>
                </div>
              </div>
              <button onClick={() => saveContent("trial_settings", trialSettingsForm)} disabled={contentBusy} className="btn-light" data-testid="admin-trial-promo-save">{contentBusy ? "…" : "Promotie opslaan"}</button>
            </div>
            {/* Trials tabel */}
            <div className="mt-8">
              <AanmeldingenInbox
                items={items.filter(it => it.type === "trial")} payments={payments} filter="alle" setFilter={() => {}}
                onOpen={(it) => setSelected(it)} onStatus={updateSubmissionStatus} onDelete={deleteTrialDirect}
                search={listSearch} setSearch={setListSearch} sort={listSort} setSort={setListSort}
                onOpenLinked={openLinkedClass}
                hideTabs
              />
            </div>
          </div>
        ) : tab === "classes" ? (
          classes.length === 0 ? (
            <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="admin-classes-empty">
              Geen komende lessen in dit venster.
            </div>
          ) : (
            <>
            <div className="mb-6 flex items-center justify-between">
              <div className="section-tag section-tag-dark">— {classes.length} komende lessen</div>
              <button onClick={exportClasses} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-classes-export">
                <Download size={14}/> CSV export
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-classes-grid">
              {classes.map((c) => {
                const d = fmtDate(c.date);
                const pct = Math.min(100, Math.round(((c.going || 0) / c.capacity) * 100));
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedClass(c)}
                    className="text-left border border-[#F5F3F0]/15 p-5 hover:bg-white/[0.04] transition-colors group"
                    data-testid={`admin-class-${c.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="chip text-[#A3A3A3]">{d.weekday}</div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="font-display text-4xl leading-none">{d.day}</span>
                          <span className="chip text-[#A3A3A3] uppercase">{d.month}</span>
                        </div>
                      </div>
                      <div className="chip text-[#A3A3A3] text-right">
                        <div>{c.start}</div>
                        <div className="opacity-70">— {c.end}</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="font-display uppercase text-xl leading-tight">{c.type}</div>
                      <div className="chip text-[#A3A3A3] mt-1">{c.level}</div>
                    </div>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-sm text-[#DDD6CB] inline-flex items-center gap-2">
                        <Users size={14}/> {c.going || 0}/{c.capacity}
                        {c.waitlist_count > 0 && (
                          <span className="chip" style={{ color: "var(--joh-accent)" }} data-testid={`admin-class-wl-badge-${c.id}`}>+{c.waitlist_count} WL</span>
                        )}
                      </span>
                      <ChevronRight size={16} className="text-[#A3A3A3] group-hover:text-[#F5F3F0]"/>
                    </div>
                    <div className="mt-3 h-[2px] bg-white/10 relative overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 ${pct >= 100 ? "bg-red-500/80" : "bg-[#F5F3F0]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); toggleCountVisibility(c); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); toggleCountVisibility(c); } }}
                      className={`mt-3 chip inline-flex items-center gap-1.5 border px-2.5 py-1.5 transition-colors w-fit ${c.show_count ? "border-[#C6FF3C]/50" : "border-[#F5F3F0]/15 text-[#4A4A4A]"}`}
                      style={c.show_count ? { color: "var(--jace)" } : {}}
                      title={c.show_count ? "Teller zichtbaar op de website — klik om te verbergen" : "Teller verborgen op de website — klik om te tonen"}
                      data-testid={`admin-class-count-toggle-${c.id}`}
                    >
                      {c.show_count ? <Eye size={12}/> : <EyeOff size={12}/>}
                      WEBSITE TELLER {c.show_count ? "AAN" : "UIT"}
                    </span>
                  </button>
                );
              })}
            </div>
            </>
          )
        ) : tab === "archive" ? (
          <div className="max-w-5xl" data-testid="admin-archive-panel">
            <div className="section-tag section-tag-dark mb-4">— Planning / Archief</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Archief.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">
              Verwijderde items gaan nooit direct verloren: ze worden hier <span className="text-[#F5F3F0]">{archiveData?.retention_days || 31} dagen</span> bewaard en daarna automatisch definitief verwijderd. Herstellen kan altijd — definitief verwijderen alleen met een extra bevestiging.
            </p>
            {(() => {
              const cats = archiveData?.categories || {};
              const CATS = [
                { k: "betalingen", l: "Betalingen" },
                { k: "leden", l: "Leden" },
                { k: "proeflessen", l: "Proeflessen" },
                { k: "aanmeldingen", l: "Aanmeldingen" },
                { k: "contactaanvragen", l: "Contactaanvragen" },
                { k: "lesaanmeldingen", l: "Lesaanmeldingen" },
                { k: "overige", l: "Overige" },
              ];
              let list = [...(cats[archiveCat] || [])];
              if (archiveCat === "betalingen" && archivePaySub !== "alle") list = list.filter(x => x.extra?.sub === archivePaySub);
              const q = archiveQuery.trim().toLowerCase();
              if (q) list = list.filter(x => [x.title, x.subtitle, x.description, x.original_status].join(" ").toLowerCase().includes(q));
              if (archiveSort === "days") list.sort((a, b) => a.days_left - b.days_left);
              else if (archiveSort === "type") list.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
              else list.sort((a, b) => (b.archived_at || "").localeCompare(a.archived_at || ""));
              const daysBadge = (d) => d <= 0
                ? { t: "Wordt vandaag verwijderd", c: "#F87171" }
                : d <= 7
                ? { t: `Nog ${d} dag${d === 1 ? "" : "en"}`, c: "#FB923C" }
                : { t: `Nog ${d} dagen`, c: "#A3A3A3" };
              return (
                <>
                  {/* Categorie-tabs */}
                  <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-archive-cats">
                    {CATS.map(c => (
                      <button key={c.k} onClick={() => { setArchiveCat(c.k); setArchivePaySub("alle"); }}
                        className={`px-3 py-2 chip border ${archiveCat === c.k ? "text-[#0A0A0A] bg-[#F5F3F0] border-[#F5F3F0]" : "text-[#A3A3A3] border-[#F5F3F0]/20 hover:text-[#F5F3F0]"}`}
                        data-testid={`admin-archive-tab-${c.k}`}>
                        {c.l} ({(cats[c.k] || []).length})
                      </button>
                    ))}
                  </div>
                  {/* Sub-filter betalingen */}
                  {archiveCat === "betalingen" && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="admin-archive-pay-subs">
                      {[{ k: "alle", l: "Alle" }, { k: "openstaand", l: "Openstaand" }, { k: "betaald", l: "Betaald" }, { k: "geannuleerd", l: "Geannuleerd" }, { k: "mislukt", l: "Mislukt" }].map(s => (
                        <button key={s.k} onClick={() => setArchivePaySub(s.k)}
                          className={`px-2.5 py-1.5 chip border ${archivePaySub === s.k ? "accent-ink border-[#D4A93A]/60" : "text-[#A3A3A3] border-[#F5F3F0]/15 hover:text-[#F5F3F0]"}`}
                          data-testid={`admin-archive-pay-sub-${s.k}`}>
                          {s.l}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Zoeken + sorteren */}
                  <div className="mt-5 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
                      <input value={archiveQuery} onChange={(e) => setArchiveQuery(e.target.value)} placeholder="Zoek op naam, e-mail of omschrijving…" className="editorial-input dark pl-6 w-full" data-testid="admin-archive-search"/>
                    </div>
                    <select value={archiveSort} onChange={(e) => setArchiveSort(e.target.value)} className="editorial-input dark md:w-64" data-testid="admin-archive-sort">
                      <option value="recent" className="bg-[#0A0A0A]">Recentst gearchiveerd eerst</option>
                      <option value="days" className="bg-[#0A0A0A]">Resterende bewaartijd (kortst eerst)</option>
                      <option value="type" className="bg-[#0A0A0A]">Sorteren op type</option>
                    </select>
                  </div>
                  {/* Lijst */}
                  {list.length === 0 ? (
                    <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3] mt-6" data-testid="admin-archive-empty">
                      <Archive size={16} className="inline mr-2 opacity-60"/>Geen items in deze categorie.
                    </div>
                  ) : (
                    <div className="line-t line-dark mt-6" data-testid="admin-archive-list">
                      {list.map((x) => {
                        const badge = daysBadge(x.days_left);
                        return (
                          <div key={`${x.kind}-${x.id}`} className="line-b line-dark py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-5" data-testid={`admin-archive-row-${x.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-display text-lg uppercase truncate">{x.title}</span>
                                {x.amount != null && <span className="chip text-[#F5F3F0]">€{((x.amount || 0) / 100).toLocaleString("nl-NL")}</span>}
                                {x.original_status && <span className="chip px-2 py-0.5 border border-[#F5F3F0]/20 text-[#A3A3A3]" data-testid={`admin-archive-status-${x.id}`}>was: {x.original_status}</span>}
                              </div>
                              <div className="chip text-[#A3A3A3] mt-1 truncate">
                                {x.description && <span className="text-[#DDD6CB]">{x.description} · </span>}
                                {x.subtitle}
                                {x.date && <span> · {new Date(x.date).toLocaleDateString("nl-NL")}</span>}
                              </div>
                              <div className="chip mt-1 flex items-center gap-3 flex-wrap">
                                <span className="text-[#4A4A4A]">Gearchiveerd {x.archived_at ? new Date(x.archived_at).toLocaleDateString("nl-NL") : "—"}</span>
                                <span style={{ color: badge.c }} data-testid={`admin-archive-days-${x.id}`}>{badge.t}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button onClick={() => archiveRestore(x)} className="chip text-[#F5F3F0] border border-[#F5F3F0]/40 px-3 py-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-2" data-testid={`admin-archive-restore-${x.id}`}>
                                <ArchiveRestore size={13}/> Herstel
                              </button>
                              <button onClick={() => archivePurge(x)} className="chip text-red-400 border border-red-400/40 px-3 py-1.5 hover:bg-red-400/10 transition-colors inline-flex items-center gap-2" data-testid={`admin-archive-delete-${x.id}`}>
                                <Trash2 size={13}/> Definitief
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : tab === "payments" ? (
          <div>
          {/* Stripe testbetaling — altijd zichtbaar, gemarkeerd als TEST, telt niet mee in omzet */}
          <div className="mb-6 border border-[#D4A93A]/40 bg-[#D4A93A]/5 p-5" data-testid="admin-testpay-panel">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="chip" style={{ color: "var(--joh-accent)" }}>Stripe testbetaling</div>
                <p className="text-sm text-[#DDD6CB] mt-1">€1,00 via de Stripe-testomgeving — raakt nooit echte omzet of live-kaarten.</p>
              </div>
              <button onClick={runTestPayment} disabled={testPayBusy} className="btn-light" data-testid="admin-testpay-run">
                {testPayBusy ? "Bezig…" : "Testbetaling uitvoeren"}
              </button>
            </div>
            {testPayResult && (
              <div className="mt-4 border-t border-[#D4A93A]/30 pt-4 space-y-1.5" data-testid="admin-testpay-result">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="chip px-2 py-1" style={{ background: testPayResult.success ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)", color: testPayResult.success ? "#4ADE80" : "#F87171" }} data-testid="admin-testpay-status">
                    TEST {testPayResult.success ? "GESLAAGD" : "MISLUKT"}
                  </span>
                  {testPayResult.payment_id && <span className="chip text-[#A3A3A3]" data-testid="admin-testpay-id">{testPayResult.payment_id}</span>}
                </div>
                <div className="chip text-[#A3A3A3]">Bedrag: €{((testPayResult.amount || 0) / 100).toFixed(2)} · {new Date(testPayResult.created).toLocaleString("nl-NL")}</div>
                {testPayResult.error && <div className="text-sm text-red-400" data-testid="admin-testpay-error">{testPayResult.error}</div>}
              </div>
            )}
          </div>
          {payments.length === 0 ? (
            <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="admin-payments-empty">
              Nog geen betalingen.
            </div>
          ) : (
            <div>

              <div className="mb-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="chip text-[#DDD6CB] flex-1" data-testid="admin-payments-revenue">
                  Totale omzet (paid, excl. test): <span className="text-[#F5F3F0] font-display text-2xl ml-2">€{(paidRevenue / 100).toLocaleString("nl-NL")}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="admin-payments-filters">
                  {[
                    { k: "all", l: "Alle" },
                    { k: "paid", l: "Betaald" },
                    { k: "open", l: "Openstaand" },
                    { k: "failed", l: "Mislukt" },
                    { k: "test", l: "Test" },
                  ].map(f => (
                    <button
                      key={f.k}
                      onClick={() => setPaymentFilter(f.k)}
                      className={`px-3 py-2 chip border ${paymentFilter === f.k ? "text-[#0A0A0A] bg-[#F5F3F0] border-[#F5F3F0]" : "text-[#A3A3A3] border-[#F5F3F0]/20 hover:text-[#F5F3F0]"}`}
                      data-testid={`admin-payments-filter-${f.k}`}
                    >
                      {f.l}
                    </button>
                  ))}
                  <SortSelect value={paymentSort} onChange={setPaymentSort} options={MONEY_OPTIONS} testid="admin-payments-sort" className="md:w-56" />
                </div>
              </div>
              {filteredPayments.length === 0 ? (
                <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]">
                  Geen betalingen in deze filter.
                </div>
              ) : (
                <div className="line-t line-dark" data-testid="admin-payments-list">
                  {filteredPayments.map((p) => {
                    const age = paymentAge(p);
                    const ageStyle = age.tone === "paid"
                      ? { color: "var(--joh-accent)" }
                      : age.tone === "overdue"
                      ? { color: "#F87171" }
                      : age.tone === "warning"
                      ? { color: "#FB923C" }
                      : { color: "#A3A3A3" };
                    const ageLabel = age.tone === "paid" ? "paid"
                      : age.tone === "overdue" ? `${age.days}d te laat`
                      : age.tone === "warning" ? `${age.days}d open`
                      : `${age.days}d`;
                    return (
                      <div key={p.session_id || p.id} className="line-b line-dark py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6" data-testid={`admin-payment-${p.session_id || p.id}`}>
                        <span className="chip text-[#A3A3A3] w-24 inline-flex items-center gap-2">
                          {p.kind}
                          {p.is_test && <span className="px-1.5 py-0.5 border border-[#D4A93A]/60" style={{ color: "var(--joh-accent)" }} data-testid={`admin-payment-test-badge-${p.session_id || p.id}`}>TEST</span>}
                        </span>
                        <span className="font-display text-lg md:text-xl uppercase flex-1">{p.label || p.lookup_key}</span>
                        <a href={`mailto:${p.email}`} className="text-[#DDD6CB] text-sm accent-hover">{p.email || "—"}</a>
                        <span className={`chip w-24 ${p.payment_status === "paid" ? "accent-ink" : ["failed","overdue"].includes(p.payment_status) ? "text-red-400" : "text-[#A3A3A3]"}`}>{{ paid: "Betaald", pending: "Openstaand", open: "Openstaand", failed: "Mislukt", overdue: "Mislukt", refunded: "Terugbetaald" }[p.payment_status] || p.payment_status}</span>
                        {p.kind === "membership" && p.approval_status && (
                          <span className={`chip px-2 py-0.5 border ${p.approval_status === "approved" ? "text-[#4ADE80] border-[#4ADE80]/50" : p.approval_status === "rejected" ? "text-[#F87171] border-[#F87171]/50" : ""}`}
                            style={p.approval_status === "pending" ? { color: "var(--joh-accent)", borderColor: "rgba(212,169,58,0.5)" } : {}}
                            data-testid={`admin-payment-approval-${p.session_id || p.id}`}>
                            {{ pending: "Wacht op goedkeuring", approved: "Goedgekeurd", rejected: "Afgewezen" }[p.approval_status]}
                          </span>
                        )}
                        {p.kind === "membership" && p.approval_status === "pending" && (
                          <span className="flex gap-1.5">
                            <button onClick={() => membershipApprove(p)} className="chip px-3 py-1.5 border border-[#4ADE80]/60 text-[#4ADE80] hover:bg-[#4ADE80] hover:text-[#0A0A0A] transition-colors" data-testid={`admin-payment-approve-${p.session_id || p.id}`}>Keur goed</button>
                            <button onClick={() => membershipReject(p)} className="chip px-3 py-1.5 border border-[#F87171]/60 text-[#F87171] hover:bg-[#F87171] hover:text-[#0A0A0A] transition-colors" data-testid={`admin-payment-reject-${p.session_id || p.id}`}>Wijs af</button>
                          </span>
                        )}
                        <span className="chip w-24 text-right" style={ageStyle} data-testid={`admin-payment-age-${p.session_id}`}>{ageLabel}</span>
                        <span className="chip text-[#F5F3F0] w-24 text-right">€{((p.amount || 0)/100).toLocaleString("nl-NL")}</span>
                        <span className="chip text-[#A3A3A3] w-40 text-right">{new Date(p.created_at).toLocaleString("nl-NL")}</span>
                        <button
                          onClick={() => archivePayment(p)}
                          className="text-[#4A4A4A] hover:text-red-400 transition-colors shrink-0"
                          title="Verwijderen (naar archief, 31 dagen bewaard)"
                          data-testid={`admin-payment-delete-${p.session_id || p.id}`}
                        >
                          <Trash2 size={15}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </div>
        ) : tab === "members" ? (
          <>
            <div className="section-tag section-tag-dark mb-4">— Klanten / Leden</div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <h2 className="font-display uppercase text-3xl md:text-5xl">Klantenbestand.</h2>
              <button onClick={() => setMemberFormOpen(true)} className="btn-light w-fit" data-testid="admin-member-add-open">
                <UserPlus size={14}/> Klant toevoegen
              </button>
            </div>
            <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-members-groups">
              {[["all", "Alle"], ["active", "Actief"], ["inactive", "Niet-actief"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setMemberGroup(k)}
                  className={`chip px-4 py-2 border transition-colors ${memberGroup === k ? "border-[#D4A93A] accent-ink bg-[#D4A93A]/10" : "border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0]"}`}
                  data-testid={`admin-members-group-${k}`}>
                  {lbl} ({k === "all" ? members.length : k === "active" ? memberCounts.active : memberCounts.inactive})
                </button>
              ))}
              <span className="chip text-[#4A4A4A] px-1 py-2 hidden md:inline">Actief = membership of resterende credits</span>
            </div>
            <div className="mb-6 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
                <input
                  type="text"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Zoek op naam, e-mail of telefoon…"
                  className="editorial-input dark pl-6 w-full"
                  data-testid="admin-members-search"
                />
              </div>
              <select value={memberStatusFilter} onChange={(e) => setMemberStatusFilter(e.target.value)} className="editorial-input dark md:w-56" data-testid="admin-members-status-filter">
                <option value="" className="bg-[#0A0A0A]">Alle statussen</option>
                {["ACTIEF", "PROEFLES", "GEPAUZEERD", "GEANNULEERD", "BETALING OPEN", "CONTACT"].map(s => <option key={s} value={s} className="bg-[#0A0A0A]">{s}</option>)}
              </select>
              <SortSelect value={memberSort} onChange={setMemberSort} testid="admin-members-sort" className="md:w-56" />
              <button onClick={exportMembers} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-members-export">
                <Download size={14}/> CSV
              </button>
            </div>
            {filteredMembers.length === 0 ? (
              <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="admin-members-empty">
                {memberQuery || memberStatusFilter || memberGroup !== "all" ? "Geen klanten gevonden voor deze filters." : "Nog geen klanten."}
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="admin-members-list">
                <div className="min-w-[960px]">
                  <div className="grid grid-cols-12 gap-3 chip text-[#4A4A4A] pb-2 line-b line-dark">
                    <span className="col-span-2"><SortHeader label="Voornaam" sort={memberSort} onSort={setMemberSort} asc="first_az" desc="first_za" testid="admin-members-sort-first" /></span>
                    <span className="col-span-2"><SortHeader label="Achternaam" sort={memberSort} onSort={setMemberSort} asc="last_az" desc="last_za" testid="admin-members-sort-last" /></span>
                    <span className="col-span-2">E-mail</span>
                    <span className="col-span-1">Telefoon</span>
                    <span className="col-span-1">Membership</span>
                    <span className="col-span-1">Status</span>
                    <span className="col-span-1"><SortHeader label="Startdatum" sort={memberSort} onSort={setMemberSort} asc="date_old" desc="date_new" testid="admin-members-sort-date" /></span>
                    <span className="col-span-1 text-right">Betaling</span>
                    <span className="col-span-1 text-right">Acties</span>
                  </div>
                  {filteredMembers.map((m) => (
                    <div key={m.email} role="button" tabIndex={0} onClick={() => setSelectedMember(m)} onKeyDown={(e) => { if (e.key === "Enter") setSelectedMember(m); }} className="w-full text-left line-b line-dark py-3 grid grid-cols-12 gap-3 items-center hover:bg-white/[0.03] transition-colors cursor-pointer" data-testid={`admin-member-${m.email}`}>
                      <span className="col-span-2 text-sm truncate">{m.first_name || "—"}</span>
                      <span className="col-span-2 text-sm truncate">{m.last_name || "—"}</span>
                      <span className="col-span-2 text-[#DDD6CB] text-sm truncate">{realEmail(m.email) || "—"}</span>
                      <span className="col-span-1 chip truncate">
                        {waLink(m.phone) ? (
                          <a href={waLink(m.phone)} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#4ADE80] transition-colors" title="Open in WhatsApp" data-testid={`member-whatsapp-${m.email}`}>{m.phone}</a>
                        ) : <span className="text-[#A3A3A3]">—</span>}
                      </span>
                      <span className="col-span-1 chip text-[#A3A3A3] truncate">{m.membership || (m.has_membership ? "Membership" : "—")}</span>
                      <span className="col-span-1"><StatusPill label={m.status_label || "—"} /></span>
                      <span className="col-span-1 chip text-[#4A4A4A]">{m.start_date ? new Date(m.start_date + (m.start_date.length === 10 ? "T00:00:00" : "")).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "—"}</span>
                      <span className="col-span-1 chip text-right" style={{ color: m.payment_status === "paid" ? "#4ADE80" : ["overdue","failed"].includes(m.payment_status) ? "#F87171" : "#4A4A4A" }}>
                        {m.payment_status === "paid" ? "Betaald" : m.payment_status === "overdue" || m.payment_status === "failed" ? "Mislukt" : m.payment_status === "pending" ? "Openstaand" : "—"}
                      </span>
                      <span className="col-span-1 flex justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteMember(m); }}
                          className="text-[#A3A3A3] hover:text-red-400 transition-colors"
                          title="Verwijderen"
                          data-testid={`admin-member-delete-${m.email}`}
                        >
                          <Trash2 size={15}/>
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : tab === "promos" ? (
          <div className="max-w-4xl" data-testid="admin-promos-panel">
            <div className="section-tag section-tag-dark mb-4">— Acties / promocodes</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Maak een actie.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">
              Kortingscodes werken direct in de Stripe-checkout (procent op de eerste betaling). Een <span className="accent-ink">gratis sessie</span>-code kan een lid inwisselen in het portaal of krijgt die automatisch bovenop een aankoop.
            </p>

            <form onSubmit={createPromo} className="mt-8 border border-[#F5F3F0]/20 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-promo-form">
              <div>
                <label className="chip text-[#A3A3A3]">Code</label>
                <input required minLength={3} value={promoForm.code} onChange={(e) => setPromoForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="editorial-input dark uppercase" placeholder="BV. OPENING20" data-testid="admin-promo-code"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Type actie</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[["percent", "% korting"], ["fixed", "€ vast bedrag"], ["free_session", "Gratis 1 sessie"]].map(([k, lbl]) => (
                    <button type="button" key={k} onClick={() => setPromoForm(f => ({ ...f, kind: k }))}
                      className={`chip px-4 py-2 border transition-colors ${promoForm.kind === k ? "border-[#F5F3F0] text-[#F5F3F0] bg-[#F5F3F0]/10" : "border-[#F5F3F0]/25 text-[#A3A3A3]"}`}
                      data-testid={`admin-promo-kind-${k}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              {promoForm.kind === "percent" && (
                <div>
                  <label className="chip text-[#A3A3A3]">Kortingspercentage</label>
                  <input required type="number" min="1" max="100" value={promoForm.percent} onChange={(e) => setPromoForm(f => ({ ...f, percent: e.target.value }))} className="editorial-input dark" data-testid="admin-promo-percent"/>
                </div>
              )}
              {promoForm.kind === "fixed" && (
                <div>
                  <label className="chip text-[#A3A3A3]">Vast kortingsbedrag (€)</label>
                  <input required type="number" min="1" step="0.50" value={promoForm.amount_eur} onChange={(e) => setPromoForm(f => ({ ...f, amount_eur: e.target.value }))} className="editorial-input dark" data-testid="admin-promo-amount"/>
                </div>
              )}
              <div>
                <label className="chip text-[#A3A3A3]">Geldig vanaf (leeg = direct)</label>
                <input type="date" value={promoForm.starts_at} onChange={(e) => setPromoForm(f => ({ ...f, starts_at: e.target.value }))} className="editorial-input dark" data-testid="admin-promo-starts"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Geldig t/m (leeg = onbeperkt)</label>
                <input type="date" value={promoForm.ends_at} onChange={(e) => setPromoForm(f => ({ ...f, ends_at: e.target.value }))} className="editorial-input dark" data-testid="admin-promo-ends"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Max. aantal keer gebruikt (leeg = onbeperkt)</label>
                <input type="number" min="1" value={promoForm.max_uses} onChange={(e) => setPromoForm(f => ({ ...f, max_uses: e.target.value }))} className="editorial-input dark" placeholder="Onbeperkt" data-testid="admin-promo-max-uses"/>
              </div>
              <div className="md:col-span-2">
                <label className="chip text-[#A3A3A3]">Geldt voor</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PROMO_PACKAGES.map(([k, lbl]) => (
                    <button type="button" key={k} onClick={() => togglePromoPkg(k)}
                      className={`chip px-3 py-2 border transition-colors ${promoForm.packages.includes(k) ? "text-[#0A0A0A]" : "border-[#F5F3F0]/25 text-[#A3A3A3]"}`}
                      style={promoForm.packages.includes(k) ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : undefined}
                      data-testid={`admin-promo-pkg-${k}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <button disabled={promoBusy} type="submit" className="btn-light md:col-span-2 justify-center" data-testid="admin-promo-submit">
                <Ticket size={14}/> {promoBusy ? "Opslaan…" : "Maak promocode"}
              </button>
            </form>

            <div className="mt-10">
              <div className="chip text-[#A3A3A3] mb-3">Actieve & verlopen codes ({promos.length})</div>
              <div className="line-t line-dark" data-testid="admin-promo-list">
                {promos.length === 0 && <div className="line-b line-dark py-6 chip text-[#A3A3A3]" data-testid="admin-promo-empty">Nog geen acties aangemaakt.</div>}
                {promos.map((p) => (
                  <div key={p.id} className="line-b line-dark py-4 grid grid-cols-12 gap-3 items-center" data-testid={`admin-promo-row-${p.code}`}>
                    <span className="col-span-6 md:col-span-3 font-display text-xl uppercase truncate">{p.code}</span>
                    <span className="col-span-6 md:col-span-3 chip" style={{ color: "var(--joh-accent)" }}>
                      {promoKindLabel(p)}
                    </span>
                    <span className="col-span-6 md:col-span-3 chip text-[#A3A3A3] truncate">{pkgLabel(p.packages)} · {promoWindowLabel(p)}</span>
                    <span className="col-span-3 md:col-span-1 chip text-[#A3A3A3] text-right">{p.used_count || 0}{p.max_uses ? `/${p.max_uses}` : "×"}</span>
                    <button onClick={() => togglePromo(p)} className={`col-span-5 md:col-span-1 chip text-right ${p.active ? "text-green-400" : "text-red-400"}`} data-testid={`admin-promo-toggle-${p.code}`}>
                      {p.active ? "actief" : "uit"}
                    </button>
                    <button onClick={() => deletePromo(p)} className="col-span-4 md:col-span-1 chip text-red-400 hover:text-red-300 inline-flex items-center justify-end gap-1" data-testid={`admin-promo-delete-${p.code}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : tab === "pt" ? (
          <AdminPT />
        ) : tab === "schedule" ? (
          <div className="max-w-5xl" data-testid="admin-schedule-panel">
            <div className="section-tag section-tag-dark mb-4">— Rooster / lestijden</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Lestijden aanpassen.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">Wijzigingen zijn direct zichtbaar in het rooster op de website én sturen de lesplanning (RSVP) aan.</p>
            <div className="mt-8 border border-[#F5F3F0]/15 p-4 max-w-sm" data-testid="admin-program-start-wrap">
              <label className="chip text-[#A3A3A3]">Programma startdatum</label>
              <input type="date" value={scheduleProgramStart} onChange={(e) => setScheduleProgramStart(e.target.value)} className="editorial-input dark" data-testid="admin-program-start" />
              <p className="chip text-[#4A4A4A] mt-2">Deze datum stuurt de lessen aan; de mededeling/actieknop heeft een eigen promo-datum.</p>
            </div>

            <div className="mt-8 space-y-3" data-testid="admin-schedule-rows">
              {scheduleRows.map((r, i) => (
                <div key={i} className="border border-[#F5F3F0]/15 p-4 grid grid-cols-2 md:grid-cols-12 gap-3 items-end" data-testid={`admin-schedule-row-${i}`}>
                  <div className="col-span-2">
                    <label className="chip text-[#A3A3A3]">Dag</label>
                    <select value={r.weekday} onChange={(e) => { const wd = parseInt(e.target.value, 10); updRow(i, { weekday: wd, day: WEEKDAY_NL[wd], dayEn: WEEKDAY_EN[wd] }); }} className="editorial-input dark" data-testid={`admin-schedule-day-${i}`}>
                      {WEEKDAY_NL.map((d, wd) => <option key={wd} value={wd} className="bg-[#0A0A0A]">{d}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-1">
                    <label className="chip text-[#A3A3A3]">Start</label>
                    <input value={r.start} placeholder="18:30" onChange={(e) => updRow(i, { start: e.target.value })} className="editorial-input dark" data-testid={`admin-schedule-start-${i}`} />
                  </div>
                  <div className="md:col-span-1">
                    <label className="chip text-[#A3A3A3]">Eind</label>
                    <input value={r.end} placeholder="20:00" onChange={(e) => updRow(i, { end: e.target.value })} className="editorial-input dark" data-testid={`admin-schedule-end-${i}`} />
                  </div>
                  <div className="md:col-span-3">
                    <label className="chip text-[#A3A3A3]">Training</label>
                    <input value={r.type} onChange={(e) => updRow(i, { type: e.target.value })} className="editorial-input dark" data-testid={`admin-schedule-type-${i}`} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="chip text-[#A3A3A3]">Niveau</label>
                    <input value={r.level || ""} onChange={(e) => updRow(i, { level: e.target.value })} className="editorial-input dark" data-testid={`admin-schedule-level-${i}`} />
                  </div>
                  <div className="md:col-span-1">
                    <label className="chip text-[#A3A3A3]">Coach</label>
                    <input value={r.coach || ""} onChange={(e) => updRow(i, { coach: e.target.value })} className="editorial-input dark" data-testid={`admin-schedule-coach-${i}`} />
                  </div>
                  <div className="md:col-span-1">
                    <label className="chip text-[#A3A3A3]">Cap.</label>
                    <input type="number" min="1" value={r.capacity ?? 14} onChange={(e) => updRow(i, { capacity: parseInt(e.target.value || "14", 10) })} className="editorial-input dark" data-testid={`admin-schedule-capacity-${i}`} />
                  </div>
                  <button onClick={() => removeRow(i)} className="chip text-red-400 hover:text-red-300 inline-flex items-center justify-end gap-1 pb-2" data-testid={`admin-schedule-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={addRow} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-schedule-add">
                <Plus size={14} /> Les toevoegen
              </button>
              <button onClick={() => saveContent("schedule", { rows: scheduleRows, program_start: scheduleProgramStart })} disabled={contentBusy} className="btn-light" data-testid="admin-schedule-save">
                {contentBusy ? "Opslaan…" : "Rooster opslaan"}
              </button>
            </div>
          </div>
        ) : tab === "teaser" ? (
          <div className="max-w-4xl" data-testid="admin-teaser-panel">
            <div className="section-tag section-tag-dark mb-4">— This week by JOH.</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Teaser aanpassen.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">De teaserkaarten op de homepage, los van het vaste rooster. Laat de kaarten leeg om automatisch het rooster over te nemen.</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="chip text-[#A3A3A3]">Subtekst (NL)</label>
                <input value={teaserForm.sub_nl || ""} onChange={(e) => setTeaserForm(f => ({ ...f, sub_nl: e.target.value }))} className="editorial-input dark" data-testid="admin-teaser-sub-nl" />
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Subtekst (EN)</label>
                <input value={teaserForm.sub_en || ""} onChange={(e) => setTeaserForm(f => ({ ...f, sub_en: e.target.value }))} className="editorial-input dark" data-testid="admin-teaser-sub-en" />
              </div>
            </div>
            <div className="mt-8 space-y-3" data-testid="admin-teaser-cards">
              {(teaserForm.cards || []).map((c, i) => (
                <div key={i} className="border border-[#F5F3F0]/15 p-4 grid grid-cols-2 md:grid-cols-12 gap-3 items-end" data-testid={`admin-teaser-card-${i}`}>
                  <div className="md:col-span-2"><label className="chip text-[#A3A3A3]">Dag (NL)</label><input value={c.day || ""} onChange={(e) => updCard(i, { day: e.target.value })} className="editorial-input dark" data-testid={`admin-teaser-card-day-${i}`} /></div>
                  <div className="md:col-span-2"><label className="chip text-[#A3A3A3]">Dag (EN)</label><input value={c.dayEn || ""} onChange={(e) => updCard(i, { dayEn: e.target.value })} className="editorial-input dark" /></div>
                  <div className="md:col-span-2"><label className="chip text-[#A3A3A3]">Tijd</label><input value={c.time || ""} placeholder="18:30 — 20:00" onChange={(e) => updCard(i, { time: e.target.value })} className="editorial-input dark" data-testid={`admin-teaser-card-time-${i}`} /></div>
                  <div className="md:col-span-3"><label className="chip text-[#A3A3A3]">Training</label><input value={c.type || ""} onChange={(e) => updCard(i, { type: e.target.value })} className="editorial-input dark" data-testid={`admin-teaser-card-type-${i}`} /></div>
                  <div className="md:col-span-2"><label className="chip text-[#A3A3A3]">Niveau</label><input value={c.level || ""} onChange={(e) => updCard(i, { level: e.target.value })} className="editorial-input dark" /></div>
                  <button onClick={() => removeCard(i)} className="chip text-red-400 hover:text-red-300 inline-flex items-center justify-end gap-1 pb-2" data-testid={`admin-teaser-card-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={addCard} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-teaser-add-card"><Plus size={14} /> Kaart toevoegen</button>
              <button onClick={cardsFromSchedule} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-teaser-from-schedule">Overnemen uit rooster</button>
              <button onClick={() => saveContent("teaser", teaserForm)} disabled={contentBusy} className="btn-light" data-testid="admin-teaser-save">{contentBusy ? "Opslaan…" : "Teaser opslaan"}</button>
            </div>
          </div>
        ) : tab === "announcement" ? (
          <div className="max-w-3xl space-y-14" data-testid="admin-announcement-panel">
            <div>
              <div className="section-tag section-tag-dark mb-4">— Programma start</div>
              <h2 className="font-display uppercase text-3xl md:text-5xl">Mededeling.</h2>
              <p className="mt-4 text-[#DDD6CB] max-w-xl">Wordt als compacte actieknop bij het rooster getoond. Je beheert hier zelf de exacte tekst, datum en optionele link voor elke promo of aankondiging.</p>
              <div className="mt-8 space-y-4">
                <label className="flex items-center gap-3 chip text-[#A3A3A3] cursor-pointer" data-testid="admin-ann-active-label">
                  <input type="checkbox" checked={!!annForm.active} onChange={(e) => setAnnForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[#D4A93A]" data-testid="admin-ann-active" />
                  Mededeling actief
                </label>
                <div>
                  <label className="chip text-[#A3A3A3]">Datum</label>
                  <input type="date" value={annForm.date || ""} onChange={(e) => setAnnForm(f => ({ ...f, date: e.target.value }))} className="editorial-input dark" data-testid="admin-ann-date" />
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Knoplink (optioneel)</label>
                  <input value={annForm.url || ""} onChange={(e) => setAnnForm(f => ({ ...f, url: e.target.value }))} placeholder="/join of /welkom" className="editorial-input dark" data-testid="admin-ann-url" />
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Tekst (NL)</label>
                  <textarea rows={2} value={annForm.text_nl || ""} onChange={(e) => setAnnForm(f => ({ ...f, text_nl: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-ann-text-nl" />
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Tekst (EN)</label>
                  <textarea rows={2} value={annForm.text_en || ""} onChange={(e) => setAnnForm(f => ({ ...f, text_en: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-ann-text-en" />
                </div>
                <button onClick={() => saveContent("announcement", annForm)} disabled={contentBusy} className="btn-light" data-testid="admin-ann-save">{contentBusy ? "Opslaan…" : "Actieknop opslaan"}</button>
              </div>
            </div>
            <div>
              <div className="section-tag section-tag-dark mb-4">— Actie-banner</div>
              <h2 className="font-display uppercase text-3xl md:text-5xl">Actie-banner.</h2>
              <p className="mt-4 text-[#DDD6CB] max-w-xl">Opvallende banner bovenaan de website, bijvoorbeeld voor een kortingsactie. Optioneel met promocode (beheer je onder Acties).</p>
              <div className="mt-8 space-y-4">
                <label className="flex items-center gap-3 chip text-[#A3A3A3] cursor-pointer">
                  <input type="checkbox" checked={!!bannerForm.active} onChange={(e) => setBannerForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[#D4A93A]" data-testid="admin-banner-active" />
                  Actie-banner actief
                </label>
                <div>
                  <label className="chip text-[#A3A3A3]">Tekst (NL)</label>
                  <textarea rows={2} value={bannerForm.text_nl || ""} onChange={(e) => setBannerForm(f => ({ ...f, text_nl: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-banner-text-nl" />
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Tekst (EN)</label>
                  <textarea rows={2} value={bannerForm.text_en || ""} onChange={(e) => setBannerForm(f => ({ ...f, text_en: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-banner-text-en" />
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Promocode (optioneel)</label>
                  <input value={bannerForm.code || ""} onChange={(e) => setBannerForm(f => ({ ...f, code: e.target.value }))} className="editorial-input dark" placeholder="bijv. OPENING20" data-testid="admin-banner-code" />
                </div>
                <button onClick={() => saveContent("promo_banner", bannerForm)} disabled={contentBusy} className="btn-light" data-testid="admin-banner-save">{contentBusy ? "Opslaan…" : "Banner opslaan"}</button>
              </div>
            </div>

            <div>
              <div className="section-tag section-tag-dark mb-4">— Weekplanning</div>
              <h2 className="font-display uppercase text-3xl md:text-5xl">Extra items.</h2>
              <p className="mt-4 text-[#DDD6CB] max-w-xl">Voeg events, mededelingen of activiteiten toe aan "This week at JOH." op de homepage. Het vaste rooster blijft automatisch zichtbaar; extra items verschijnen in dezelfde stijl. Verlopen items verdwijnen vanzelf van de website.</p>

              <form onSubmit={saveWeekItem} className="mt-8 border border-[#F5F3F0]/20 p-5 md:p-6 space-y-4" data-testid="admin-weekitem-form">
                <div>
                  <label className="chip text-[#A3A3A3]">Titel*</label>
                  <input required value={weekForm.title} onChange={(e) => setWeekForm(f => ({ ...f, title: e.target.value }))} className="editorial-input dark" placeholder="bijv. Open mat training" data-testid="admin-weekitem-title"/>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="chip text-[#A3A3A3]">Datum*</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className={`editorial-input dark w-full flex items-center justify-between gap-2 text-left ${weekForm.date ? "" : "text-[#A3A3A3]"}`} data-testid="admin-weekitem-date">
                          <span>{weekForm.date
                            ? new Date(weekForm.date + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "long", year: "numeric" })
                            : "Kies datum"}</span>
                          <Calendar size={16} className="shrink-0 opacity-60"/>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 border border-[#F5F3F0]/25 bg-[#0A0A0A] rounded-none shadow-none" align="start">
                        <DayPickerCalendar
                          mode="single"
                          showOutsideDays={false}
                          selected={weekForm.date ? new Date(weekForm.date + "T00:00:00") : undefined}
                          className="p-2"
                          classNames={{
                            head_cell: "text-[#A3A3A3] w-7 font-normal text-[0.7rem]",
                            day: "h-7 w-7 p-0 text-xs font-normal text-[#F5F3F0] aria-selected:opacity-100 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors",
                            cell: "p-0 text-center text-xs",
                            row: "flex w-full mt-1",
                            month: "space-y-2",
                            caption_label: "text-sm font-medium text-[#F5F3F0]",
                            nav_button: "h-6 w-6 bg-transparent p-0 opacity-40 hover:opacity-100 border border-[#F5F3F0]/25 text-[#F5F3F0]",
                          }}
                          onSelect={(d) => {
                            if (!d) return;
                            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                            setWeekForm(f => ({ ...f, date: iso }));
                          }}
                          data-testid="admin-weekitem-date-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="chip text-[#A3A3A3]">Tijd</label>
                    <input value={weekForm.time} onChange={(e) => setWeekForm(f => ({ ...f, time: e.target.value }))} className="editorial-input dark" placeholder="19:00 — 21:00" data-testid="admin-weekitem-time"/>
                  </div>
                  <div>
                    <label className="chip text-[#A3A3A3]">Type</label>
                    <select value={weekForm.kind} onChange={(e) => setWeekForm(f => ({ ...f, kind: e.target.value }))} className="editorial-input dark" data-testid="admin-weekitem-kind">
                      <option value="training" className="bg-[#0A0A0A]">Training</option>
                      <option value="event" className="bg-[#0A0A0A]">Event</option>
                      <option value="mededeling" className="bg-[#0A0A0A]">Mededeling</option>
                      <option value="activiteit" className="bg-[#0A0A0A]">Andere activiteit</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Korte omschrijving</label>
                  <textarea rows={2} value={weekForm.description} onChange={(e) => setWeekForm(f => ({ ...f, description: e.target.value }))} className="editorial-input dark w-full" data-testid="admin-weekitem-description"/>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button disabled={weekBusy || !weekForm.title || !weekForm.date} type="submit" className="btn-light" data-testid="admin-weekitem-save">
                    {weekBusy ? "Opslaan…" : weekEditId ? "Item bijwerken" : "Item toevoegen"}
                  </button>
                  {weekEditId && (
                    <button type="button" onClick={() => { setWeekEditId(""); setWeekForm({ title: "", date: "", time: "", kind: "event", description: "" }); }} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-weekitem-cancel">
                      Annuleren
                    </button>
                  )}
                </div>
              </form>

              <div className="mt-8">
                <div className="chip text-[#A3A3A3] mb-3">Items in de weekplanning ({weekItems.length})</div>
                {weekItems.length === 0 ? (
                  <div className="border border-[#F5F3F0]/15 p-6 text-center chip text-[#A3A3A3]" data-testid="admin-weekitems-empty">Nog geen extra items.</div>
                ) : (
                  <div className="line-t line-dark" data-testid="admin-weekitems-list">
                    {weekItems.map((it) => (
                      <div key={it.id} className="line-b line-dark py-4 grid grid-cols-12 gap-3 items-center" data-testid={`admin-weekitem-row-${it.id}`}>
                        <span className="col-span-4 md:col-span-2 chip text-[#A3A3A3]">{new Date(it.date + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}{it.time ? ` · ${it.time}` : ""}</span>
                        <span className="col-span-8 md:col-span-3 font-display text-xl uppercase truncate">{it.title}</span>
                        <span className="col-span-4 md:col-span-2 chip" style={{ color: "var(--joh-accent)" }}>{{ training: "Training", event: "Event", mededeling: "Mededeling", activiteit: "Activiteit" }[it.kind] || it.kind}</span>
                        <span className="col-span-8 md:col-span-3 text-[#DDD6CB] text-sm truncate">{it.description}</span>
                        <span className="col-span-12 md:col-span-2 flex items-center justify-end gap-3">
                          <button onClick={() => editWeekItem(it)} className="chip text-[#A3A3A3] accent-hover" data-testid={`admin-weekitem-edit-${it.id}`}>Bewerk</button>
                          <button onClick={() => deleteWeekItem(it.id)} className="chip text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`admin-weekitem-delete-${it.id}`}><Trash2 size={14}/></button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : tab === "texts_home" ? (
          <TextsPanel
            tag="Homepagina" title="Teksten homepagina."
            hint="Alle teksten van de homepagina per sectie, NL én EN — inclusief grote koppen, navigatie, knoppen en footer. Leeg laten = standaardtekst. Wijzigingen zijn direct zichtbaar."
            sections={TEXT_SECTIONS_HOME}
            form={textsForm} setForm={setTextsForm}
            onSave={() => saveContent("text_overrides", textsForm)} busy={contentBusy}
            testid="admin-texts-home-panel"
          />
        ) : tab === "texts_landing" ? (
          <TextsPanel
            tag="Landingpage (/welkom + /landingpage)" title="Teksten landingpage."
            hint="Alle teksten van de proefles-landingspagina, NL én EN. Leeg laten = standaardtekst. Wijzigingen zijn direct zichtbaar."
            sections={TEXT_SECTIONS_LANDING}
            form={textsForm} setForm={setTextsForm}
            onSave={() => saveContent("text_overrides", textsForm)} busy={contentBusy}
            testid="admin-texts-landing-panel"
          />
        ) : tab === "media" ? (
          <>
            <MediaPanel
              form={mediaForm} files={mediaFiles} busy={mediaBusy}
              onUpload={mediaUpload} onSetUrl={mediaSetUrl} onReset={mediaReset} onDeleteFile={mediaDeleteFile}
            />
            <div className="max-w-5xl mt-16 border-t border-[#F5F3F0]/10 pt-10 space-y-16">
              <JohWorldScoresPanel />
              <JohWorldMusicPanel />
            </div>
          </>
        ) : tab === "emails" ? (
          <EmailsPanel />
        ) : tab === "team" ? (
          <div className="max-w-3xl" data-testid="admin-team-panel">
            <div className="section-tag section-tag-dark mb-4">— Team / admin toegang</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Nodig een admin uit.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">
              Voeg een e-mailadres toe. Laat het wachtwoord leeg voor <span className="accent-ink">alleen magic-link</span> toegang, of stel een wachtwoord in voor login via <span className="accent-ink">/admin/login</span>.
            </p>

            <form onSubmit={inviteAdmin} className="mt-8 border border-[#F5F3F0]/20 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-team-form">
              <div>
                <label className="chip text-[#A3A3A3]">E-mail</label>
                <input required type="email" value={inviteForm.email} onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))} className="editorial-input dark" data-testid="admin-team-email"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Naam (optioneel)</label>
                <input value={inviteForm.name} onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))} className="editorial-input dark" data-testid="admin-team-name"/>
              </div>
              <div className="md:col-span-2">
                <label className="chip text-[#A3A3A3]">Wachtwoord (leeg = alleen magic-link)</label>
                <input type="text" value={inviteForm.password} onChange={(e) => setInviteForm(f => ({ ...f, password: e.target.value }))} className="editorial-input dark" placeholder="Kies een sterk wachtwoord of laat leeg" data-testid="admin-team-password"/>
              </div>
              {isOwner && (
                <div className="md:col-span-2">
                  <label className="chip text-[#A3A3A3]">Rol</label>
                  <div className="flex gap-2 mt-2">
                    {[["admin", "Admin"], ["owner", "Owner"]].map(([r, lbl]) => (
                      <button type="button" key={r} onClick={() => setInviteForm(f => ({ ...f, role: r }))}
                        className={`chip px-4 py-2 border transition-colors ${inviteForm.role === r ? (r === "owner" ? "border-[#D4A93A] accent-ink bg-[#D4A93A]/10" : "border-[#F5F3F0] text-[#F5F3F0] bg-[#F5F3F0]/10") : "border-[#F5F3F0]/25 text-[#A3A3A3]"}`}
                        data-testid={`admin-team-role-${r}`}>{lbl}</button>
                    ))}
                  </div>
                  <div className="chip text-[#4A4A4A] mt-2">Owner = volledige toegang incl. rolbeheer. Alleen owners kunnen owners aanmaken.</div>
                </div>
              )}
              <button disabled={inviteLoading} type="submit" className="btn-light md:col-span-2 justify-center" data-testid="admin-team-submit">
                <UserPlus size={14}/> {inviteLoading ? "Uitnodigen…" : inviteForm.role === "owner" && isOwner ? "Voeg owner toe" : "Voeg admin toe"}
              </button>
            </form>

            {inviteResult?.magic_url && (
              <div className="mt-6 border border-[#F5F3F0]/20 p-5" data-testid="admin-team-invite-result">
                <div className="chip text-[#A3A3A3] mb-2">Deel deze magic-link met de nieuwe admin (30 dagen geldig)</div>
                <div className="font-mono text-xs md:text-sm text-[#DDD6CB] break-all bg-black/40 p-3 border border-[#F5F3F0]/10">{inviteResult.magic_url}</div>
                <div className="mt-3 flex gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult.magic_url); toast.success("Gekopieerd"); }} className="btn-light" data-testid="admin-team-copy-magic">
                    <Copy size={14}/> Kopieer link
                  </button>
                </div>
              </div>
            )}

            <div className="mt-10">
              <div className="chip text-[#A3A3A3] mb-3">Huidige toegang ({team.length})</div>
              <div className="line-t line-dark" data-testid="admin-team-list">
                {team.map((t) => {
                  const isOwnerRow = t.role === "owner";
                  const isPrimaryOwner = t.email === "glenn@johboxing.com";
                  return (
                  <div key={t.email} className="line-b line-dark py-4" data-testid={`admin-team-row-${t.email}`}>
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <span className="col-span-6 md:col-span-3 font-display text-xl uppercase truncate">{t.name || "—"}</span>
                      <a href={`mailto:${t.email}`} className="col-span-6 md:col-span-4 text-[#DDD6CB] text-sm accent-hover truncate">{t.email}</a>
                      <span className="col-span-4 md:col-span-2">
                        <span className={`chip px-2 py-0.5 border ${isOwnerRow ? "accent-ink border-[#D4A93A]/50" : "text-[#A3A3A3] border-[#F5F3F0]/15"}`} data-testid={`admin-team-role-badge-${t.email}`}>
                          {isOwnerRow ? (isPrimaryOwner ? "OWNER · PRIMAIR" : "OWNER") : "ADMIN"}
                        </span>
                      </span>
                      <span className="col-span-3 md:col-span-2 chip text-[#A3A3A3] text-right">{t.created_at ? new Date(t.created_at).toLocaleDateString("nl-NL") : "—"}</span>
                      <span className="col-span-5 md:col-span-1 flex items-center justify-end gap-3">
                        {isOwnerRow ? (
                          <span className="chip text-[#4A4A4A]" title="Owner-accounts zijn beschermd">beschermd</span>
                        ) : (
                          <>
                            {isOwner && (
                              <button onClick={() => changeTeamRole(t.email, "owner")} className="chip text-[#A3A3A3] accent-hover" title="Maak owner" data-testid={`admin-team-make-owner-${t.email}`}>
                                <ShieldCheck size={14}/>
                              </button>
                            )}
                            <button onClick={() => { setPwResetFor(pwResetFor === t.email ? "" : t.email); setPwResetValue(""); }} className="chip text-[#A3A3A3] accent-hover inline-flex items-center gap-1" title="Wachtwoord instellen" data-testid={`admin-team-pw-${t.email}`}>
                              <Key size={14}/>
                            </button>
                            <button onClick={() => removeAdmin(t.email)} className="chip text-red-400 hover:text-red-300 inline-flex items-center gap-1" title="Intrekken" data-testid={`admin-team-remove-${t.email}`}>
                              <Trash2 size={14}/>
                            </button>
                          </>
                        )}
                        {isOwnerRow && isOwner && !isPrimaryOwner && (
                          <button onClick={() => changeTeamRole(t.email, "admin")} className="chip text-[#A3A3A3] accent-hover" title="Degradeer naar admin" data-testid={`admin-team-demote-${t.email}`}>
                            <Shield size={14}/>
                          </button>
                        )}
                      </span>
                    </div>
                    {pwResetFor === t.email && !isOwnerRow && (
                      <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-end" data-testid={`admin-team-pwform-${t.email}`}>
                        <div className="flex-1">
                          <label className="chip text-[#A3A3A3]">Nieuw wachtwoord voor {t.email}</label>
                          <input type="text" value={pwResetValue} onChange={(e) => setPwResetValue(e.target.value)} className="editorial-input dark" placeholder="Minimaal 8 tekens" data-testid={`admin-team-pwinput-${t.email}`}/>
                        </div>
                        <button onClick={() => setAdminPassword(t.email)} disabled={pwBusy || pwResetValue.length < 8} className="btn-light" data-testid={`admin-team-pwsave-${t.email}`}>
                          {pwBusy ? "Opslaan…" : "Zet wachtwoord"}
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : tab === "access" ? (
          <div className="max-w-3xl">
            <div className="chip text-[#A3A3A3] mb-2">— Bookmarkable auto-login</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl">Genereer je magic-link.</h2>
            <p className="mt-4 text-[#DDD6CB] max-w-xl">
              Één klik. Één keer opslaan als bookmark. Daarna log je nooit meer opnieuw in op dit apparaat.
              Deel deze link <span className="accent-ink">niet</span> met anderen — wie 'm heeft, komt binnen.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="chip text-[#A3A3A3]">Geldig aantal dagen</label>
                <input
                  type="number" min="1" max="365"
                  value={magicDays}
                  onChange={(e) => setMagicDays(parseInt(e.target.value || "30", 10))}
                  className="editorial-input dark"
                  data-testid="admin-magic-days"
                />
              </div>
              <button onClick={generateMagicLink} disabled={magicLoading} className="btn-light" data-testid="admin-magic-generate">
                <Key size={14}/> {magicLoading ? "Genereren…" : "Genereer link"}
              </button>
            </div>

            {magicLink && (
              <div className="mt-8 border border-[#F5F3F0]/20 p-5 md:p-6" data-testid="admin-magic-result">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-8 items-start">
                  <div>
                    <div className="chip text-[#A3A3A3] mb-3">Jouw persoonlijke admin-URL ({magicDays} dagen geldig)</div>
                    <div className="font-mono text-xs md:text-sm text-[#DDD6CB] break-all bg-black/40 p-4 border border-[#F5F3F0]/10">{magicLink}</div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button onClick={copyMagicLink} className="btn-light" data-testid="admin-magic-copy"><Copy size={14}/> Kopieer</button>
                      <a href={magicLink} target="_blank" rel="noreferrer" className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="admin-magic-open">
                        <ExternalLink size={14}/> Open in nieuw venster
                      </a>
                    </div>
                  </div>
                  <div className="text-center" data-testid="admin-magic-qr">
                    <div className="chip text-[#A3A3A3] mb-3">Scan op je telefoon</div>
                    <div className="bg-[#F5F3F0] p-4 inline-block">
                      <QRCodeSVG value={magicLink} size={160} bgColor="#F5F3F0" fgColor="#0A0A0A" level="M" includeMargin={false} />
                    </div>
                  </div>
                </div>
                <div className="chip text-[#A3A3A3] mt-6">
                  Tip: sla op als bookmark of scan de QR-code met je telefoon. Als de link verloopt kom je hier terug voor een nieuwe.
                </div>
              </div>
            )}

            <div className="mt-12 border border-[#F5F3F0]/15 p-5 md:p-6" data-testid="admin-own-password">
              <div className="chip text-[#A3A3A3] mb-3">— Eigen wachtwoord wijzigen</div>
              <form onSubmit={changeOwnPassword} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="chip text-[#A3A3A3]">Huidig wachtwoord</label>
                  <input required type="password" value={pwForm.current} onChange={(e) => setPwForm(f => ({ ...f, current: e.target.value }))} className="editorial-input dark" data-testid="admin-pw-current"/>
                </div>
                <div>
                  <label className="chip text-[#A3A3A3]">Nieuw wachtwoord (min. 8 tekens)</label>
                  <input required type="password" minLength={8} value={pwForm.next} onChange={(e) => setPwForm(f => ({ ...f, next: e.target.value }))} className="editorial-input dark" data-testid="admin-pw-next"/>
                </div>
                <button disabled={pwBusy} type="submit" className="btn-light md:col-span-2 justify-center" data-testid="admin-pw-save">
                  <Key size={14}/> {pwBusy ? "Opslaan…" : "Wijzig mijn wachtwoord"}
                </button>
              </form>
            </div>

            <div className="mt-12 border border-[#F5F3F0]/15 p-5 md:p-6">
              <div className="chip text-[#A3A3A3] mb-3">Waar de link vandaan komt (dev)</div>
              <div className="text-sm text-[#DDD6CB] space-y-2 font-mono">
                <div><span className="text-[#A3A3A3]">POST</span> /api/auth/magic-link?days={magicDays}</div>
                <div><span className="text-[#A3A3A3]">Header:</span> Authorization: Bearer &lt;jouw huidige token&gt;</div>
                <div><span className="text-[#A3A3A3]">Response:</span> {"{ token, expires_in_days }"}</div>
                <div><span className="text-[#A3A3A3]">Frontend:</span> visit /admin?token=&lt;token&gt; → auto-login</div>
              </div>
            </div>

            <AuditLogPanel />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="admin-empty">
            Geen inzendingen in deze categorie.
          </div>
        ) : (
          <div data-testid="admin-forms-list">
            <div className="mb-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
              <div className="flex-1 flex items-center gap-3 border border-[#F5F3F0]/20 px-4 py-2">
                <Search size={16} className="text-[#A3A3A3]"/>
                <input
                  type="text"
                  value={formsSearch}
                  onChange={(e) => setFormsSearch(e.target.value)}
                  placeholder="Zoek op naam, e-mail, telefoon, onderwerp of bericht"
                  className="w-full bg-transparent border-0 outline-none py-2 text-[#F5F3F0] placeholder-[#A3A3A3]"
                  data-testid="admin-forms-search"
                />
                {formsSearch && (
                  <button onClick={() => setFormsSearch("")} className="chip text-[#A3A3A3] accent-hover" data-testid="admin-forms-search-clear">wissen</button>
                )}
              </div>
              <span className="chip text-[#A3A3A3]">{filteredItems.length} van {items.length}</span>
            </div>

            {groupedByMonth.length === 0 ? (
              <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]">
                Geen inzendingen gevonden.
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-forms-months">
                {groupedByMonth.map((g) => {
                  const isOpen = openMonths.has(g.key);
                  return (
                    <div key={g.key} className="border border-[#F5F3F0]/15" data-testid={`admin-forms-month-${g.key}`}>
                      <button
                        onClick={() => toggleMonth(g.key)}
                        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.03]"
                        data-testid={`admin-forms-month-toggle-${g.key}`}
                      >
                        <div className="flex items-center gap-4">
                          <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`}/>
                          <span className="font-display uppercase text-xl md:text-2xl">{g.label}</span>
                        </div>
                        <span className="chip text-[#A3A3A3]">{g.rows.length} inzending{g.rows.length === 1 ? "" : "en"}</span>
                      </button>
                      {isOpen && (
                        <div className="line-t line-dark">
                          {g.rows.map(item => (
                            <button key={item.id} onClick={()=>setSelected(item)} className="w-full text-left line-b line-dark py-4 px-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 hover:bg-white/[0.03] transition-colors" data-testid={`admin-row-${item.id}`}>
                              <span className="chip text-[#A3A3A3] w-32">{typeLabel[item.type] || item.type}</span>
                              <span className="font-display text-2xl uppercase flex-1">{item.data?.name || "—"}</span>
                              <span className="text-[#DDD6CB] text-sm">{item.data?.email}</span>
                              <span className="chip text-[#A3A3A3] w-40 text-right">{new Date(item.created_at).toLocaleString("nl-NL")}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </main>
      </div>

      {memberFormOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setMemberFormOpen(false)}>
          <form onSubmit={submitMemberForm} className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-8" onClick={(e)=>e.stopPropagation()} data-testid="admin-member-form-modal">
            <div className="chip text-[#A3A3A3]">Klanten / Leden</div>
            <div className="font-display uppercase text-3xl mt-1 mb-6">Klant toevoegen.</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="chip text-[#A3A3A3]">Voornaam*</label>
                <input required value={memberForm.first_name} onChange={(e)=>setMemberForm(f=>({...f,first_name:e.target.value}))} className="editorial-input dark" data-testid="member-form-first-name"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Achternaam*</label>
                <input required value={memberForm.last_name} onChange={(e)=>setMemberForm(f=>({...f,last_name:e.target.value}))} className="editorial-input dark" data-testid="member-form-last-name"/>
              </div>
              <div className="col-span-2">
                <label className="chip text-[#A3A3A3]">E-mailadres (optioneel)</label>
                <input type="email" value={memberForm.email} onChange={(e)=>setMemberForm(f=>({...f,email:e.target.value}))} className="editorial-input dark" data-testid="member-form-email"/>
              </div>
              <div>
                <PhoneField dark value={memberForm.phone} onChange={(v) => setMemberForm(f => ({ ...f, phone: v }))} testid="member-form-phone" label="Telefoonnummer"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Membership type</label>
                <select value={memberForm.membership} onChange={(e)=>setMemberForm(f=>({...f,membership:e.target.value}))} className="editorial-input dark" data-testid="member-form-membership">
                  <option value="" className="bg-[#0A0A0A]">— Geen —</option>
                  <option value="1x per week" className="bg-[#0A0A0A]">1× per week</option>
                  <option value="2x per week" className="bg-[#0A0A0A]">2× per week</option>
                  <option value="Onbeperkt" className="bg-[#0A0A0A]">Onbeperkt</option>
                  <option value="Rittenkaart" className="bg-[#0A0A0A]">Rittenkaart</option>
                  <option value="Drop-in" className="bg-[#0A0A0A]">Drop-in</option>
                </select>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Startdatum</label>
                <input type="date" value={memberForm.start_date} onChange={(e)=>setMemberForm(f=>({...f,start_date:e.target.value}))} className="editorial-input dark" data-testid="member-form-start-date"/>
              </div>
              <div>
                <label className="chip text-[#A3A3A3]">Status</label>
                <select value={memberForm.status} onChange={(e)=>setMemberForm(f=>({...f,status:e.target.value}))} className="editorial-input dark" data-testid="member-form-status">
                  <option value="actief" className="bg-[#0A0A0A]">Actief</option>
                  <option value="proefles" className="bg-[#0A0A0A]">Proefles</option>
                  <option value="gepauzeerd" className="bg-[#0A0A0A]">Gepauzeerd</option>
                  <option value="geannuleerd" className="bg-[#0A0A0A]">Geannuleerd</option>
                  <option value="betaling_open" className="bg-[#0A0A0A]">Betaling open</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="chip text-[#A3A3A3]">Direct koppelen aan les (optioneel)</label>
                <select value={memberForm.class_id} onChange={(e)=>setMemberForm(f=>({...f,class_id:e.target.value}))} className="editorial-input dark" data-testid="member-form-class">
                  <option value="" className="bg-[#0A0A0A]">— Niet koppelen —</option>
                  {classes.map(c => { const d = fmtDate(c.date); return <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.type} · {d.weekday} {d.day} {d.month} · {c.start}–{c.end} ({c.going || 0}/{c.capacity})</option>; })}
                </select>
              </div>
              <div className="col-span-2">
                <label className="chip text-[#A3A3A3]">Notities</label>
                <textarea rows={2} value={memberForm.notes} onChange={(e)=>setMemberForm(f=>({...f,notes:e.target.value}))} className="editorial-input dark w-full" data-testid="member-form-notes"/>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={memberFormBusy} className="btn-light flex-1 justify-center" data-testid="member-form-save">{memberFormBusy ? "Opslaan…" : "Klant opslaan"}</button>
              <button type="button" onClick={()=>setMemberFormOpen(false)} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="member-form-cancel">Annuleren</button>
            </div>
          </form>
        </div>
      )}

      {selectedMember && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setSelectedMember(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="admin-member-detail-modal">
            <div className="chip text-[#A3A3A3]">Klant</div>
            <div className="font-display uppercase text-4xl mt-1">{selectedMember.name || `${selectedMember.first_name || ""} ${selectedMember.last_name || ""}`}</div>
            <div className="mt-2"><StatusPill label={selectedMember.status_label || "CONTACT"}/></div>
            <div className="mt-6 space-y-3">
              <Row icon={<Mail size={14}/>} label="E-mail" value={selectedMember.email}/>
              <Row icon={<Phone size={14}/>} label="Telefoon" value={selectedMember.phone} href={waLink(selectedMember.phone)}/>
              <Row icon={<Wallet size={14}/>} label="Membership" value={selectedMember.membership || (selectedMember.has_membership ? "Actief membership" : "—")}/>
              <Row icon={<Calendar size={14}/>} label="Startdatum" value={selectedMember.start_date}/>
              <Row icon={<Activity size={14}/>} label="Betaling" value={selectedMember.payment_status === "paid" ? "Betaald" : ["overdue","failed"].includes(selectedMember.payment_status) ? "Mislukt / open" : selectedMember.payment_status === "pending" ? "Openstaand" : "—"}/>
              <Row icon={<Ticket size={14}/>} label="Credits" value={selectedMember.pack_credits > 0 ? `${selectedMember.pack_credits} resterend` : "—"}/>
              <Row icon={<Users size={14}/>} label="Les-aanmeldingen" value={String(selectedMember.rsvp_count || 0)}/>
              <Row icon={<Activity size={14}/>} label="Bronnen" value={(selectedMember.sources || []).join(", ")}/>
              <Row icon={<Calendar size={14}/>} label="Eerste contact" value={selectedMember.first_seen ? new Date(selectedMember.first_seen).toLocaleDateString("nl-NL") : "—"}/>
              {selectedMember.manual_notes && <Row icon={<MessageSquare size={14}/>} label="Notities" value={selectedMember.manual_notes}/>}
            </div>

            {/* Profielgegevens (spec 18) */}
            {memberHistory?.profile && (
              <div className="mt-6 border border-[#F5F3F0]/15 p-4" data-testid="member-detail-profile">
                <div className="chip text-[#A3A3A3] mb-3">— Profielgegevens</div>
                <div className="space-y-3">
                  <Row icon={<Calendar size={14}/>} label="Geboortedatum" value={memberHistory.profile.dob || "—"}/>
                  <Row icon={<Users size={14}/>} label="Geslacht" value={{ male: "Man", female: "Vrouw", undisclosed: "Zeg ik liever niet" }[memberHistory.profile.gender] || "—"}/>
                  <Row icon={<Phone size={14}/>} label="Noodcontact" value={memberHistory.profile.emergency_contact?.name || "—"}/>
                  <Row icon={<Phone size={14}/>} label="Noodcontact tel." value={memberHistory.profile.emergency_contact?.phone || "—"} href={waLink(memberHistory.profile.emergency_contact?.phone)}/>
                  <Row icon={<Users size={14}/>} label="Relatie noodcontact" value={{ partner: "Partner", ouder: "Ouder", broer_zus: "Broer / zus", vriend_in: "Vriend / vriendin", familie: "Familie", anders: "Anders" }[memberHistory.profile.emergency_contact?.relation] || "—"}/>
                  {!memberHistory.profile.profile_complete && (
                    <div className="chip" style={{ color: "var(--joh-accent)" }} data-testid="member-profile-incomplete">Profiel nog niet compleet</div>
                  )}
                </div>
              </div>
            )}

            {/* Opkomst */}
            <div className="mt-6 border border-[#F5F3F0]/15 p-4" data-testid="member-detail-opkomst">
              <div className="chip text-[#A3A3A3] mb-3">— Opkomst</div>
              {!memberHistory ? (
                <div className="chip text-[#4A4A4A]">Laden…</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><div className="chip text-[#4A4A4A]">Geboekt</div><div className="font-display text-2xl" data-testid="member-opkomst-booked">{memberHistory.stats.booked}</div></div>
                  <div><div className="chip text-[#4A4A4A]">Aanwezig</div><div className="font-display text-2xl" style={{ color: "#4ADE80" }} data-testid="member-opkomst-attended">{memberHistory.stats.attended}</div></div>
                  <div><div className="chip text-[#4A4A4A]">No-show</div><div className="font-display text-2xl" style={{ color: memberHistory.stats.no_shows > 0 ? "#F87171" : "#F5F3F0" }} data-testid="member-opkomst-noshow">{memberHistory.stats.no_shows}</div></div>
                  <div><div className="chip text-[#4A4A4A]">Opkomst</div><div className="font-display text-2xl accent-ink" data-testid="member-opkomst-pct">{memberHistory.stats.attendance_pct != null ? `${memberHistory.stats.attendance_pct}%` : "—"}</div></div>
                </div>
              )}
            </div>

            {/* Volledige geschiedenis */}
            {memberHistory && (
              <div className="mt-4 space-y-5" data-testid="member-detail-history">
                <div>
                  <div className="chip text-[#A3A3A3] mb-2">— Boekingen ({memberHistory.bookings.length})</div>
                  {memberHistory.bookings.length === 0 ? <div className="chip text-[#4A4A4A]">Nog geen boekingen.</div> : (
                    <div className="line-t line-dark">
                      {memberHistory.bookings.slice(0, 10).map((b, i) => (
                        <div key={b.id || i} className="line-b line-dark py-2.5 flex items-center gap-3" data-testid={`member-history-booking-${i}`}>
                          <span className="chip text-[#DDD6CB] flex-1 truncate">{b.class_type} · {b.class_date} {b.class_start}</span>
                          <span className="chip" style={{ color: b.attended === true ? "#4ADE80" : b.attended === false ? "#F87171" : "#4A4A4A" }}>
                            {b.attended === true ? "Aanwezig" : b.attended === false ? "No-show" : (b.credit_type === "trial" ? "Trial" : "Gepland")}
                          </span>
                        </div>
                      ))}
                      {memberHistory.bookings.length > 10 && <div className="chip text-[#4A4A4A] py-2">+ {memberHistory.bookings.length - 10} oudere boekingen</div>}
                    </div>
                  )}
                </div>
                {memberHistory.cancellations.length > 0 && (
                  <div>
                    <div className="chip text-[#A3A3A3] mb-2">— Annuleringen ({memberHistory.cancellations.length})</div>
                    <div className="line-t line-dark">
                      {memberHistory.cancellations.slice(0, 5).map((c, i) => (
                        <div key={c.id || i} className="line-b line-dark py-2.5 flex items-center gap-3" data-testid={`member-history-cancel-${i}`}>
                          <span className="chip text-[#DDD6CB] flex-1 truncate">{c.detail}</span>
                          <span className="chip text-[#4A4A4A]">{c.at ? new Date(c.at).toLocaleDateString("nl-NL") : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="chip text-[#A3A3A3] mb-2">— Betalingen ({memberHistory.payments.length})</div>
                  {memberHistory.payments.length === 0 ? <div className="chip text-[#4A4A4A]">Geen betalingen.</div> : (
                    <div className="line-t line-dark">
                      {memberHistory.payments.slice(0, 6).map((p, i) => (
                        <div key={p.session_id || i} className="line-b line-dark py-2.5 flex items-center gap-3" data-testid={`member-history-payment-${i}`}>
                          <span className="chip text-[#DDD6CB] flex-1 truncate">{p.label || p.lookup_key || p.kind}</span>
                          <span className="chip text-[#F5F3F0]">€{((p.amount || 0) / 100).toLocaleString("nl-NL")}</span>
                          <span className={`chip ${p.payment_status === "paid" ? "accent-ink" : "text-[#A3A3A3]"}`}>{{ paid: "Betaald", pending: "Openstaand", failed: "Mislukt", refunded: "Terugbetaald", expired: "Verlopen" }[p.payment_status] || p.payment_status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {memberHistory.submissions.length > 0 && (
                  <div>
                    <div className="chip text-[#A3A3A3] mb-2">— Aanmeldingen ({memberHistory.submissions.length})</div>
                    <div className="line-t line-dark">
                      {memberHistory.submissions.slice(0, 6).map((s, i) => (
                        <div key={s.id || i} className="line-b line-dark py-2.5 flex items-center gap-3" data-testid={`member-history-sub-${i}`}>
                          <span className="chip text-[#DDD6CB] flex-1 truncate">{{ trial: "Proefles", contact: "Contact", pt: "PT-aanvraag", membership: "Membership" }[s.type] || s.type}</span>
                          <span className="chip text-[#A3A3A3]">{STATUS_META[s.status]?.label || s.status}</span>
                          <span className="chip text-[#4A4A4A]">{s.created_at ? new Date(s.created_at).toLocaleDateString("nl-NL") : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 mt-6">
              {realEmail(selectedMember.email) && (
                <button onClick={() => setEmailTo({ email: selectedMember.email, name: selectedMember.name || `${selectedMember.first_name || ""} ${selectedMember.last_name || ""}`.trim() })}
                  className="btn-light flex-1 justify-center" data-testid="member-detail-send-email">
                  <Send size={14}/> Stuur e-mail
                </button>
              )}
              {realEmail(selectedMember.email) && <a href={`mailto:${selectedMember.email}`} title="Open in je eigen mailapp" className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="member-detail-mail"><MailOpen size={14}/></a>}
              {selectedMember.manual && (
                <button onClick={() => deleteMember(selectedMember)} className="btn-ghost-dark text-red-400 border-red-400/40" data-testid="member-detail-delete"><Trash2 size={14}/></button>
              )}
              <button onClick={()=>setSelectedMember(null)} className="btn-ghost-dark text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="member-detail-close">Sluiten</button>
            </div>
          </div>
        </div>
      )}

      {emailTo && (
        <EmailComposerModal email={emailTo.email} name={emailTo.name} onClose={() => setEmailTo(null)} />
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setSelected(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-8" onClick={(e)=>e.stopPropagation()} data-testid="admin-detail-modal">
            <div className="chip text-[#A3A3A3]">{typeLabel[selected.type]}</div>
            <div className="font-display uppercase text-4xl mt-1">{selected.data?.name}</div>
            <div className="mt-6 space-y-3">
              {selected.data?.email && <Row icon={<Mail size={14}/>} label="E-mail" value={selected.data.email}/>}
              {selected.data?.phone && <Row icon={<Phone size={14}/>} label="Telefoon" value={selected.data.phone} href={waLink(selected.data.phone)}/>}
              {selected.data?.subject && <Row icon={<MessageSquare size={14}/>} label="Onderwerp" value={selected.data.subject}/>}
              {selected.data?.experience && <Row icon={<MessageSquare size={14}/>} label="Ervaring" value={selected.data.experience}/>}
              {selected.data?.goal && <Row icon={<MessageSquare size={14}/>} label="Doel" value={selected.data.goal}/>}
              {selected.data?.preferred_day && <Row icon={<Calendar size={14}/>} label="Voorkeursdag" value={selected.data.preferred_day}/>}
              {selected.data?.trial_date && <Row icon={<Calendar size={14}/>} label="Proefles-datum" value={selected.data.trial_date}/>}
              {selected.data?.message && <Row icon={<MessageSquare size={14}/>} label="Bericht" value={selected.data.message}/>}
            </div>
            <div className="chip text-[#A3A3A3] mt-6">{new Date(selected.created_at).toLocaleString("nl-NL")}</div>

            {selected.type === "trial" && (
              <div className="mt-6 border border-[#F5F3F0]/15 p-4" data-testid="admin-trial-manage">
                {/* Koppeling aan lesprogramma */}
                {selected.status === "approved" && selected.data?.linked_instance_id ? (
                  <div className="mb-5 border border-[#4ADE80]/40 bg-[#4ADE80]/5 p-4" data-testid="admin-trial-linked">
                    <div className="chip text-[#4ADE80] mb-2">Goedgekeurd · gekoppeld</div>
                    <button
                      onClick={() => openLinkedClass(selected.data.linked_instance_id)}
                      className="text-left group"
                      data-testid="admin-trial-linked-class"
                    >
                      <span className="font-display uppercase text-xl text-[#F5F3F0] group-hover:text-[#D4A93A] transition-colors inline-flex items-center gap-2">
                        {selected.data.linked_class_type} — {selected.data.linked_date ? new Date(selected.data.linked_date + "T00:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "long" }) : ""}, {selected.data.linked_start}
                        <ArrowUpRight size={16}/>
                      </span>
                    </button>
                    <div className="chip text-[#A3A3A3] mt-1">Zichtbaar als deelnemer (badge Trial) in het lesprogramma. Status terugzetten ontkoppelt weer.</div>
                  </div>
                ) : selected.status !== "approved" ? (
                  <div className="mb-5 border border-[#F5F3F0]/15 p-4" data-testid="admin-trial-link-panel">
                    <div className="chip text-[#A3A3A3] mb-2">Koppeling aan lesprogramma</div>
                    {trialLinkOptions.length > 1 && (
                      <select value={trialLinkChoice} onChange={(e) => setTrialLinkChoice(e.target.value)} className="editorial-input dark w-full mb-3" data-testid="admin-trial-link-select">
                        {trialLinkOptions.map(c => <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.type} · {c.start} — {c.end} ({c.going || 0}/{c.capacity})</option>)}
                      </select>
                    )}
                    {trialLinkOptions.length === 1 && (
                      <div className="chip text-[#DDD6CB] mb-3">Wordt gekoppeld aan: {trialLinkOptions[0].type} · {trialLinkOptions[0].start}</div>
                    )}
                    {trialLinkOptions.length === 0 && (
                      <div className="chip text-[#A3A3A3] mb-3">Geen les gevonden op de proeflesdatum — goedkeuren kan, koppeling volgt zodra er een les op die datum staat.</div>
                    )}
                    <button onClick={() => updateSubmissionStatus(selected.id, "approved", trialLinkChoice || undefined)} className="btn-light w-full justify-center" data-testid="admin-trial-approve-link">
                      <CheckCheck size={14}/> Keur goed &amp; koppel aan les
                    </button>
                  </div>
                ) : null}
                <label className="chip text-[#A3A3A3]">Proefles-dag</label>
                <div className="mt-2 flex flex-col md:flex-row gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className={`editorial-input dark flex-1 flex items-center justify-between gap-2 text-left ${trialDay ? "" : "text-[#A3A3A3]"}`} data-testid="admin-trial-day">
                        <span>{trialDay
                          ? (/^\d{4}-\d{2}-\d{2}$/.test(trialDay)
                              ? new Date(trialDay + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                              : trialDay)
                          : "Kies een datum"}</span>
                        <Calendar size={16} className="shrink-0 opacity-60"/>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border border-[#F5F3F0]/25 bg-[#0A0A0A] rounded-none shadow-none" align="start">
                      <DayPickerCalendar
                        mode="single"
                        showOutsideDays={false}
                        selected={/^\d{4}-\d{2}-\d{2}$/.test(trialDay) ? new Date(trialDay + "T00:00:00") : undefined}
                        className="p-2"
                        classNames={{
                          head_cell: "text-[#A3A3A3] w-7 font-normal text-[0.7rem]",
                          day: "h-7 w-7 p-0 text-xs font-normal text-[#F5F3F0] aria-selected:opacity-100 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors",
                          cell: "p-0 text-center text-xs",
                          row: "flex w-full mt-1",
                          month: "space-y-2",
                          caption_label: "text-sm font-medium text-[#F5F3F0]",
                          nav_button: "h-6 w-6 bg-transparent p-0 opacity-40 hover:opacity-100 border border-[#F5F3F0]/25 text-[#F5F3F0]",
                        }}
                        onSelect={(d) => {
                          if (!d) return;
                          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          setTrialDay(iso);
                        }}
                        data-testid="admin-trial-day-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                  <button onClick={saveTrialDay} className="btn-light" data-testid="admin-trial-day-save">Dag opslaan</button>
                </div>
                <button onClick={deleteTrial} className="mt-4 chip text-red-400 border border-red-400/40 px-3 py-2 hover:bg-red-400/10 inline-flex items-center gap-2" data-testid="admin-trial-delete">
                  <Trash2 size={12}/> Verwijder deze proefles
                </button>
              </div>
            )}

            <button onClick={()=>setSelected(null)} className="btn-light mt-6 w-full justify-center" data-testid="admin-detail-close">Sluiten</button>
            <button onClick={async () => { await deleteTrialDirect(selected); setSelected(null); }} className="mt-3 w-full chip text-red-400 border border-red-400/40 px-3 py-2.5 hover:bg-red-400/10 inline-flex items-center justify-center gap-2" data-testid="admin-detail-delete">
              <Trash2 size={12}/> Verwijder dit bericht (31 dagen in archief)
            </button>
          </div>
        </div>
      )}

      {selectedClass && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setSelectedClass(null)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="admin-class-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="chip text-[#A3A3A3]">{fmtDate(selectedClass.date).weekday} · {selectedClass.start} — {selectedClass.end}</div>
                <div className="font-display uppercase text-4xl mt-1">{selectedClass.type}</div>
                <div className="chip text-[#A3A3A3] mt-2">
                  {selectedClass.level} · {selectedClass.going || 0}/{selectedClass.capacity}
                  {selectedClass.waitlist_count > 0 && (
                    <span className="ml-3" style={{ color: "var(--joh-accent)" }}>· {selectedClass.waitlist_count} op reservelijst</span>
                  )}
                </div>
                <div className="chip mt-2" data-testid="admin-class-breakdown">
                  <span className="text-[#DDD6CB]">{selectedClass.member_count ?? 0} leden</span>
                  <span className="ml-3" style={{ color: "var(--joh-accent)" }}>{selectedClass.trial_count ?? 0} proeflessers</span>
                  {((selectedClass.present_count || 0) + (selectedClass.absent_count || 0)) > 0 && (
                    <span className="ml-3" data-testid="admin-class-presence-count">
                      <span style={{ color: "#4ADE80" }}>{selectedClass.present_count} aanwezig</span>
                      <span className="text-[#4A4A4A]"> · </span>
                      <span style={{ color: "#F87171" }}>{selectedClass.absent_count} no-show</span>
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button onClick={() => shareClass(selectedClass.id)} className="chip text-[#F5F3F0] border border-[#F5F3F0]/40 px-3 py-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-2" data-testid="admin-class-share">
                    <Share2 size={13}/> DEEL LINK MET COACH
                  </button>
                  {shareLink && (
                    <a href={shareLink} target="_blank" rel="noreferrer" className="chip accent-ink break-all" data-testid="admin-class-share-link">{shareLink}</a>
                  )}
                </div>
              </div>
              <button onClick={()=>setSelectedClass(null)} className="w-9 h-9 border border-[#F5F3F0] flex items-center justify-center" data-testid="admin-class-modal-close">×</button>
            </div>

            <div className="mt-8 mb-3 flex items-center justify-between">
              <div className="chip text-[#A3A3A3]">Aanmeldingen</div>
              <button onClick={() => setAttOpen(v => !v)} className="chip text-[#F5F3F0] border border-[#F5F3F0]/40 px-3 py-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-2" data-testid="admin-class-add-open">
                <Plus size={13}/> Deelnemer toevoegen
              </button>
            </div>
            {attOpen && (
              <form onSubmit={addManualAttendee} className="mb-5 border border-[#D4A93A]/40 bg-[#D4A93A]/5 p-4" data-testid="admin-class-add-form">
                <div className="chip mb-3" style={{ color: "var(--joh-accent)" }}>Handmatig toevoegen — ook blanco (zonder account of betaling)</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input required value={attForm.name} onChange={(e) => setAttForm(f => ({ ...f, name: e.target.value }))} placeholder="Naam*" className="editorial-input dark" data-testid="admin-class-add-name"/>
                  <input type="email" value={attForm.email} onChange={(e) => setAttForm(f => ({ ...f, email: e.target.value }))} placeholder="E-mail (optioneel)" className="editorial-input dark" data-testid="admin-class-add-email"/>
                  <input value={attForm.phone} onChange={(e) => setAttForm(f => ({ ...f, phone: e.target.value }))} placeholder="Telefoon (optioneel)" className="editorial-input dark" data-testid="admin-class-add-phone"/>
                </div>
                <div className="flex gap-3 mt-3">
                  <button type="submit" disabled={attBusy || !attForm.name.trim()} className="btn-light" data-testid="admin-class-add-submit">{attBusy ? "Toevoegen…" : "Toevoegen aan les"}</button>
                  <button type="button" onClick={() => setAttOpen(false)} className="chip text-[#A3A3A3] accent-hover" data-testid="admin-class-add-cancel">Annuleren</button>
                </div>
              </form>
            )}
            {(selectedClass.attendees || []).length === 0 ? (
              <div className="border border-[#F5F3F0]/15 p-6 text-center chip text-[#A3A3A3]" data-testid="admin-class-empty-attendees">
                Nog geen aanmeldingen voor deze les.
              </div>
            ) : (
              <div className="line-t line-dark">
                {(selectedClass.attendees || []).map((a, i) => {
                  const ct = a.credit_type || "guest";
                  const isTrial = ct === "trial" || !!a.trial_submission_id;
                  const badge = isTrial ? "Trial"
                    : ct === "membership" ? "Membership"
                    : ct === "joh_pack_5" ? "Pack 5"
                    : ct === "joh_pack_10" ? "Pack 10"
                    : ct === "joh_dropin" ? "Drop-in"
                    : ct === "manual" ? "Handmatig"
                    : "Gast";
                  return (
                    <div key={a.id || i} className="line-b line-dark py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-testid={`admin-attendee-${i}`}>
                      <span className="chip text-[#A3A3A3] w-6">{String(i+1).padStart(2,"0")}</span>
                      <button
                        onClick={() => a.trial_submission_id ? openSubmissionById(a.trial_submission_id) : openMemberByEmail(a.email)}
                        className="font-display text-xl uppercase flex-1 text-left hover:text-[#D4A93A] transition-colors"
                        title={a.trial_submission_id ? "Open proefles-aanmelding" : "Open klantprofiel"}
                        data-testid={`admin-attendee-open-${i}`}
                      >{a.name}
                        {a.promoted_from_waitlist && (
                          <span className="chip ml-2" style={{ color: "var(--joh-accent)" }}>{a.promoted_auto ? "auto" : "handmatig"} gepromoveerd</span>
                        )}
                      </button>
                      <span
                        className={`chip px-2 py-0.5 w-fit ${isTrial ? "" : ct === "membership" ? "accent-ink" : ct === "guest" ? "text-[#A3A3A3]" : "text-[#F5F3F0]"}`}
                        style={isTrial ? { color: "var(--joh-accent)", border: "1px solid rgba(212,169,58,0.5)" } : {}}
                        data-testid={`admin-credit-${i}`}
                      >{badge}</span>
                      <button
                        onClick={() => markPresence(a.id, a.attended === true ? false : a.attended === false ? null : true)}
                        title={a.attended === true ? "Aanwezig — klik voor no-show" : a.attended === false ? "No-show — klik om te wissen" : "Markeer aanwezig"}
                        className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center hover:border-[#F5F3F0]/50 transition-colors shrink-0"
                        data-testid={`admin-presence-${i}`}
                      >
                        {a.attended === true ? <Check size={14} style={{ color: "#4ADE80" }}/> : a.attended === false ? <X size={14} style={{ color: "#F87171" }}/> : <Minus size={14} className="text-[#4A4A4A]"/>}
                      </button>
                      {(a.credit_type === "manual" || a.source === "manual") && (
                        <button
                          onClick={() => removeManualAttendee(a)}
                          title="Verwijderen van deze les"
                          className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center hover:border-red-400/60 text-[#4A4A4A] hover:text-red-400 transition-colors shrink-0"
                          data-testid={`admin-attendee-remove-${i}`}
                        ><Trash2 size={13}/></button>
                      )}
                      {realEmail(a.email) ? <a href={`mailto:${a.email}`} className="text-[#DDD6CB] text-sm accent-hover">{a.email}</a> : <span className="text-[#4A4A4A] text-sm">—</span>}
                      {a.phone && <a href={`tel:${a.phone}`} className="chip text-[#A3A3A3] accent-hover">{a.phone}</a>}
                      <span className="chip text-[#A3A3A3] w-40 text-right">{new Date(a.created_at).toLocaleString("nl-NL")}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {(selectedClass.pending_trials || []).length > 0 && (
              <>
                <div className="chip text-[#FB923C] mt-8 mb-3" data-testid="admin-class-pending-header">
                  Wacht op goedkeuring — {selectedClass.pending_trials.length} proefles{selectedClass.pending_trials.length > 1 ? "saanmeldingen" : ""}
                </div>
                <div className="line-t line-dark" data-testid="admin-class-pending">
                  {selectedClass.pending_trials.map((p, i) => (
                    <div key={p.id || i} className="line-b line-dark py-3.5 flex items-center gap-4" data-testid={`admin-pending-trial-${i}`}>
                      <span className="chip px-2 py-0.5" style={{ color: "#FB923C", border: "1px solid rgba(251,146,60,0.4)" }}>WACHT</span>
                      <span className="font-display text-lg uppercase flex-1 truncate">{p.name || "—"}</span>
                      <span className="text-[#A3A3A3] text-sm truncate hidden md:block">{p.email}</span>
                      <button
                        onClick={() => openSubmissionById(p.id)}
                        className="chip text-[#F5F3F0] border border-[#F5F3F0]/40 px-3 py-1 hover:bg-[#F5F3F0] hover:text-[#0A0A0A]"
                        data-testid={`admin-pending-open-${i}`}
                      >
                        Open aanmelding
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {(selectedClass.waitlist || []).length > 0 && (
              <>
                <div className="chip mt-8 mb-3" style={{ color: "var(--joh-accent)" }} data-testid="admin-class-waitlist-header">
                  Reservelijst — {selectedClass.waitlist.length}
                </div>
                <div className="line-t line-dark" data-testid="admin-class-waitlist">
                  {selectedClass.waitlist.map((w, i) => (
                    <div key={w.id || i} className="line-b line-dark py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-testid={`admin-waitlist-${i}`}>
                      <span className="chip w-8" style={{ color: "var(--joh-accent)" }}>#{w.position}</span>
                      <span className="font-display text-xl uppercase flex-1">{w.name}</span>
                      <a href={`mailto:${w.email}`} className="text-[#DDD6CB] text-sm accent-hover truncate">{w.email}</a>
                      {w.phone && <a href={`tel:${w.phone}`} className="chip text-[#A3A3A3] accent-hover">{w.phone}</a>}
                      <div className="flex gap-2">
                        <button
                          onClick={() => promoteWaitlist(selectedClass.id, w.email)}
                          className="chip text-[#F5F3F0] border border-[#F5F3F0]/40 px-3 py-1 hover:bg-[#F5F3F0] hover:text-[#0A0A0A]"
                          data-testid={`admin-waitlist-promote-${i}`}
                        >
                          <ArrowUpRight size={12}/> Accepteer
                        </button>
                        <button
                          onClick={() => removeWaitlist(selectedClass.id, w.email)}
                          className="chip text-red-400 border border-red-400/40 px-3 py-1 hover:bg-red-400/10"
                          data-testid={`admin-waitlist-remove-${i}`}
                        >
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------- Centrale AANMELDINGEN-inbox --------------------- */
const INBOX_TABS = [
  { key: "alle", label: "Alle" },
  { key: "trial", label: "Proefles" },
  { key: "membership", label: "Membership" },
  { key: "contact", label: "Contact" },
  { key: "mislukt", label: "Mislukt" },
];

function AanmeldingenInbox({ items, payments, filter, setFilter, onOpen, onStatus, onDelete, search, setSearch, sort, setSort, hideTabs, onOpenLinked }) {
  // e-mails met een mislukte/achterstallige betaling (voor tab Mislukt)
  const failedEmails = new Set(
    (payments || [])
      .filter(p => !p.is_test && ["failed", "overdue"].includes(p.payment_status || p.status))
      .map(p => (p.email || "").toLowerCase())
  );
  const withFail = (it) => failedEmails.has((it.data?.email || "").toLowerCase());

  const counts = {
    alle: items.length,
    trial: items.filter(i => i.type === "trial").length,
    membership: items.filter(i => i.type === "membership").length,
    contact: items.filter(i => i.type === "contact").length,
    mislukt: items.filter(withFail).length,
  };

  let shown = items;
  if (filter === "mislukt") shown = items.filter(withFail);
  else if (filter !== "alle") shown = items.filter(i => i.type === filter);

  const q = (search || "").trim().toLowerCase();
  if (q) {
    shown = shown.filter(i => {
      const d = i.data || {};
      return [d.name, d.first_name, d.last_name, d.email, d.phone].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }
  if (sort === "status") {
    shown = [...shown].sort((a, b) => SUB_STATUS_ORDER.indexOf(a.status || "new") - SUB_STATUS_ORDER.indexOf(b.status || "new"));
  } else {
    shown = sortRows(shown, sort, {
      first: (i) => firstOf(i.data || {}),
      last: (i) => lastOf(i.data || {}),
      date: (i) => i.created_at || "",
    });
  }

  return (
    <div data-testid="aanmeldingen-inbox">
      <div className="section-tag section-tag-dark mb-4">— Klanten / Aanmeldingen</div>
      <h2 className="font-display uppercase text-3xl md:text-5xl">Aanmeldingen.</h2>

      {!hideTabs && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="aanmeldingen-tabs">
          {INBOX_TABS.map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              className={`px-3 py-2 chip border ${filter === t.key ? "text-[#0A0A0A] bg-[#F5F3F0] border-[#F5F3F0]" : "text-[#A3A3A3] border-[#F5F3F0]/20 hover:text-[#F5F3F0]"}`}
              data-testid={`aanmeldingen-tab-${t.key}`}>
              {t.label} <span className="opacity-60">({counts[t.key]})</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op naam, e-mail of telefoon…"
            className="editorial-input dark pl-6" data-testid="aanmeldingen-search" />
        </div>
        <SortSelect value={sort} onChange={setSort} options={[...SORT_OPTIONS, ["status", "Status"]]} testid="aanmeldingen-sort" className="md:w-52" />
      </div>

      {shown.length === 0 ? (
        <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3] mt-6" data-testid="aanmeldingen-empty">Geen inzendingen in deze categorie.</div>
      ) : (
        <div className="mt-6 overflow-x-auto" data-testid="aanmeldingen-table">
          <div className="min-w-[860px]">
            <div className="grid grid-cols-12 gap-3 chip text-[#4A4A4A] pb-2 line-b line-dark">
              <span className="col-span-2"><SortHeader label="Naam" sort={sort} onSort={setSort} asc="first_az" desc="first_za" testid="aanmeldingen-sort-name" /></span>
              <span className="col-span-1">Type</span>
              <span className="col-span-3">E-mail</span>
              <span className="col-span-2">Telefoon</span>
              <span className="col-span-1"><SortHeader label="Datum" sort={sort} onSort={setSort} asc="date_old" desc="date_new" testid="aanmeldingen-sort-date" /></span>
              <span className="col-span-2">Status</span>
              <span className="col-span-1 text-right">Acties</span>
            </div>
            {shown.map((it) => {
              const d = it.data || {};
              const st = it.status || "new";
              const meta = STATUS_META[st] || STATUS_META.new;
              return (
                <div key={it.id} className="grid grid-cols-12 gap-3 items-center py-3 line-b line-dark" data-testid={`aanmeldingen-row-${it.id}`}>
                  <button onClick={() => onOpen(it)} className="col-span-2 text-left font-display text-base uppercase truncate hover:text-[#D4A93A] transition-colors" data-testid={`aanmeldingen-open-${it.id}`}>
                    {d.name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—"}
                  </button>
                  <span className="col-span-1 chip text-[#A3A3A3]">{{ trial: "Proefles", membership: "Member", contact: "Contact", pt: "PT" }[it.type] || it.type}
                    {it.type === "trial" && st === "approved" && d.linked_instance_id && (
                      <button
                        onClick={() => onOpenLinked && onOpenLinked(d.linked_instance_id)}
                        className="block mt-1 chip text-left"
                        style={{ color: "var(--joh-accent)" }}
                        title="Open gekoppelde les"
                        data-testid={`aanmeldingen-linked-${it.id}`}
                      >
                        ▸ {d.linked_class_type} {d.linked_start}
                      </button>
                    )}
                  </span>
                  <a href={`mailto:${d.email}`} className="col-span-3 text-[#DDD6CB] text-sm truncate accent-hover">{d.email || "—"}</a>
                  <span className="col-span-2 text-[#A3A3A3] text-sm truncate">{d.phone || "—"}</span>
                  <span className="col-span-1 chip text-[#4A4A4A]">{it.created_at ? new Date(it.created_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "—"}</span>
                  <span className="col-span-2">
                    <select value={st} onChange={(e) => onStatus(it.id, e.target.value)}
                      className="bg-transparent border border-[#F5F3F0]/20 px-2 py-1.5 chip w-full cursor-pointer"
                      style={{ color: meta.color, background: meta.bg }}
                      data-testid={`aanmeldingen-status-${it.id}`}>
                      {SUB_STATUS_ORDER.map(s => <option key={s} value={s} className="bg-[#0A0A0A] text-[#F5F3F0]">{SUB_STATUS_LABEL[s]}</option>)}
                    </select>
                  </span>
                  <span className="col-span-1 flex items-center justify-end gap-3">
                    <button onClick={() => onOpen(it)} className="text-[#A3A3A3] hover:text-[#F5F3F0]" title="Bekijken" data-testid={`aanmeldingen-view-${it.id}`}><Eye size={15}/></button>
                    {["trial", "membership"].includes(it.type) && st !== "approved" && (
                      <button onClick={() => onStatus(it.id, "approved")} className="text-[#A3A3A3] hover:text-[#4ADE80]" title="Goedkeuren" data-testid={`aanmeldingen-approve-${it.id}`}><CheckCheck size={15}/></button>
                    )}
                    {st !== "handled" && (
                      <button onClick={() => onStatus(it.id, "handled")} className="text-[#A3A3A3] hover:text-[#4ADE80]" title="Markeer als afgerond" data-testid={`aanmeldingen-done-${it.id}`}><Check size={15}/></button>
                    )}
                    <button onClick={() => onDelete(it)} className="text-[#A3A3A3] hover:text-red-400" title="Verwijderen" data-testid={`aanmeldingen-delete-${it.id}`}><Trash2 size={15}/></button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function StatusPill({ label }) {
  const colors = {
    "ACTIEF": { c: "#4ADE80", bg: "rgba(74,222,128,0.10)" },
    "PROEFLES": { c: "var(--joh-accent)", bg: "rgba(212,169,58,0.12)" },
    "GEPAUZEERD": { c: "#FB923C", bg: "rgba(251,146,60,0.12)" },
    "GEANNULEERD": { c: "#F87171", bg: "rgba(248,113,113,0.10)" },
    "BETALING OPEN": { c: "#F87171", bg: "rgba(248,113,113,0.10)" },
    "CONTACT": { c: "#A3A3A3", bg: "rgba(163,163,163,0.10)" },
  };
  const st = colors[label] || colors["CONTACT"];
  return <span className="chip px-2 py-1 inline-block" style={{ color: st.c, background: st.bg }} data-testid={`status-pill-${label}`}>{label}</span>;
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-[#F5F3F0]/15 p-4 md:p-6" data-testid={testid}>
      <div className="chip text-[#A3A3A3]">{label}</div>
      <div className="font-display text-4xl md:text-6xl mt-2">{value}</div>
    </div>
  );
}

function Row({ icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[#A3A3A3] mt-1">{icon}</div>
      <div>
        <div className="chip text-[#A3A3A3]">{label}</div>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer" className="text-[#F5F3F0] hover:text-[#4ADE80] transition-colors" title="Open in WhatsApp">{value}</a>
        ) : (
          <div className="text-[#F5F3F0]">{value}</div>
        )}
      </div>
    </div>
  );
}
